/**
 * EN dictionary. Typed as `Messages` (derived from `tr.ts`), so a missing or extra key
 * breaks the build — this is the CI guard required by Story 0.3.
 */
export const en = {
    common: {
        appTagline: 'Leave every event with new business connections.',
        loading: 'Loading…',
        save: 'Save',
        cancel: 'Cancel',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        retry: 'Try again',
        search: 'Search',
        delete: 'Delete',
        edit: 'Edit',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        // Finding 5: URL fields accept schemeless input; the rule is stated in text.
        urlHint: 'e.g. example.com',
        optional: 'Optional',
        required: 'Required',
        seeAll: 'See all',
        chipMore: '+{count} more options',
        chipMore_one: '+{count} more option',
        chipLess: 'Show fewer',
        all: 'All',
        offlineNotice: 'You are offline. You will pick up where you left off once the connection returns.',
        backOnline: 'Back online.',
        errorOffline: 'No connection. The content could not be loaded; try again once you are online.',
        empty: 'Nothing here yet',
        deletedUser: 'Deleted user',
        somethingWentWrong: 'Something went wrong',
        errorSupportCode: 'Support code: {code}',
        sending: 'Sending…',
        saving: 'Saving…',
    },
    a11y: {
        skipToContent: 'Skip to content',
        mainNavigation: 'Main navigation',
        closeDialog: 'Close dialog',
        loadingContent: 'Loading content',
        languageSelector: 'Language selector',
    },
    brand: {
        sharedWith: 'Shared with {brandName}',
        sharedWithDomain: 'Shared with {brandName} · {publicDomain}',
    },
    datePicker: {
        openCalendar: 'Open calendar',
        calendarLabel: 'Pick a date',
        previousMonth: 'Previous month',
        nextMonth: 'Next month',
        today: 'Today',
        clear: 'Clear',
        timeLabel: 'Time',
        timeListLabel: 'Time options',
        selected: 'Selected',
        unparsable: "I couldn't read that date. Example: {example}",
        pastDisabled: 'Past dates cannot be selected.',
        timezoneNote: 'Times shown in {timezone}',
        maskDay: 'DD',
        maskMonth: 'MM',
        maskYear: 'YYYY',
        maskHour: 'HH',
        maskMinute: 'mm',
        month1: 'January',
        month2: 'February',
        month3: 'March',
        month4: 'April',
        month5: 'May',
        month6: 'June',
        month7: 'July',
        month8: 'August',
        month9: 'September',
        month10: 'October',
        month11: 'November',
        month12: 'December',
        weekdayShort0: 'Sun',
        weekdayShort1: 'Mon',
        weekdayShort2: 'Tue',
        weekdayShort3: 'Wed',
        weekdayShort4: 'Thu',
        weekdayShort5: 'Fri',
        weekdayShort6: 'Sat',
        weekdayLong0: 'Sunday',
        weekdayLong1: 'Monday',
        weekdayLong2: 'Tuesday',
        weekdayLong3: 'Wednesday',
        weekdayLong4: 'Thursday',
        weekdayLong5: 'Friday',
        weekdayLong6: 'Saturday',
    },
    footer: {
        label: 'Footer',
        navLabel: 'Footer links',
        events: 'Events',
        organizers: 'For organizers',
        faq: 'Frequently asked questions',
        contact: 'Contact',
        copyright: '© {year} {brandName}',
        domainNotice: '{publicDomain}',
    },
    faq: {
        metaTitle: 'Frequently asked questions',
        metaDescription: 'Frequently asked questions and answers for attendees and organizers.',
        title: 'Frequently asked questions',
        tabsLabel: 'Question groups',
        tabs: {
            katilimcilar: 'Attendees',
            organizatorler: 'Organizers',
        },
        participants: {
            whatIs: {
                question: 'What is {brandName}?',
                answer: '{brandName} is an app for professionals who attend events. You follow the program of the events you attend, who is coming, your meetings and the business cards you collect, all in one place.',
            },
            oneApp: {
                question: 'Do I need to download a separate app for each event?',
                answer: 'No, you only need to download {brandName} once. Whichever event you register for, you follow them all in the same app.',
            },
            attendeeList: {
                question: 'Who can see the attendee list?',
                answer: 'Only people registered for that event can see the list. When you register, you appear on the list too. The list does not include contact details such as phone numbers or email addresses. If you want to leave the list, just cancel your registration.',
            },
            cardExchange: {
                question: 'How does business card sharing work?',
                answer: 'The other person scans your QR code, and once you both approve, your business cards are shared. Your details go to no one unless you approve.',
            },
            meetingRequest: {
                question: 'What is a meeting request for?',
                answer: 'Before the event starts, you can send a meeting request to an attendee you want to meet and suggest times that work for you. If they accept, the meeting is added to your agenda.',
            },
            recommendations: {
                question: 'How are event recommendations made?',
                answer: 'We recommend events that suit you based on your interests and the events you have attended before. Next to each recommendation you can also see why it was recommended.',
            },
            entry: {
                question: 'What do I need to show when entering the event?',
                answer: 'The entry QR code for the event you registered for is ready in your app. Just show this QR code to the staff at the entrance.',
            },
            price: {
                question: 'Does it cost anything to use the app?',
                answer: 'No, it is free for attendees. You can keep as many business cards as you like.',
            },
            export: {
                question: 'Can I export my business cards to my phone or computer?',
                answer: 'Yes. You can download the contacts in your wallet as a vCard or CSV file and import them into your contacts or a spreadsheet.',
            },
            deleteAccount: {
                question: 'How can I delete my account?',
                answer: 'You can delete your account from the Data section in Settings. You can find details about the deletion process {link}.',
                answerLink: 'on the account deletion page',
            },
        },
        organizers: {
            openEvent: {
                question: 'How can we run our event on {brandName}?',
                answer: 'We open your organization account after a short introductory call. Just fill in the "{link}" form and our team will get back to you as soon as possible.',
                answerLink: 'Contact us',
            },
            eventTypes: {
                question: 'What kinds of events is {brandName} suited to?',
                answer: 'It suits any event where attendees want to meet one another, such as a congress, summit, symposium, career day, demo day or industry meetup.',
            },
            audience: {
                question: 'Who will see our event?',
                answer: 'Your event is shown as a recommendation to users whose interests and attendance history match your event.',
            },
            access: {
                question: 'Can we decide who attends our event?',
                answer: 'Yes. You can open registration to everyone, approve applications one by one, or allow only the people you invite to attend.',
            },
            announcements: {
                question: 'How can we make announcements to our attendees?',
                answer: 'You can send instant notifications to your registered attendees from the management panel.',
            },
            checkIn: {
                question: 'How can we check entries?',
                answer: "Your staff scan the attendee's entry QR code with their phone. You can follow who has arrived in real time from the panel.",
            },
            reports: {
                question: 'Which reports can we get after the event ends?',
                answer: 'You can view and download reports on registrations, attendance and attendee profiles from the panel. You can also send your attendees a certificate of attendance and a survey.',
            },
            pricing: {
                question: 'How do fees work?',
                answer: 'Your first event is free. We decide on the package that suits you together during the introductory call.',
            },
            privacy: {
                question: "Is our attendees' data safe?",
                answer: 'Personal data is processed in line with KVKK and is never sold to any sponsor or third party.',
            },
        },
        closing: {
            participants: "Couldn't find the answer to your question? {link}.",
            participantsLink: 'Contact us',
            organizers: "Couldn't find the answer to your question? {link}.",
            organizersLink: 'Contact us',
        },
    },
    appStores: {
        kicker: 'Get the app',
        appStoreLabel: 'Download on the App Store',
        googlePlayLabel: 'Get it on Google Play',
        appStoreAlt: 'Download on the App Store',
        googlePlayAlt: 'Get it on Google Play',
    },
    notFound: {
        siteMineTitle: 'Back to what you manage',
        siteMineBody: 'If you are an organizer, return to your event list.',
        siteDiscoverLink: 'Go to event discovery',
        siteMineLink: 'Events you manage',
        kicker: 'Not found',
        title: 'This page is no longer here',
        body: 'The link may be stale. The event might have been removed, unpublished, or the address mistyped.',
        stepDiscover: 'Discover events',
        stepDiscoverBody: 'Browse upcoming events.',
        stepMine: 'Back to what you manage',
        stepMineBody: 'Your own events are still there.',
        action: 'Back to home',
        previewLabel: 'Sample event card',
    },
    temporaryError: {
        kicker: 'Temporary problem',
        title: 'This page could not be opened right now',
        body: 'Your access has not changed; too many requests were sent in a short time or a temporary error occurred. Try again in a few seconds.',
        publicBody: 'The page could not be loaded right now. The server is temporarily not responding. Try again in a few seconds.',
        stepRetry: 'Try again',
        stepRetryBody: 'Reload the page; a second attempt usually works.',
        stepMine: 'Back to what you manage',
        stepMineBody: 'Your event list opens from here.',
        action: 'Try again',
        previewLabel: 'Panel row preview',
    },
    theme: {
        light: 'Light',
        dark: 'Dark',
        system: 'System',
        current: 'Theme: {name}',
    },
    language: {
        tr: 'Turkish',
        en: 'English',
        current: 'Language: {name}',
    },
    nav: {
        home: 'Home',
        events: 'Events',
        myEvents: 'Managing',
        wallet: 'Wallet',
        profile: 'Profile',
        accountSettings: 'Account settings',
        signIn: 'Sign in',
        signOut: 'Sign out',
        signOutRetry: 'Retry sign out',
        accountMenu: 'Menu, {name}',
        menu: 'Menu',
        signOutFailed: "Sign-out didn't complete, you are still signed in. Check your connection and try again.",
        createEvent: 'Create event',
        createOrganization: 'Create organization',
        context: 'Context',
        contextCurrent: 'Context: {name}',
        contextPersonal: 'Personal',
        contextPersonalHint: 'Join and meet people as yourself',
        contextOrganizations: 'Your organizations',
        contextVerified: 'Verified',
        contextLoadError: 'Your organizations could not be loaded',
        card: 'Card',
        qr: 'QR code',
        cardWallet: 'My wallet',
        cardSegmentLabel: 'Card view',
        eventsDiscover: 'Discover',
        eventsSegmentLabel: 'Events view',
        requests: 'Requests',
        participationHistory: 'My participation',
        pendingRequests: '{count} pending requests',
        pendingRequests_one: '{count} pending request',
        pendingRequestsAtLeast: 'At least {count} pending requests',
        pendingRequestsAtLeast_one: 'At least {count} pending request',
        agenda: 'Agenda',
        myAgenda: 'My agenda',
        notifications: 'Notifications',
    },
    dashboard: {
        kicker: 'Home',
        greeting: 'Hello, {name}',
        greetingNext: 'Your next event is on {date}.',
        countdownDays: '{days} days left',
        countdownToday: 'Today',
        countdownOngoing: 'Happening now',
        countdownTomorrow: 'Tomorrow',
        whoIsComing: 'Who is coming',
        openEvent: 'Event',
        attendingTitle: 'Events you joined',
        attendingCount: '{count} events',
        attendingCount_one: '{count} event',
        attendingCountLink: 'Open participation history · {count} events',
        attendingCountLink_one: 'Open participation history · {count} event',
        attendingAll: 'All',
        attendingUpcoming: 'Upcoming',
        attendingPast: 'Past',
        attendingUpcomingEmpty: 'You have no other upcoming attendance.',
        attendingPastEmpty: 'You have no past events yet.',
        pendingInvitationsTitle: 'Invitations awaiting your reply',
        respondInvitationCta: 'Respond to invitation',
        managedTitle: 'Events you manage',
        emptyPrimaryCta: 'Discover events',
        emptySecondaryCta: 'Complete your profile',
        emptyRailTitle: 'Requests and agenda',
        emptyRailBody: 'Once you register for an event the attendee list opens; meeting requests and your agenda collect in this panel.',
        loadError: 'Your events could not be loaded.',
        topActionKicker: 'Do now',
        topOpenEventHint: 'Opens the event page',
        topVisibleLabel: 'people on the attendee list',
        topMatchCount: '{count} people match you',
        topMatchCount_one: '{count} person matches you',
        topMatchCountAtLeast: 'At least {count} people match you',
        topMatchCountAtLeast_one: 'At least {count} person matches you',
        topCrowdOne: '{first}',
        topCrowdTwo: '{first} and {second}',
        topCrowdMore: '{first}, {second} and {count} more',
        topCrowdMore_one: '{first}, {second} and {count} more',
        topOpenAttendees: 'Open the attendee list',
        topQrTitle: 'My QR code',
        topQrBody: 'Let the person you meet scan your card; both sides approve.',
        topQrCta: 'Open QR code',
        topRequestsKickerNone: 'None waiting',
        topRequestsTitle: 'Requests',
        topRequestsBody: '{count} meeting requests are waiting for your answer; accepted ones land in your agenda.',
        topRequestsBody_one: '{count} meeting request is waiting for your answer; once accepted it lands in your agenda.',
        topRequestsBodyNone: 'No request is waiting for an answer. You can request a meeting from the attendee list.',
        topPeopleError: 'People could not be loaded.',
        topRequestsCta: 'See requests',
        topPendingTitle: 'Pending requests',
        topPendingIncoming: 'Waiting for your reply',
        topPendingOutgoing: 'Sent by you, awaiting reply',
        topPendingReply: 'Reply',
        topQrTileBody: 'Show your card',
        topRequestsTileBody: '{count} requests waiting',
        topRequestsTileBody_one: '{count} request waiting',
        topRoleAttendee: 'Attendee',
        topRoleSponsor: 'Sponsor',
        topNoEventBody: 'You have no upcoming event. Once you register, you will see who is coming here.',
        topEmptyKicker: 'No events yet',
        topEmptyTitle: 'Register for an event and the rest starts here',
        topEmptyBody: 'Recommendations come out of your attendance history. Until that history exists we do not guess on your behalf; we show this month’s most registered events instead.',
        topEmptyPreviewLabel: 'This is how it looks once you register',
        topEmptyPreviewTitle: 'Istanbul Product Conference 2026',
        topEmptyPreviewMeta: '27 people on the attendee list · 4 match you',
        topErrorKicker: 'Top area could not load',
        topErrorAlert: 'Event details could not be fetched from the server. If your connection is fine, try again.',
        topErrorTitle: 'Your agenda and QR code still work',
        topErrorBody: 'The upcoming event card could not load; you can still reach your agenda and QR code. If you are at the venue, nothing stops.',
        topRecsMeta: '{count} recommendations · with reasons',
        topRecsMeta_one: '{count} recommendation · with reasons',
        peopleMeta: '{count} people from this event · with reasons',
        peopleMeta_one: '{count} person from this event · with reasons',
        peopleAll: 'Who is coming',
        peopleRequestCta: 'Request a meeting',
        peopleSentBadge: 'Request sent',
        peopleMoreLabel: 'More actions for {name}',
        agendaTitle: 'Your agenda today',
        agendaNextTitle: 'Your next scheduled day · {day}',
        agendaScopeNote: 'This card lists sessions and accepted meetings; an event shows up here once its programme is published.',
        agendaAll: 'Your full agenda',
        agendaEmpty: 'Nothing planned today.',
        agendaLoadError: 'Your agenda could not be loaded.',
        managedPanelCta: 'Panel',
        managedWarnUnverified: 'Your organization is not verified. Your event does not appear in discovery or recommendations.',
        managedWarnNoMeetingArea: 'You have not defined a meeting area. Attendees cannot arrange meetings at this event.',
        managedWarnLoadError: 'Could not load event warnings.',
        profileTitle: 'Profile completion',
        profileStepCount: '{done} / {total} steps',
        profileNote: 'Your interest tags decide which events are shown to you. Your profile is only visible to attendees of the events you register for.',
        profileStepIdentity: 'Name, title, company',
        profileStepPhoto: 'Profile photo',
        profileStepInterests: 'Interest tags',
        profileStepLinks: 'Links',
        profileStepAttendance: 'First event registration',
        popularTitle: 'Popular this month',
        popularLead: 'The events getting the most interest in your city. This list is ordered by registration count; it is not built from your own history.',
    },
    home: {
        heroSubtitle: 'With {brandName} you see who is coming before the event, set up meetings and break the ice before it even starts.',
        heroSecondaryCta: 'I organize events',
        // --- Blok 8-3: landing panel / trust / pricing / closing sections ---
        closingCta: 'Create a free account',
        statsTitle: 'Where the platform stands today',
        statsNote: 'The first two numbers are live, read from published events. The last two are product rules that never change.',
        statEventsLabel: 'Published events',
        statTopicsLabel: 'Topics',
        navHowItWorks: 'How it works',
        navForOrganizers: 'Organizers',
        siteNavContact: 'Contact us',
        heroTitleSite: 'Run your event from registration to report in one place.',
        heroLeadSite: 'You approve applications from the panel. Attendees show their QR code at the door. The report is ready as soon as the event ends. Your attendees also see who is coming beforehand and plan meetings.',
        heroEventsCta: 'See events',
        freeFirstEvent: 'Your first event is free.',
        storyTitle: 'This is how an event goes today.',
        storyRegistrationLead: 'Registration.',
        storyRegistrationBody: 'Applications pile up in a form, approvals get lost in email threads.',
        storyGateLead: 'Door.',
        storyGateBody: 'A queue forms on the morning of the event, staff search names on a printed list.',
        storyAfterLead: 'Afterwards.',
        storyAfterBody: 'The event ends and you have no proper report of how many people came.',
        stepsTitle: 'The same event, with {brandName}.',
        step1Title: 'You see applications in one list and approve or reject them.',
        step1Body: 'Approved people get their ticket and join the attendee list. You can approve or reject several applications at once.',
        step2Title: 'Attendees show their QR code at the door, staff check them in by phone.',
        step2Body: 'If a code does not scan, you search by name. You see at the door how many people are registered and how many are in. Camera images are not stored.',
        step3Title: 'The event ends, the report is ready.',
        step3Body: 'The report shows how many people came and which sectors they came from. You download it as a PDF and share it with your team.',
        audienceTitle: 'Your event reaches the right audience.',
        audience1Title: 'Recommendations by interest',
        audience1Body: 'Users receive event recommendations based on their interests and the events they attend.',
        audience2Title: 'You don’t search for your audience from scratch',
        audience2Body: 'When you create your event on {brandName}, it reaches users whose interests and attendance history match your event, as a recommendation.',
        audience3Title: 'Recommendations with a reason',
        audience3Body: 'Every recommendation shows its reason. Ranking does not change with payment, and users are recommended the events that truly suit them.',
        attendeesTitle: 'Your attendees start meeting before they enter the hall.',
        attendeesBody: 'Everyone registered for your event sees each other in the {brandName} app.',
        phoneListTitle: 'Who is coming',
        phoneListBody: 'Everyone registered is on the list. Shared interests come with the reason.',
        phoneMeetingTitle: 'Meeting plan',
        phoneMeetingBody: 'Time and table are chosen. An accepted meeting lands in both agendas.',
        phoneCardTitle: 'Approved business card',
        phoneCardBody: 'Cards are shared by QR code. Nothing is passed on until the other side approves.',
        phoneWalletTitle: 'Wallet',
        phoneWalletBody: 'Everyone you meet is kept together with the event where you met.',
        rulesTitle: 'We have three rules.',
        rule1: 'A business card is shared only if both sides approve.',
        rule2: 'Only people registered for the event see the attendee list.',
        rule3: 'You can export your event data whenever you want.',
        rulesLink: 'Privacy details',
        closingLeadSite: 'Tell us about your event and we will get back to you.',
        closingAppsLabel: 'App for your attendees',
        shotApplicationsAlt: 'Applications screen in the organizer panel',
        shotGateAlt: 'QR check-in on the door screen',
        shotReportAlt: 'Event report in the organizer panel',
        shotAttendeesAlt: 'Attendee list with reasons to meet',
        shotMeetingAlt: 'Meeting request screen',
        shotCardAlt: 'Business card exchange QR code',
        shotWalletAlt: 'Business cards in the wallet',
        shotRecommendationsAlt: 'Recommended events, each with the reason it was suggested',
        contactBody: 'Your first event is free.',
    },
    event: {
        accessMode: {
            public: 'Public',
            approval: 'Application',
            inviteOnly: 'Invite only',
        },
        format: {
            inPerson: 'In person',
            online: 'Online',
            hybrid: 'Hybrid',
        },
        status: {
            draft: 'Draft',
            published: 'Published',
            cancelled: 'Cancelled',
            archived: 'Archived',
        },
        statusEnded: 'Ended',
        capacityRemaining: '{count} spots left',
        capacityRemaining_one: '{count} spot left',
        startsAt: 'Starts',
        endsAt: 'Ends',
    },
    attendance: {
        endedNotice: 'Your attendance stays in your history and cannot be removed.',
        listedNote: "Because you are registered, you appear on this event's attendee list. Only registered attendees see the list and your contact details are not on it. To leave the list, cancel your registration.",
        listedPendingNote: "Once your registration is confirmed you will appear on this event's attendee list. Only registered attendees see the list.",
        listedPreRegisterNote: "When you register, your name appears on this event's attendee list. Only registered attendees see the list and your contact details are not on it.",
        listedPreApplyNote: "If your application is approved, your name appears on this event's attendee list. Only registered attendees see the list.",
    },
    consent: {
        termsLabel: 'I accept the {brandName} terms of service',
        privacyLabel: 'I have read the privacy policy',
        /* Bilgilendirme dili — TR karşılığındaki gerekçe aynen geçerlidir. */
        kvkkLabel: 'Here is how we handle your personal data:',
        marketingLabel: 'I want to receive campaign emails',
        analyticsLabel: 'I can share usage data to improve the product',
        marketingHint: 'Transactional email such as sign-in codes is separate from this.',
        analyticsHint: 'We measure which screens get used and what became of the recommendations shown to you.',
        termsHint: 'Explains the rules you use the service under.',
        privacyHint: 'Explains which of your data we process and why.',
        kvkkReadNote: 'This is not a consent; your reading is recorded.',
        openDocumentByType: {
            terms_of_service: 'Open the Terms of service document',
            privacy_policy: 'Open the Privacy policy document',
            kvkk_clarification: 'Open the KVKK clarification text document',
            marketing_email: 'Open the Marketing emails document',
            analytics: 'Open the Usage analytics document',
            attendee_visibility: 'Open the Attendee list visibility document',
            organization_account_notice: 'Open the Organization account privacy notice document',
        },
    },
    consentTypes: {
        terms_of_service: 'Terms of service',
        privacy_policy: 'Privacy policy',
        kvkk_clarification: 'KVKK clarification text',
        marketing_email: 'Marketing emails',
        analytics: 'Usage analytics',
        attendee_visibility: 'Attendee list visibility (no longer collected)',
        organization_account_notice: 'Organization account privacy notice',
    },
    push: {
        /* Invitation: GENERIC text. No event or organization name on the lock screen. */
        invitation: {
            title: "You're invited to an event",
            body: 'Tap to see the invitation.',
        },
        /* Application result: body is the event name. Rejection copy carries no reason. */
        applicationApproved: {
            title: 'Your application was approved',
            body: '{eventTitle}',
        },
        applicationRejected: {
            title: "There's an update on your application",
            body: '{eventTitle}',
        },
        organizationEventPublished: {
            title: 'New event from an organization you follow',
            body: '{orgName} published a new event: {eventTitle}',
        },
        eventUpdated: {
            title: '{eventTitle} was updated',
            body: 'Date, time or location details changed. Check the new details.',
        },
        eventCancelled: {
            title: '{eventTitle} was cancelled',
            body: 'The event was cancelled. Tap for details.',
        },
        meetingRequestReceived: {
            title: '{requesterName} proposed a meeting',
            body: '{eventTitle} - {durationMinutes} minutes. Open the request in the app.',
        },
        meetingRequestAccepted: {
            title: '{counterpartName} accepted the meeting',
            body: '{eventTitle} - {durationMinutes} minutes. It is on your agenda.',
        },
        meetingRequestDeclined: {
            title: '{counterpartName} did not accept the meeting',
            body: 'Your request for {eventTitle} was answered.',
        },
        meetingRequestCancelled: {
            title: '{counterpartName} cancelled the meeting',
            body: '{eventTitle} - the meeting was removed from your agenda.',
        },
        meetingAreaAssigned: {
            title: 'Your meeting has a spot',
            body: '{eventTitle} - your meeting spot is {areaName}. You can see it on your agenda.',
        },
        cardExchangeOffered: {
            title: '{peerName} wants to exchange cards',
            body: 'Nothing is shared until you approve. Open and decide.',
        },
        cardExchangeConfirmed: {
            title: 'You met {peerName}',
            body: 'The card is in your wallet. Tap to add a note.',
        },
        /* D6: the body carries NO note text — only the person and the way back. */
        followUpReminder: {
            title: 'Time to follow up with {name}',
            body: 'Tap to open your note in the wallet.',
        },
        /* Organizer announcement (T4): "Organization: Subject" and the first 120 characters. */
        organizerAnnouncement: {
            title: '{orgName}: {subject}',
            body: '{body}',
        },
        /* After-event message (certificate and survey Story 6): three content states, no names. */
        postEventCertificateAndSurvey: {
            title: 'Thanks for attending',
            body: '{eventTitle} has ended. Download your certificate and take a short survey.',
        },
        postEventCertificate: {
            title: 'Thanks for attending',
            body: '{eventTitle} has ended. Your certificate is ready.',
        },
        postEventSurvey: {
            title: 'Thanks for attending',
            body: '{eventTitle} has ended. Share your thoughts with the organizer in a short survey.',
        },
        /* Event reminder (reminder Story 1, rule 5): shown on the lock screen, no names. */
        upcomingEventReminderPeople: {
            title: 'Tomorrow: {eventTitle}',
            body: 'Starts at {startTime}. See who you should meet.',
        },
        upcomingEventReminder: {
            title: 'Tomorrow: {eventTitle}',
            body: 'Starts at {startTime}. Tap for the place and details.',
        },
    },
    email: {
        footerNote: 'This email was sent by {brandName} via {appDomain}.',
        footerLegalNote: 'Our privacy notice explains how we process your personal data.',
        footerLegalLink: 'Privacy notice',
        footerUnsubscribe: 'Change notification preferences',
        calendarAttachmentNote: 'The calendar file is attached.',
        otp: {
            subject: 'Your {brandName} sign-in code',
            heading: 'Your sign-in code',
            body: 'Use the code below within {minutes} minutes. Never share it with anyone.',
            cta: 'Back to sign in',
        },
        staffLoginCode: {
            subject: 'Your {brandName} admin panel sign-in code',
            heading: 'Your panel sign-in code',
            body: 'Use the code below within {minutes} minutes to sign in to the admin panel. Never share it; the panel team will never ask you for a code.',
            cta: 'Back to the panel',
        },
        staffInvitation: {
            subject: 'Your {brandName} admin panel invite',
            heading: 'Your panel account is ready',
            body: 'Your admin panel account has been created. Use the link below within {hours} hours to set your password. The link works once.',
            cta: 'Set my password',
        },
        registrationConfirmed: {
            subject: 'Your registration for {eventTitle} is confirmed',
            heading: 'Registration confirmed',
            body: 'You are registered for {eventTitle}. The event starts on {eventDate}.',
            cta: 'View event',
        },
        eventInvitation: {
            subject: 'You are invited to {eventTitle}',
            heading: 'You received an invitation',
            body: '{organizerName} invited you to {eventTitle}. The invitation is valid until {expiresAt}.',
            cta: 'View invitation',
        },
        applicationResult: {
            subjectApproved: 'Your application for {eventTitle} was approved',
            subjectRejected: 'About your application for {eventTitle}',
            headingApproved: 'Application approved',
            headingRejected: 'Application not approved',
            body: 'There is a decision on your application for {eventTitle}.',
            cta: 'View event',
        },
        eventUpdated: {
            subject: '{eventTitle} was updated',
            heading: 'Event details changed',
            body: 'There is an update for {eventTitle}. Please review the new details.',
            cta: 'View event',
        },
        eventCancelled: {
            subject: '{eventTitle} was cancelled',
            heading: 'Event cancelled',
            body: '{eventTitle} was cancelled by the organizer.',
            bodyPlatform: '{eventTitle} has been cancelled and will no longer take place.',
            cta: 'View event',
        },
        teamInvitation: {
            subject: 'You were invited to the {organizationName} team',
            heading: 'Team invitation',
            body: '{inviterName} invited you to the {organizationName} team as {role}.',
            cta: 'View invitation',
            /** Inviter on a panel-opened organization's owner invitation (Story C1): the brand team. */
            staffInviter: 'The {brandName} team',
        },
        claimOwnerNotice: {
            subject: 'There is an ownership claim for {eventTitle}',
            heading: 'Ownership claim for your event',
            body: 'An ownership claim was submitted for {eventTitle}, which you created. You can object within {days} days.',
            cta: 'Review claim',
        },
        claimDecision: {
            subject: 'Decision on the ownership claim for {eventTitle}',
            heading: 'Ownership decision',
            body: 'A decision has been made on your ownership claim for {eventTitle}.',
            cta: 'View details',
        },
        organizationVerificationDecision: {
            subject: '{organizationName} is verified',
            heading: 'Your organization is verified',
            body: 'Your verification application for {organizationName} was approved. You can now publish public and approval-based events and appear in discovery and search results.',
            cta: 'Publish an event',
            subjectRejected: 'Verification application for {organizationName} was rejected',
            headingRejected: 'Your verification application was rejected',
            bodyRejected: 'Your verification application for {organizationName} was rejected. Reason: {reason} You can fix the gaps and reapply right away.',
            ctaRejected: 'Reapply',
        },
        claimInfoRequest: {
            subject: 'More information is needed for your claim on {eventTitle}',
            heading: 'Request for more information',
            body: 'A note from the team reviewing your ownership claim for {eventTitle}: {message}',
            cta: 'View claim',
        },
        claimTransferred: {
            subject: 'The handover of {eventTitle} is complete',
            heading: 'Event transferred',
            body: 'Management of {eventTitle}, the event you created, has passed to {newOrganizerName}. Your registration, your data and your wallet were not affected; your founding contribution record stays with you permanently.',
            cta: 'View event',
        },
        dataExportReady: {
            subject: 'Your data export is ready',
            heading: 'Your data is ready',
            body: 'The data export you requested is ready. The link is valid for {hours} hours.',
            cta: 'Download',
        },
        accountDeletionConfirmed: {
            subject: 'Your account was deleted',
            heading: 'Your account was deleted',
            body: 'Your account and personal data have been deleted. Thank you for using us.',
            cta: 'Start again',
        },
        invitationRequest: {
            subject: 'New invitation requested for an expired invite',
            heading: 'An invitee is asking for a new invitation',
            body: 'The invitation you sent for {eventTitle} expired and {email} asked for a new one. You can send a new invitation from the panel or ignore the request.',
            cta: 'Manage invitations',
        },
        speakerLinkRequest: {
            subject: 'Speaker profile link request',
            heading: 'An organizer wants to link you as a speaker',
            body: 'The organizer of {eventTitle} wants to link their speaker card to your profile. Your profile is not used until you approve; you can approve or reject the request in your settings.',
            cta: 'Review request',
        },
        meetingRequestReceived: {
            subject: '{requesterName} wants to meet you',
            heading: 'You have a new meeting request',
            body: '{requesterName} proposes a {durationMinutes} minute meeting at {eventTitle}. You can see the proposed times and their message in the app, then accept or decline.',
            cta: 'Review the request',
        },
        meetingRequestAccepted: {
            subject: '{counterpartName} accepted your meeting request',
            heading: 'The meeting is on your agenda',
            body: '{counterpartName} accepted your {durationMinutes} minute meeting request at {eventTitle}. You can see the time in the app.',
            cta: 'Open your agenda',
        },
        meetingRequestDeclined: {
            subject: '{counterpartName} answered your meeting request',
            heading: 'Your request was not accepted',
            body: '{counterpartName} did not accept your meeting request at {eventTitle}. No reason is asked for and none is shared.',
            cta: 'Open your requests',
        },
        meetingRequestCancelled: {
            subject: 'The {eventTitle} meeting was cancelled',
            heading: 'The meeting was cancelled',
            body: '{counterpartName} cancelled the meeting at {eventTitle}. The time was removed from both agendas; you can send a new request if you like.',
            cta: 'Open your requests',
        },
        cardExchangeConfirmed: {
            // The subject line carries NO name (Blok 21 tech-lead round, Orta-5).
            subject: 'Card exchange completed',
            heading: 'A new card in your wallet',
            body: 'Your card exchange with {peerName} completed with both sides approving. You can view the card in your wallet and add a short note so that six months from now you still remember who you met and why.',
            cta: 'Open the app',
        },
        followUpReminder: {
            // The subject line carries NO name — same reasoning as cardExchangeConfirmed.
            subject: 'Your follow-up reminder',
            heading: 'Time to follow up',
            body: 'You set a follow-up reminder for {name}. Your note is waiting in your wallet; it is not included in this email.',
            cta: 'Open the wallet',
        },
        emailVerification: {
            subject: 'Confirm your {brandName} account',
            heading: 'Confirm your account',
            body: 'Enter the code below in the app within {minutes} minutes and your account is active right away. Never share this code. If you did not start this sign-up, you can ignore this email.',
            cta: 'Open the app',
        },
        passwordReset: {
            subject: 'Your {brandName} password reset code',
            heading: 'Reset your password',
            body: 'Use the code below within {minutes} minutes to set a new password. This code only resets your password; it cannot sign you in. If you did not ask for a reset, your password stays as it is and you can ignore this email.',
            cta: 'Reset your password',
        },
        accountExistsNotice: {
            subject: 'You already have a {brandName} account',
            heading: 'An account already exists for this address',
            body: 'Someone tried to create a new account with this email address. You already have one, so you can sign in with your password. If you do not remember it, use "Forgot password" on the sign-in screen to set a new one. If this was not you, there is nothing to do. Your account was not touched.',
            cta: 'Sign in',
        },
        eventCeilingWarning: {
            subject: '{eventTitle} is close to its attendee limit',
            heading: 'You reached 80% of the attendee limit',
            body: 'The registration count for {eventTitle} is {used} and the limit is {ceiling}. Once the limit is reached, new registrations, invitation acceptances and application approvals close. Check-in stays open for registered attendees. Contact us about an Enterprise agreement for unlimited attendance.',
            cta: 'View usage',
        },
        eventReportReady: {
            kicker: 'REPORT READY',
            subject: 'Your {eventTitle} report is ready',
            heading: 'Your {eventTitle} report is ready',
            body: 'Your event has ended and the report is final.',
            cta: 'View report',
            statRegistered: 'confirmed registrations',
            statAttendanceRate: 'attendance rate',
            statSatisfaction: 'satisfaction',
            ratePercent: '{rate}%',
            satisfactionValue: '{value} / 5',
            reason: 'You’re receiving this because you manage an event for {orgName}.',
        },
        postEventMessage: {
            kicker: 'AFTER THE EVENT',
            subject: 'Thanks for coming to {eventTitle}',
            heading: 'Thanks for attending',
            bodyCertificateAndSurvey: '{eventTitle} has ended. You can download your certificate and share your thoughts in a short survey.',
            bodyCertificate: '{eventTitle} has ended. Your certificate is ready.',
            bodySurvey: '{eventTitle} has ended. You can share your thoughts with the organizer in a short survey.',
            certificateCta: 'Download certificate',
            surveyCta: 'Take the survey',
            surveyNote: 'The survey closes on {surveyClosesOn}. Your answers are anonymous.',
            reason: 'You’re receiving this because you checked in at {eventTitle}.',
            turnOff: 'Turn off after-event emails',
        },
        /** Event reminder (reminder and suggested people, design H1). */
        eventReminder: {
            kicker: 'TOMORROW',
            subject: 'Tomorrow: {eventTitle}',
            heading: '{eventTitle}',
            body: 'Event details, time and place.',
            bodyPeople: 'Event details and {count} people to meet.',
            bodyPeople_one: 'Event details and {count} person to meet.',
            cta: 'Open event',
            dateLabel: 'WHEN',
            placeLabel: 'WHERE',
            placeOnline: 'Online. The join link is on the event page.',
            placeHybrid: '{venue}. The online join link is on the event page too.',
            peopleTitle: 'People you should meet',
            peopleIntro: 'People coming to the event who have something in common with you.',
            profileLink: 'View profile',
            profileLinkLabel: 'View {name}’s profile',
            reason: 'You’re receiving this because you’re registered for {eventTitle}.',
            turnOff: 'Turn off event reminders',
        },
        organizerAnnouncement: {
            subject: '{subject}',
            heading: '{eventTitle}: announcement from {orgName}',
            kicker: 'Announcement from {orgName}',
            meta: '{eventTitle} · {eventDate}',
            // Gövde organizatörün metnidir (tasarım kare 11); kimden geldiği altbilgide yazar.
            body: '{body}',
            cta: 'View event',
            reason: {
                attendee: 'You received this email because you attended {eventTitle}.',
                registered: 'You received this email because you registered for {eventTitle}.',
                applicant: 'You received this email because you applied to {eventTitle}.',
                follower: 'You received this email because you follow {orgName}.',
            },
            sender: 'The sender, {orgName}, does not see the recipient list.',
            unsubscribe: 'Stop announcements from this organization',
            unsubscribeHint: 'One click, no sign-in needed.',
        },
    },
    calendar: {
        eventPage: 'Event page: {url}',
        addCta: 'Add to calendar',
        downloadIcs: 'Download calendar file (.ics)',
        google: 'Google Calendar',
        outlook: 'Outlook',
        fileError: 'Couldn’t create the calendar file.',
        retry: 'Try again',
    },
    auth: {
        // --- Blok 8-3: Google / Apple sign-in (screen 1) ---
        oauthDivider: 'or',
        oauthGoogle: 'Continue with Google',
        oauthApple: 'Continue with Apple',
        oauthUnavailable: 'This sign-in method is not available right now. You can sign in with your email and password.',
        oauthPending: 'Redirecting you…',
        oauthMergeNote: 'If an account already exists with the same email, no new account is created; your Google or Apple account is linked to it. The name and photo coming from there are only a suggestion and are never written to your profile without your approval.',
        oauthConsentHint: 'Accept the required consents before continuing.',
        emailLabel: 'Your email',
        codeSentBody: 'If this address can receive it, we sent a 6-digit code to {email}. The code is valid for {minutes} minutes.',
        codeLabel: 'Verification code',
        verifyStep: 'Verification',
        resend: 'Send a new code',
        resendIn: 'You can ask for a new code in {time}',
        changeEmail: 'Change email',
        continueWithGoogle: 'Continue with Google',
        continueWithApple: 'Continue with Apple',
        orDivider: 'or',
        privacyDefaultNote: "When you register for an event, your name appears on that event's attendee list. Only registered attendees see the list and your contact details are not on it.",
        consentRequiredHint: 'Check the required consents to continue.',
        signOutConfirmBody: 'This device will be signed out. Your other devices stay signed in.',
        panel: {
            kicker: 'Why {brandName}',
            promise: 'You know who you will talk to before the event even starts.',
            pointGuestsTitle: 'See who is coming, ahead of time',
            pointGuestsBody: 'See the attendees of the events you register for and plan meetings.',
            pointCardTitle: 'Cards need both sides',
            pointCardBody: 'Your card is shared only when both of you approve.',
            pointWalletTitle: 'The people you meet stay',
            pointWalletBody: 'Everyone you meet stays in your wallet, with the event where you met.',
        },
        // --- Block B: product password (user decision 2026-09-05) ---
        password: {
            registerTitle: 'Create your account',
            registerSubtitle: 'Enter your email and a password. We will send a 6-digit code to your email to confirm your account.',
            registerAction: 'Sign up',
            invitedEmailLocked: 'The invitation was sent to this address. Your account is created with it.',
            signInLink: 'Sign in',
            passwordLabel: 'Password',
            newPasswordLabel: 'New password',
            ruleListLabel: 'Password rules',
            ruleMinLength: 'At least {min} characters',
            ruleMaxLength: 'At most {max} characters',
            ruleUppercase: 'At least one uppercase letter',
            ruleSymbol: 'At least one special character (e.g. ! ? * #)',
            ruleMet: 'met',
            ruleUnmet: 'missing',
            ruleAnnouncement: '{rule}, {state}',
            passwordTooShort: 'Your password must be at least {min} characters.',
            passwordTooLong: 'Your password can be at most {max} characters.',
            passwordNeedsUppercase: 'Your password must contain at least one uppercase letter.',
            passwordNeedsSymbol: 'Your password must contain at least one special character (e.g. ! ? * #).',
            emailRequired: 'Enter your email address.',
            passwordRequired: 'Enter your password.',
            showPassword: 'Show password',
            hidePassword: 'Hide password',
            confirmTitle: 'Confirm your email',
            confirmSentBody: 'We sent an email to {email}. If this address is not registered you will get a 6-digit verification code; if it already has an account you will get sign-in details instead. The code is valid for {minutes} minutes.',
            confirmForgotHint: 'Got an "you already have an account" email instead of a code? Reset your password.',
            confirmAction: 'Confirm account',
            confirmLost: 'Your sign-up details are no longer in this browser. Sign up again with the same email and we will send you a new code.',
            confirmRestart: 'Back to sign-up',
            signInTitle: 'Pick up where you left off',
            signInAction: 'Sign in',
            signingIn: 'Signing in',
            registerPrompt: 'No account yet?',
            stepOf: 'Step {current} of {total}',
            codeMissingTitle: 'No code yet?',
            resetNoAccountTitle: 'No account for this address?',
            resetNoAccountBody: 'For security we do not say whether an account exists. If no code arrives, check the address and your spam folder.',
            forgotLink: 'Forgot password',
            registerLink: 'Create an account',
            forgotTitle: 'Reset your password',
            forgotSubtitle: 'Enter your email. If an account exists for this address, we will send a 6-digit reset code.',
            forgotAction: 'Send code',
            backToSignIn: 'Back to sign-in',
            resetCodeTitle: 'Enter the reset code',
            resetCodeAction: 'Continue',
            newPasswordTitle: 'Choose a new password',
            newPasswordSubtitle: 'Pick a new password for your {email} account.',
            newPasswordAction: 'Save password',
            resetDone: 'Your password has been updated. You were signed out on your other devices. You can sign in with your new password.',
            sessionUnverified: 'We could not verify your session. Sign in again to continue.',
            resetLost: 'Your reset details are no longer in this browser. Request a new code.',
            resetRestart: 'Request a new code',
            fixCode: 'Fix the code',
            rateLimitedSeconds: 'Too many attempts. You can try again in {count} seconds.',
            rateLimitedSeconds_one: 'Too many attempts. You can try again in {count} second.',
            rateLimitedWindow: 'Too many attempts. You can try again within {count} minutes.',
            rateLimitedWindow_one: 'Too many attempts. You can try again within {count} minute.',
        },
    },
    // --- Two-level tag picker (Story 2.2, 155 tags) ---
    interests: {
        searchLabel: 'Search tags',
        searchPlaceholder: 'e.g. insurance, logistics, architecture…',
        browseHint: 'Search, or open a field and pick from inside it.',
        selectedLabel: 'Your picks',
        selectedCount: '{count} / {max} tags selected',
        limitReached: 'You can select up to {max} tags. Remove one to add another.',
        removeTag: 'Remove {label}',
        noResult: 'No tag found for “{query}”. Try another word or browse the fields.',
        otherCategory: 'Other',
        categoryNotSelectable: 'Field headings cannot be selected; pick a tag inside the field.',
        taxonomyEmptyTitle: 'The tag list is empty right now',
        taxonomyEmptyBody: 'The server returned no fields or tags right now. You can ask for the list again in a moment.',
        taxonomyReload: 'Reload the list',
    },
    onboarding: {
        title: 'Create your card',
        stepLabel: 'Step {current} / {total}',
        step1Title: 'Basics',
        step2Title: 'Your interests',
        step3Title: 'Your links',
        fullNameLabel: 'Full name',
        jobTitleLabel: 'Job title',
        companyLabel: 'Company',
        professionHint: 'Seniority and sector are optional; filling them in matches you with the right people in attendee lists.',
        headlineLabel: 'Short intro',
        // Step 1 photo field (17.10.2026). Hint and visibility sentence come from `profile.photo*`.
        photoLabel: 'Profile photo (optional)',
        photoUpload: 'Upload a photo',
        photoUploadingPercent: 'Uploading… {percent}%',
        step2Hint: 'Pick at least 1 and up to {max} tags. This helps us recommend the right events.',
        tagSearchPlaceholder: 'Search tags…',
        tagSelectedCount: '{count} tags selected',
        tagSelectedCount_one: '{count} tag selected',
        suggestTag: 'Suggest the tag "{label}"',
        suggestedTagPendingHint: 'Your suggested tag is only visible on your profile until approved.',
        linkedinLabel: 'LinkedIn',
        xLabel: 'X',
        websiteLabel: 'Website',
        phoneLabel: 'Phone',
        contactEmailLabel: 'Contact email',
        skipStep: 'Skip this step',
        finish: 'Create your card',
        previewTitle: 'Card preview',
        previewKicker: 'Business card',
        previewHint: 'This is how others will see your card. Creating a card does not mean you shared it; a card is exchanged only when both sides approve.',
        resumeHint: 'Continuing where you left off.',
        // Web-only step 1 photo error copy (17.10.2026). Shared keys sit under `headlineLabel`.
        photoTooLarge: 'The file is larger than {maxMb} MB. Choose a smaller photo.',
        photoUploadFailed: 'The upload could not be completed.',
        photoRetry: 'Try again',
        step3Hint: 'Only people you exchange cards with can see these. All of them are optional.',
    },
    events: {
        // --- Blok 8-3: discovery is open to visitors; personalization needs a session ---
        visitorRecoBody: 'Once you sign in we show you matching events and a "why this event" note for each one. The list and filters work without signing in.',
        sortLabel: 'Sort',
        sortDate: 'By date',
        sortMatch: 'By match',
        sortClientNote: 'Sorting by match currently covers only the events already loaded into the list. Use "Show more" first to extend the list.',
        controlTitle: 'You are in control',
        controlBody: 'When we suggest events we look at your interests and your attendance history. You can change both at any time, or turn recommendations off completely.',
        controlCta: 'Recommendation settings',
        discoverTitle: 'Discover events',
        discoverSearchPlaceholder: 'Search events…',
        discoverEmpty: 'No events match your search.',
        discoverEmptyTitle: 'No events with these filters',
        discoverEmptyHint: 'Try loosening the filters, or contact us about your own event.',
        discoverLead: 'Browse upcoming events. The more events you attend, the better we can match what we show you.',
        filterTitle: 'Filter',
        filterCityLabel: 'City',
        filterCityAny: 'All cities',
        filterCityPick: 'Pick a city',
        filterCitySearch: 'Search cities',
        filterCityNoMatch: 'No city with that name. Try a simpler spelling.',
        filterFormatLabel: 'Format',
        filterFormatAny: 'All',
        filterTopicLabel: 'Topic',
        filterTopicHint: 'Topic filtering opens once you sign in.',
        filterFromLabel: 'Earliest date',
        filterClear: 'Clear filters',
        emptyStepClearFilters: 'Loosen the filters',
        emptyStepClearFiltersBody: 'Remove the most restrictive field and the list opens up.',
        emptyStepContact: 'Running an event?',
        emptyStepContactBody: 'Contact us and we will open your organization.',
        resultCount: '{count} events',
        resultCount_one: '{count} event',
        resultShown: 'Showing {shown} of {total}',
        loadMore: 'Show more',
        filtersHeading: 'Filters',
        filterFormatHint: 'All, In person, Online, Hybrid',
        resultSortedDate: 'by date',
        resultSortedMatch: 'by match',
        resultShownOf: 'Showing {shown} of {total}',
        badgeRegistered: 'Attending',
        badgeApplied: 'Applied',
        badgeInvited: 'Invited',
        signalsVisitorIntro: 'Once you sign in, we bring forward events that suit you based on your interests and the events you attended. Each suggestion comes with its reason.',
        signalsVisitorBoxTitle: 'After you sign in',
        signalsVisitorBoxBody: 'Your attendance history and interests appear here, and you can change them any time.',
        emptyPreviewLabel: 'An event card looks like this',
        emptyPreviewTitle: 'Product Design Meetup',
        emptyPreviewMeta: 'Free · Istanbul · 120 attendees',
        signalsTitle: 'How we build your recommendations',
        signalsIntro: 'When we suggest events we look at your attendance history and events that people like you attend. Here is what we have so far.',
        signalAttendance: 'Your attendance history',
        signalAttendanceValue: 'You are registered for {count} events',
        signalAttendanceValue_one: 'You are registered for {count} event',
        signalAttendanceEmpty: 'You have not registered for an event yet',
        signalTags: 'Your interest tags',
        signalTagsEmpty: 'You have not picked interest tags yet',
        signalTagsAction: 'Edit your tags',
        signalEncounters: 'People you have met',
        signalEncountersSoon: 'Everyone you meet by QR code at an event is counted here.',
        signalsPrivacyNote: 'Invite-only events never appear in discovery or recommendations.',
        whyRecoTitle: 'Why this list?',
        whyRecoBody: 'Right now you see every upcoming public event. As your attendance builds up, we narrow the list down to your interests and to events attended by people like you.',
        createTitle: 'Create event',
        stepLabel: 'Step {current} / {total}',
        step1Title: 'Basics',
        step2Title: 'Date and location',
        step3Title: 'Access mode',
        step4Title: 'Tickets and capacity',
        step5Title: 'Image and tags',
        titleLabel: 'Event name',
        summaryLabel: 'Short summary',
        summaryHint: 'The single line shown on list cards, in search results and in share previews. Up to {max} characters.',
        summaryCounter: '{count} / {max} characters',
        descriptionLabel: 'Description',
        descriptionHint: 'This text appears only on the event page. Describe the agenda, who should attend and how to get there; you can write up to 20,000 characters.',
        formatLabel: 'Format',
        formatInPerson: 'In person',
        formatOnline: 'Online',
        formatHybrid: 'Hybrid',
        startsAtLabel: 'Starts',
        endsAtLabel: 'Ends',
        timezoneLabel: 'Timezone',
        cityLabel: 'City',
        cityPlaceholder: 'Select a city',
        onlineUrlLabel: 'Online link',
        venueNameLabel: 'Venue name',
        venueNameHint: 'E.g. "Fintek Campus, Block B". Required for in-person and hybrid events.',
        venueAddressLabel: 'Street address',
        venueAddressHint: 'Street, number, district. Attendees get directions from this line.',
        capacityLabel: 'Total capacity (leave empty for unlimited)',
        accessPublicTitle: 'Public',
        accessPublicDesc: 'Anyone registers directly; the page is open to search engines.',
        accessApprovalTitle: 'Application',
        accessApprovalDesc: 'Anyone applies and you approve; the page stays public.',
        accessInviteOnlyTitle: 'Invite only',
        accessInviteOnlyDesc: 'Only people you invite can attend.',
        accessModeRequired: 'Pick an access mode; we do not choose one for you.',
        ticketDefaultHint: 'If you define no ticket type, a single "Full access" type is created automatically with capacity.',
        ticketDefaultHintKind: 'If you add no ticket type, a single "{kind}" type is created automatically with capacity.',
        capacityCeilingHint: "This event's attendee limit is {limit}. Capacity and ticket quotas cannot exceed it.",
        ticketTypeName: 'Type name',
        ticketKindLabel: 'Ticket type',
        ticketKindPlaceholder: 'Select a type',
        ticketQuota: 'Quota',
        addTicket: 'Add ticket type',
        removeTicket: 'Remove ticket type',
        ticketKindRequired: 'Pick a type for every ticket row, or delete the row.',
        ticketQuotaOverflow: 'Total ticket quota ({total}) exceeds the capacity ({capacity}). Raise the capacity or lower the quotas.',
        tagsHint: 'Pick at least 1 interest tag. Your event is recommended to people interested in these tags.',
        tagsRequired: 'Pick at least one interest tag; an event without tags cannot be published.',
        next: 'Next',
        back: 'Back',
        saveDraft: 'Save draft',
        publish: 'Publish',
        createdDraft: 'Event created as a draft.',
        published: 'Event published.',
        pastDateError: 'You cannot create an event with a past date.',
        resumeHint: 'Resuming where you left off.',
        draftResumedAt: 'The draft you left on {time} is back.',
        draftLifetimeHint: 'A draft is kept for a week and is then cleared automatically.',
        draftClear: 'Clear draft',
        draftCleared: 'Draft cleared. The form starts from scratch.',
        step1Desc: 'Give your event a name and briefly say what it is about.',
        step2Desc: 'When and where? Times are shown in the event timezone.',
        step3Desc: 'Who can attend? We do not choose for you; pick one of the three modes yourself.',
        step4Desc: 'Set the capacity. If you define no ticket type, one is created automatically.',
        step5Desc: 'Pick at least one interest tag; reaching the right people depends on it.',
        progressLabel: 'Event setup progress',
        accessPublicPageNote: 'The page is public and visible to search engines.',
        accessApprovalPageNote: 'The page stays public; applications replace registration.',
        accessInviteOnlyPageNote: 'The page is reachable only through an invitation link; it stays out of discovery.',
        previewKicker: 'Live preview',
        previewHint: 'Roughly how your public event page will look.',
        previewTitleFallback: 'Event name',
        previewDateFallback: 'No date selected yet',
        previewVenueFallback: 'No location set yet',
        previewSummaryFallback: 'Your short summary will appear here.',
        previewOrganizerFallback: 'No organizer selected yet',
        previewAccessFallback: 'No access mode selected',
        previewTagsCount: '{count} interest tags',
        previewTagsCount_one: '{count} interest tag',
        // New look 4a (PanelEtkinlikOlustur): preview chip and credit note. The back link is the
        // panel's `organizer.backToMyEvents` label (same word as the top menu).
        previewTagsEmpty: 'No tags added',
        draftsFreeNote: 'Drafts do not use credits.',
        // New look 4a QA: web step names (match the card; mobile reads `step*Title`, untouched).
        webStep2Title: 'Date and place',
        webStep4Title: 'Tickets',
        webStep6Title: 'Review',
        descriptionCounter: '{count} / {max} characters',
        ticketTypesLabel: 'Ticket types',
        ticketDefaultQuota: 'Quota {quota} · Created on publish if no type is added.',
        ticketDefaultUnlimited: 'Unlimited quota · Created on publish if no type is added.',
        ticketKindAddLabel: 'Add type',
        ticketKindNote: 'A type is a label. There is no price or currency, and "Paid" takes no payment.',
        reviewEmpty: 'Not entered yet.',
        reviewTicketsDefault: 'No ticket type added. A single default type is created on publish.',
        reviewCoverYes: 'Cover uploaded',
        reviewCoverNo: 'No cover, the system cover will be used',
        reviewDescriptionWritten: 'Written.',
        reviewDescriptionMissing: 'Not written yet. Writing it before publishing is recommended.',
        checklistCreditNote: 'Saving a draft uses no credits. Publishing uses {credit}, no credit is used under a corporate contract.',
        draftSaveFailed: 'The draft could not be saved. Your entries are still on this device.',
        /*
          --- "Organizer" field (item 24 · part 3, user decision 30.08) ---
          The field is no longer called "Organization": people come here to say who
          the event is published on behalf of, not to manage an organization.
         */
        organizationLabel: 'Organizer',
        organizationHint: 'The event is published on behalf of this organization. If you have none, create one from the list.',
        organizationPlaceholder: 'Select an organizer',
        organizationRequired: 'Pick an organizer.',
        organizationNoRightsTitle: 'Your role cannot create events',
        organizationNoRightsBody: 'You are a member of {organizationName}, but your role does not allow creating events. Ask an owner to raise your role, or create a new organization.',
        organizationNoRightsCta: 'Go to my organization',
        organizationNoRightsBodyMany: 'Your role does not allow creating events in the organizations you belong to. Ask an owner to raise your role, or create a new organization.',
        organizationNoRightsCtaMany: 'Go to my organizations',
        /* Title and body of the organization form that opens inside the field. */
        organizationFormTitle: 'New organization',
        organizationFormBody: 'Create your organization here and the event is published on its behalf. Nothing you entered is lost and you continue in the same place.',
        organizationCreateCta: 'Create a new organization',
        organizationCreateCancel: 'Cancel organization setup',
        organizationCreatedNotice: 'Your organization was created and selected.',
        // --- Consequences of changing access mode (design: warning + info line) ---
        accessWarningPublic: 'Switching to public opens the event page to search engines and lists it in discovery.',
        accessWarningApproval: 'In application mode the page stays public, but applications replace direct registration and you decide on each one.',
        accessWarningInviteOnly: 'Invite-only events never appear in discovery or recommendations; the page is reachable only through an invitation link.',
        // Verification is a PRIVILEGE, not a permission (decision 31.08): the copy
        // says what it earns, not what it blocks; the application path stays visible.
        accessVerificationNote: 'You can publish in this mode right away. Verifying your organization also lists the event in discovery and recommendations, and shows a “verified organization” badge on its page.',
        accessVerificationCta: 'Apply for verification',
        // Last wizard step: the event was created but publishing hit the gate.
        createdButNotPublished: 'Your event was saved as a draft but could not be published. The draft is waiting under "Managing"; fix the reason above and publish it from the panel.',
        publishOutcomeUnknown: 'Your event was saved as a draft. We could not confirm the result of publishing. You can check its status under "Managing".',
        goToMyEvents: 'Go to the events you manage',
        accessNoteVisibility: 'The access mode does not change the attendee list. In every mode the list is open only to registered attendees and everyone who registers appears on it. An invitation alone does not put anyone on the list.',
        // --- Pre-publish checklist (design screen 17, step 4) ---
        checklistTitle: 'Pre-publish check',
        checklistIntro: 'See what is missing before you publish. Edit takes you to the step of the missing item.',
        checkOk: 'Done',
        checkTodo: 'Missing',
        checkOrganizationLabel: 'Organization selected',
        checkOrganizationDesc: 'The event is published on behalf of this organization; who can manage it comes from the roles there.',
        checkBasicsLabel: 'Title and summary written',
        checkBasicsDesc: 'Title and summary appear in search results and on the discovery card.',
        checkScheduleLabel: 'Date and time zone set',
        checkScheduleDesc: 'Start and end times land in attendee agendas in this time zone.',
        checkPlaceLabel: 'Venue or link provided',
        checkPlaceDesc: 'In-person events need a city, venue name and street address; online events need a link.',
        checkAccessLabel: 'Access mode selected',
        checkAccessDesc: 'You must choose who can attend; we do not choose for you.',
        checkTagsLabel: 'Interest tags added',
        checkTagsDesc: 'Your event is recommended to people interested in the tags you pick. An event without tags cannot be recommended.',
        checkCoverLabel: 'Cover image added',
        checkCoverDesc: 'Optional. Without one, a system cover is used.',
        checklistBlocked: 'Required items are missing. The event can’t be saved or published until they are complete.',
        step6Title: 'Pre-publish check',
        step6Desc: 'One last look. Finish anything missing before you publish.',
        listHeading: '{count} upcoming events',
        listHeading_one: '{count} upcoming event',
        nearTopicsTitle: 'Related topics',
        nearTopicsHint: 'Tap a topic; the filter switches to it.',
        filterScreenTitle: 'Filter',
        filterApplyCta: 'Show {count} events',
        filterApplyCta_one: 'Show {count} event',
        filterApplyLoading: 'Counting results…',
        filterApplyGeneric: 'Show results',
        filterOpenCta: 'Filter',
    },
    myEvents: {
        orgFilterLabel: 'Organization',
        orgFilterAll: 'All organizations',
        orgSelectorLabel: 'Organization selection',
        quotaLabel: '{count} seats',
        quotaLabel_one: '{count} seat',
        quotaUnset: 'No capacity set',
        quotaCountUnavailable: 'The registration count is not shown in this list; you can see it in the management panel.',
        warnNoMeetingArea: 'You have not defined a meeting area. Attendees cannot arrange meetings at this event.',
        warnNoMeetingAreaCta: 'Add an area',
        warnUnverified: 'Your organization is not verified. Your event is not listed in discovery or recommendations.',
        warnUnverifiedCta: 'Start verification',
        kicker: 'Organizer',
        title: 'Events you manage',
        lead: 'Events you created and manage. This is the way into the management panel.',
        segAll: 'All',
        segDraft: 'Draft',
        segPublished: 'Live',
        segPast: 'Past',
        countLabel: '{count} events',
        countLabel_one: '{count} event',
        manageCta: 'Manage',
        viewCta: 'View page',
        attendeesCta: 'Attendees',
        draftNotice: 'Draft. Only you can see it.',
        cancelledNotice: 'Cancelled.',
        // Tur V2: yalnız görüntüleyici rolündeyken liste salt okunurdur (yönetim eylemi yok).
        viewerTitle: 'Your organization’s events',
        viewerLead: 'With the Viewer role you can see your organization’s events but not change them. Ask your organization admin for the Editor role to manage them.',
        viewerEmptyTitle: 'Your organization has no events yet',
        viewerEmptyBody: 'When your organization’s admins open an event, it’s listed here and you can open its page from here.',
        draftNoticeReadOnly: 'Draft. Its page opens once it’s published.',
        emptyTitle: 'You have no events yet',
        emptyBody: 'Once your event lives here, registration, the attendee list and the agenda are all in one place.',
        emptyStep1: 'Pick or create your organization',
        emptyStep1Body: 'Events are always published on behalf of an organization.',
        emptyStep2: 'Create the event and choose an access mode',
        emptyStep2Body: 'Public, approval-based or invite-only.',
        emptyStep3: 'Invite attendees',
        emptyStep3Body: 'Invitation list and application queue live in the management panel.',
        emptyAction: 'Create event',
        emptyPreviewLabel: 'An event row looks like this',
        emptyPreviewTitle: 'Product Teams Meetup',
        emptyPreviewMeta: '12 March · Istanbul · 48 registered',
        filteredEmptyTitle: 'No events in this tab',
        filteredEmptyBody: 'Check another tab or create a new event.',
        loadError: 'Your events could not be loaded.',
    },
    profile: {
        editTitle: 'Profile and card',
        visibilityPromise: 'This information appears on the attendee list of the events you register for (only to their registered attendees) and in the card exchanges you approve.',
        visibilityShortcut: 'Go to privacy settings',
        missingStepsHint: 'Your setup has missing steps.',
        // --- Block 8-2: the persistent version of screen 2 (profile + card) ---
        kicker: 'Your account',
        title: 'Profile and card',
        lead: 'Your card is the face that shows up in attendee lists and card exchanges. You can update it any time.',
        accountTitle: 'Account',
        displayNameLabel: 'Display name',
        displayNameHint: 'This is how the app refers to you.',
        emailLabel: 'Account email',
        emailReadOnlyHint: 'The account email cannot be changed here.',
        photoLabel: 'Profile photo',
        photoHint: '{formats} · up to {maxMb} MB. Without a photo we show your initials.',
        photoEmpty: 'No photo yet.',
        photoUploading: 'Uploading…',
        photoSaved: 'Photo updated.',
        photoRemoved: 'Photo removed.',
        photoPick: 'Add a photo',
        photoReplace: 'Replace the photo',
        photoRemove: 'Remove the photo',
        photoVisibility: 'Your photo is only visible to attendees of the events you register for and to people you exchange cards with.',
        cardsTitle: 'Your card',
        cardsHint: 'Your name, title and the fields you fill in are read from this card in attendee lists.',
        fullNameLabel: 'Full name',
        jobTitleLabel: 'Job title',
        companyLabel: 'Company',
        headlineLabel: 'One-line intro',
        bioLabel: 'About',
        professionTitle: 'Seniority and sector',
        professionHint: 'Attendee lists can be filtered by these two fields.',
        seniorityLabel: 'Seniority',
        sectorLabel: 'Sector',
        educationTitle: 'Education',
        educationSchoolLabel: 'School',
        educationFieldLabel: 'Field',
        educationYearLabel: 'Graduation year',
        contactTitle: 'Contact and links',
        contactHint: 'Your contact details are NEVER shown in attendee lists; they are shared only in card exchanges you approved.',
        contactEmailLabel: 'Email shown on the card',
        phoneLabel: 'Phone',
        websiteLabel: 'Website',
        linkedinLabel: 'LinkedIn',
        xLabel: 'X',
        githubLabel: 'GitHub',
        interestsTitle: 'Your interest tags',
        interestsHint: 'The tags you pick decide who gets suggested to you and what the “why this person” reasons say.',
        interestsSelected: '{count} tags selected',
        interestsSelected_one: '{count} tag selected',
        interestsSearchLabel: 'Search tags',
        previewTitle: 'This is how your card looks',
        previewHint: 'Creating a card is not sharing it; sharing always requires mutual consent.',
        saveCta: 'Save',
        savedNotice: 'Your changes were saved.',
        savedNoticeAccount: 'Your account details were saved.',
        savedNoticeCard: 'Your card was saved.',
        savedNoticeInterests: 'Your interests were saved.',
        cardUnsaved: 'Your card has unsaved changes',
        deleteCardCta: 'Delete card',
        deleteCardConfirmTitle: 'Delete this card?',
        deleteCardConfirmBody: 'This card will be deleted. Cards you already shared stay in the other person’s wallet.',
        deletingCard: 'Deleting…',
        cardsEmptyTitle: 'You have no card yet',
        cardsEmptyBody: 'Without a card, attendee lists show nothing but your name.',
        loadError: 'Your profile could not be loaded.',
        yearRangeHint: 'Between {min} and {max}',
    },
    sessions: {
        title: 'Sessions and devices',
        currentBadge: 'This device',
        lastUsed: 'Last used: {time}',
        revoke: 'End session',
        revokeAll: 'Sign out of all devices',
        revokeAllConfirm: 'All sessions, including this device, will be closed. Are you sure?',
        empty: 'No active sessions.',
        unknownDevice: 'Unknown device',
        intro: 'Devices signed in to your account. If you do not recognise one, end its session.',
        revokeOthers: 'End other sessions',
        revokeOthersConfirm: 'Every session except this device will be closed. Continue?',
        revokingOthers: 'Signing out sessions…',
        revokeOthersNone: 'No other session is open besides this device.',
        othersEmptyTitle: 'No other sessions',
        othersEmptyBody: 'You are signed in on this device only.',
        deviceAndroidApp: 'Android · {brandName} app',
        deviceIosApp: 'iPhone · {brandName} app',
        deviceBrowserOnOs: '{browser} · {os}',
        deviceBrowserOnly: '{browser} browser',
        deviceTool: '{tool} client',
        clientWeb: 'Browser',
        clientMobile: 'Mobile app',
        clientUnknown: 'Unknown client',
    },
    consentSettings: {
        historyTitle: 'Consent history',
        grantedAt: 'Granted: {time}',
        withdrawnAt: 'Withdrawn: {time}',
        withdraw: 'Withdraw consent',
        grant: 'Give consent',
        version: 'Document version: {version}',
        title: 'My consents',
        lead: 'See which document you approved and at which version. You can withdraw optional consents whenever you want.',
        requiredGroup: 'Required for the service',
        optionalGroup: 'Optional, withdrawable at any time',
        requiredLockHint: 'This is required for the service, so it cannot be withdrawn here. If you want out, you can delete your account.',
        acknowledgedNote: 'Recorded as read.',
        noRecord: 'No consent record found.',
        outdated: 'A newer version of this document has been published.',
        historyGranted: 'Granted',
        historyWithdrawn: 'Withdrawn',
        historyEmpty: 'No consent activity yet.',
        openDocument: 'Open',
        downloadRecord: 'Download the record',
        downloadHint: 'Your consent ledger comes inside the data export; you create the request in the browser.',
        emptyTitle: 'Your consent records are not showing',
        emptyBody: 'The approvals you gave while opening your account are listed here. If the list is empty the records may not have arrived yet.',
        emptyStep1: 'Check your connection',
        emptyStep1Body: 'While offline the consent ledger cannot be fetched from the server.',
        emptyStep2: 'Reload the list',
        emptyStep2Body: 'The button below asks for the records again.',
        emptyStep3: 'Download your data',
        emptyStep3Body: 'If it keeps failing, your consent ledger comes inside the data export.',
        reload: 'Reload the list',
    },
    speakersSection: {
        title: 'Speakers',
        empty: 'Speakers will be announced soon.',
        linkedBadge: 'Verified profile',
        sessionsLabel: 'Sessions',
    },
    agendaSection: {
        title: 'Agenda',
        empty: 'The program will be published soon.',
        locationLabel: 'Room: {location}',
        daysLabel: 'Day picker',
    },
    attendeeList: {
        title: 'Attendees',
        counter: '{total} registered',
        // --- Finding 7: the screen states WHY access is granted (silence read as a leak)
        accessReasonManager: 'You see this list as an organizer.',
        accessReasonAttendee: 'You see this list because you are registered for this event.',
        requiresRegistration: 'Register for the event to see the attendee list.',
        // --- Manual test 14.10: a closed event states why instead of asking to register (`ATTENDEE_LIST_LOCK_COPY`)
        closedEndedTitle: 'This event has ended',
        closedCancelledTitle: 'This event was cancelled',
        closedBody: 'The attendee list is open only to registered attendees.',
        closedEndedNote: 'This event has ended. The attendee list is open only to registered attendees.',
        closedCancelledNote: 'This event was cancelled. The attendee list is open only to registered attendees.',
        closedMeta: 'Registered attendees only',
        signInPrompt: 'Sign in to see the attendees.',
        empty: 'Nobody has registered yet. Everyone who registers appears on this list.',
        emptyTitle: 'The list is quiet for now',
        searchPlaceholder: 'Search by name',
        companyPlaceholder: 'Company',
        titlePlaceholder: 'Title',
        filterCta: 'Filter',
        loadMore: 'Show more',
        teaserCardTitle: 'Attendee',
        teaserCardMeta: 'Visible once you register',
        previewLabel: 'An attendee looks like this',
        visitorCtaTitle: 'See who is coming once you register',
        visitorCtaBody: 'Register to see the attendees and set up meetings before the event starts. Once you register, you appear on the list too.',
        visitorCtaAction: 'Go to registration',
        listHint: 'Everyone who registers for the event is on this list and only registered attendees can see it. Contact details are never shown.',
        // --- Block 8-2: dedicated list route + screen 6b filters ---
        filtersTitle: 'Filter',
        filterAny: 'All',
        seniorityFilterLabel: 'Seniority',
        sectorFilterLabel: 'Sector',
        tagFilterLabel: 'Interest',
        clearFilters: 'Clear',
        resultLabel: '{count} people listed',
        resultLabel_one: '{count} person listed',
        openFullList: 'See all attendees',
        openProfile: 'Open {name}’s profile',
        // --- B-32: the viewer's own row ---
        youBadge: 'You',
        openOwnPreview: 'Open your profile preview',
        selectPersonTitle: 'Select a person',
        selectPersonBody: 'Pick someone from the list; their profile opens here.',
        filteredEmptyTitle: 'No one matches these filters',
        filteredEmptyBody: 'No attendee matches the filters you selected.',
        filteredEmptyAction: 'Clear filters',
        backToEvent: 'Back to the event page',
        loadError: 'The attendee list could not be loaded.',
        // --- Blok 8-3: filter facet counts ---
        facetLabel: '{label} ({count})',
        facetsNote: 'Counts cover everyone on the attendee list.',
        // --- Attendee directory redesign (sections 02 · 03 · 04 · 05 · 06 · 09)
        trustNote: 'Everyone who registers for the event is on this list and only registered attendees can see it. Nobody’s contact details are shown. A meeting request is not a card share, cards are always exchanged with mutual approval.',
        promiseLabel: 'List view',
        promiseAll: 'Everyone',
        promiseRecommended: 'Picked for you',
        promiseHint: 'Picks come from your attendance history, and each one writes its reason.',
        activeFiltersLabel: 'Active filters',
        removeFilter: 'Remove the {label} filter',
        facetRatio: '{shown} / {total}',
        facetShowAll: 'See all · {count} more',
        facetShowAll_one: 'See all · {count} more',
        facetShowLess: 'Show fewer',
        facetZeroNote: '{count} more options here match nobody on this list, so they stay hidden.',
        facetZeroNote_one: '{count} more option here matches nobody on this list, so it stays hidden.',
        facetZeroRule: 'Counts come from this event’s attendee list. Options without results are hidden.',
        /** Bölüm 06: sonuç şeridi. */
        pageRange: '1–{shown} of {total}',
        detailClose: 'Close the person panel',
        visitorCtaSecondary: 'See the event',
    },
    personProfile: {
        kicker: 'Attendee',
        speakerBadge: 'Speaker',
        organizerBadge: 'Staff',
        aboutTitle: 'About',
        interestsTitle: 'Interests',
        contextTitle: 'Context',
        contextBody: 'Visible on the attendee list of {event}.',
        professionTitle: 'Seniority and sector',
        cardNote: 'Card details are shared only after mutual consent.',
        contactHidden: 'Contact details are not shown.',
        notFoundTitle: 'This person is not on the list',
        notFoundBody: 'The person may have cancelled or changed their registration. Go back to the attendee list and try again.',
        notFoundAction: 'Back to the attendee list',
        // --- Blok 8-3: shared events ---
        sharedEventsTitle: 'Events you both attended',
        sharedEventsEmpty: 'No other shared events with this person.',
        sharedEventsNote: 'Only events you are both registered for are listed.',
        // --- B-32: preview of your own row ---
        selfPreviewTitle: 'This is how other attendees see you',
        selfPreviewBody: 'Your own row has no meeting request or card action. You can change what is shown in your profile.',
        selfEditProfile: 'Edit your profile',
    },
    report: {
        menuLabel: 'Report',
        menuTitle: 'For this person',
        title: 'Report {name}',
        body: 'Your report goes to the moderation team. The other person is not notified and your identity is not shared.',
        reasonLabel: 'Reason',
        reasons: {
            fake: 'Fake account',
            spam: 'Spam or advertising',
            inappropriate: 'Inappropriate content or behaviour',
            personal_data: 'Sharing personal data without consent',
            other: 'Other',
        },
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'Briefly describe what happened.',
        submit: 'Report',
        receivedTitle: 'Report received',
        receivedBody: 'The moderation team will review it. Action is taken on the account if needed; you can also block this person.',
        alreadyBody: 'You already reported this person; no new record was opened.',
        close: 'Close',
        announcementTitle: 'Report announcement',
        announcementBody: 'Your report goes to the moderation team. The organization is not notified and your identity is not shared.',
        announcementReceivedBody: 'The moderation team will review the announcement.',
        announcementAlreadyBody: 'You already reported this announcement; no new record was opened.',
    },
    meetings: {
        title: 'Requests',
        lead: 'Incoming and outgoing meeting requests live here. Start meeting people before the event begins.',
        action: 'Send a meeting request',
        unavailable: 'A meeting request cannot be sent to this person right now.',
        notCardExchange: 'A meeting request is not a business card exchange.',
        cardShareNote: 'Card details are shared only after mutual approval.',
        pendingBadge: 'Request sent · awaiting reply',
        pendingNote: 'It is not added to your agenda until {name} accepts.',
        remaining: 'You have {count} open requests left for this event.',
        remaining_one: 'You have {count} open request left for this event.',
        blockedReason: {
            open_request_exists: 'You already have a request awaiting a reply from this person.',
            declined_before: 'This person answered your request at this event; you cannot send a new one.',
            event_quota_reached: 'You have used every open request for this event. Send a new one once a pending request is resolved.',
            blocked_by_you: 'You blocked this person. Remove the block before sending a request.',
            event_closed: 'This event is closed to meeting requests; no new request can be sent.',
        },
        blockedByYouAction: 'Go to blocked people',
        errorOffline: 'No connection. The action was not sent; try again once you are online.',
        errorGone: 'This request is no longer on your list. Refresh the page to see the current state.',
        requiresAttendance: 'You must be registered for the event to send a meeting request.',
        formTitle: 'Meeting request',
        close: 'Close',
        durationLabel: 'Duration',
        durationOption: '{minutes} minutes',
        durationRequired: 'Pick a duration.',
        slotsLabel: 'Times you suggest',
        proposedSlotsLabel: 'Proposed times',
        slotsHint: 'Pick at least 1 and at most {max} times.',
        slotsRequired: 'Suggest at least one time.',
        slotsFull: 'You can suggest at most {max} times.',
        slotsEmpty: 'There are no times left to suggest for this event.',
        slotsNeedDuration: 'Pick a meeting length first and the available times appear here.',
        slotsSelected: '{count} / {max} times selected',
        // Selected row label in the mobile slot grid (reference `isForm`).
        slotSelected: 'selected',
        // A slot that overlaps the caller's OWN accepted meeting (18.10.2026).
        slotBusy: 'busy',
        slotBusyA11y: '{time}, busy. You already have an accepted meeting at this time.',
        slotsBusyNote: 'Times marked busy overlap a meeting you have already accepted.',
        areaLabel: 'Meeting area',
        areaAny: 'No specific area',
        areaOption: '{name} · {hours}',
        areaHoursAll: 'all event long',
        areaDaysHint: 'Open only on: {days}',
        areaFilterNote: 'Times are filtered by the availability window of the area you picked.',
        slotsAreaEmpty: 'No times are left inside this area availability window. Pick another area or duration.',
        // Izgara tavanı SESSİZ kesmez (Blok 14 QA notu → Blok 16): kısıt yazılır.
        slotsTruncatedDays: 'This event runs long; only the first {days} days are listed here. Check again closer to the event for later days.',
        messageLabel: 'Message (optional)',
        messagePlaceholder: 'Write in one sentence what you would like to discuss.',
        messageCounter: '{count} / {max}',
        messageTooLong: 'The message can be at most {max} characters.',
        footerNote: 'The request is not added to your agenda until {name} accepts.',
        send: 'Send request',
        sending: 'Sending…',
        offlineNote: 'You are offline. The request cannot be sent; try again once you reconnect.',
        sentKicker: 'Request sent',
        sentTitle: 'Awaiting a reply from {name}',
        sentTitleAccepted: '{name} accepted your request',
        sentTitleAnswered: 'Your request was answered',
        sentTitleClosed: 'Your request is closed',
        backToList: 'Back to list',
        goToRequests: 'Go to my requests',
        progressSent: 'Sent',
        progressSentMeta: '{date} · {duration} minutes, you suggested {count} times',
        progressSentMeta_one: '{date} · {duration} minutes, you suggested {count} time',
        progressResponse: 'Approval from {name}',
        progressResponseWaiting: 'Waiting',
        progressResponseAccepted: 'Accepted',
        progressResponseDeclined: 'Answered, no reason is shared',
        progressResponseCancelled: 'Request cancelled',
        progressResponseExpired: 'Suggested times have passed',
        progressAgenda: 'Added to the agenda',
        progressAgendaIdle: 'The accepted time lands on both agendas',
        incomingTitle: 'Incoming requests',
        incomingLead: 'When you accept, the time lands on both agendas. If you decline, no reason is sent to the other side.',
        outgoingTitle: 'Requests you sent',
        colPerson: 'Person',
        colTopic: 'Topic',
        colRequest: 'Request',
        colStatus: 'Status',
        colActions: 'Actions',
        noMessage: 'No message was written.',
        accept: 'Accept',
        decline: 'Decline',
        withdraw: 'Withdraw request',
        cancelMeeting: 'Cancel meeting',
        block: 'Block',
        acceptTitle: 'Which time are you accepting?',
        acceptBody: 'The time you pick lands on both agendas. Accepting is not a business card exchange; no contact details are shared.',
        acceptConfirm: 'Accept',
        conflictOwnMeeting: 'Your agenda already has a meeting at this time: {slot} · {name}',
        declineTitle: 'Decline the request from {name}',
        declineBody: 'No reason is sent to the other side; only the fact that you answered.',
        withdrawTitle: 'Withdraw the request',
        withdrawBody: 'The request stays on their list as “Cancelled” and they are notified.',
        cancelMeetingTitle: 'Cancel the meeting',
        cancelMeetingBody: 'The meeting leaves both agendas and the other side is notified. Send a new request for another time.',
        blockTitle: 'Block {name}',
        blockBody: 'This person cannot send you meeting requests. No notification is sent and your identity is not revealed. You keep seeing them in attendee lists and they keep seeing you.',
        blockConfirm: 'Block',
        snapshotNote: 'The name and title are shown as they were when the request was sent.',
        requestedAt: 'Requested: {date}',
        durationValue: '{minutes} minutes',
        slotCount: '{count} times suggested',
        slotCount_one: '{count} time suggested',
        acceptedSlot: 'Accepted time: {slot}',
        incomingEmptyTitle: 'No requests for you yet',
        incomingEmptyBody: 'When you register for an event you appear on its attendee list and other attendees can send you meeting requests.',
        incomingEmptyVisibleBody: 'You appear on the attendee list of the events you registered for, so attendees can send you requests. Instead of waiting, you can make the first move too.',
        incomingEmptyVisibleAction: 'See who is coming',
        incomingPickTitle: 'Which event should we look at?',
        incomingPickEmpty: 'No upcoming event with a list you can open is left.',
        incomingEmptyClosedBody: 'You turned off meeting requests. Turn them on and attendees can send you requests. Your place on the attendee list does not change.',
        incomingEmptyClosedAction: 'Turn requests back on',
        outgoingEmptyTitle: 'You have not sent a request yet',
        outgoingEmptyBody: 'Pick someone from attendee discovery and send a request. Arranging a meeting before the event works better than trusting the hallway.',
        outgoingEmptyAction: 'Go to events',
        previewLabel: 'A request looks like this',
        previewName: 'Attendee',
        previewMeta: 'Awaiting reply',
        loadError: 'Requests could not be loaded.',
        loadMore: 'Show more',
        status: {
            pending: 'Awaiting reply',
            accepted: 'Accepted',
            declined: 'Declined',
            cancelled: 'Cancelled',
            expired: 'Expired',
        },
        settingsTitle: 'Meeting requests',
        receiveLabel: 'Receive meeting requests',
        receiveHint: 'On by default.',
        receiveNoteOn: 'While this is on, people who can see you in an attendee list can send you meeting requests.',
        receiveNoteOff: 'While off, nobody can send you requests and you are not shown who tried. This setting does not remove you from the attendee list.',
        notifyLabel: 'Notify me about new requests',
        notifyLocked: 'If you accept requests you need to know about them; this notification cannot be turned off.',
        notifyDisabled: 'This notification is not sent because you turned off receiving requests.',
        blockedTitle: 'Blocked people',
        blockedLead: 'These people cannot send you meeting requests. You keep seeing them on attendee lists and they keep seeing you. Blocking does not remove anyone from the list.',
        blockedEmptyTitle: 'You have not blocked anyone',
        blockedEmptyBody: 'If you block someone they cannot send you meeting requests. Blocking is done with the “Block” action on an incoming request row and the other side is not notified.',
        blockedSince: 'Blocked on {date}',
        unblock: 'Remove block',
        unblockTitle: 'Remove block for {name}',
        unblockBody: 'This person can send you meeting requests again. No notification is sent. If needed, you can block them again with the “Block” action on an incoming request row.',
        blockedCount: '{count} people',
        blockedCount_one: '{count} person',
        blockedNone: 'You have not blocked anyone',
        blockedOpenCta: 'Blocked people',
        blockedScopeNote: 'Turning off requests does not remove you from the attendee list. The way to leave the list is to cancel your registration.',
    },
    /**
     * Personal agenda (screen 10d). Times are ALWAYS in the event timezone;
     * meeting rows are highlighted, session rows stay neutral.
     */
    myAgenda: {
        title: 'My agenda',
        lead: 'Sessions and accepted meetings in one flow.',
        meetingTag: 'Meeting',
        sessionTag: 'Session',
        meetingTitle: 'Meeting · {name}',
        meetingMeta: '{duration} minutes',
        meetingMetaWithArea: '{duration} minutes · {area}',
        meetingMetaNoArea: '{duration} minutes · venue to be assigned',
        timeRangeA11y: 'from {start} to {end}',
        conflictNotice: 'Another entry in your agenda overlaps this time',
        openEventA11y: '{title}, go to event',
        dayToday: 'Today',
        dayTomorrow: 'Tomorrow',
        filterAll: 'All events',
        loadError: 'Couldn’t load your agenda. Check your connection and try again.',
        retry: 'Try again',
        emptyKicker: 'My agenda',
        emptyTitle: 'Your agenda is empty',
        emptyBody: 'Sessions from the events you joined and your accepted meetings appear here in a single flow.',
        emptyStep1: 'Register for an event.',
        emptyStep1Body: 'Once you register, the event agenda lands in this list.',
        emptyStep2: 'See who’s coming and request a meeting.',
        emptyStep2Body: 'Pick someone from the attendee list and send a meeting request.',
        emptyStep3: 'Accepted meetings are added here.',
        emptyStep3Body: 'When the other person accepts, the row is added to both agendas.',
        emptyAction: 'Discover events',
        previewLabel: 'Example · an accepted meeting looks like this',
        previewTitle: 'Meeting · Attendee',
        previewTime: '15:00',
        previewMeta: '30 minutes · Foyer table 3',
        nextKicker: 'Up next',
        nextInMinutes: 'In {count} minutes',
        nextInMinutes_one: 'In 1 minute',
        nextToday: 'Today {time}',
        nextTomorrow: 'Tomorrow {time}',
        nextOn: '{day} {time}',
        viewProfile: 'View profile',
        placePending: 'Location to be assigned',
        placePendingHint: 'The spot is assigned automatically once the organizer opens meeting areas.',
        kindFilterA11y: 'Entry type',
        kindAll: 'All',
        kindMeetings: 'Meetings',
        kindSessions: 'Sessions',
        showPast: 'Show past',
        goToEvent: 'Go to event',
        nowMarker: 'Now',
        timeRange: '{start}–{end}',
        durationShort: '{duration} min',
        gapHours: 'You have {count} hours free',
        gapHours_one: 'You have 1 hour free',
        gapHoursMinutes: 'You have {hours} h {minutes} min free',
        gapMeta: '{range} · {event}',
        gapCta: 'Set up a meeting',
        noResultTitle: 'Nothing matches this filter',
        noResultBodyMeetings: 'You have no accepted meetings from today on under the Meetings filter. You can change the filter.',
        noResultBodySessions: 'You have no sessions from today on under the Sessions filter. You can change the filter.',
        noResultBody: 'No session or meeting matches your filter. You can change the filter.',
        clearFilter: 'Clear filter',
        previewName: 'Attendee',
        previewSub: 'Title · Organization',
        previewStart: '11:00',
        previewEnd: '11:30',
        previewPlace: 'Foyer · Table 3',
        offlineNotice: 'You are offline. Showing your agenda as you last opened it.',
        eventLabel: 'Event',
        meetingOpenA11y: 'Open meeting details with {name}',
        sheetA11y: 'Meeting details',
        sheetTime: 'Time',
        sheetDuration: 'Duration',
        sheetPlace: 'Location',
        sheetPlacePending: 'Location to be assigned. It is assigned automatically once the organizer opens meeting areas.',
    },
    participationHistory: {
        title: 'Events you attended',
        lead: 'Events you registered for, applied to or were invited to, in one list. Only you can see it.',
        loadError: 'Could not load your participation.',
        emptyKicker: 'Your participation',
        emptyTitle: 'No participation yet',
        emptyBody: 'Once you register for an event its row stays here. Your check-in, accepted meetings and the people you met are read from that row.',
        emptyStep1: 'Discover events',
        emptyStep1Body: 'Browse upcoming events and pick the right one.',
        emptyStep2: 'Register',
        emptyStep2Body: 'Registration opens the attendee list and meeting requests.',
        emptyStep3: 'Come back after the event',
        emptyStep3Body: 'Your attendance, meetings and the people you met collect here.',
        emptyAction: 'Discover events',
        previewLabel: 'This is how your row will look',
        previewTitle: 'Data Engineering Meetup',
        previewMeta: 'İzmir · half day',
        previewResult: 'Met 3 people · 1 meetings',
    },
    /** Organizer meeting areas (Story 13.2 - screen `venuesFull`). */
    meetingAreas: {
        tab: 'Meeting areas',
        title: 'Meeting areas',
        /*
         * Üç BAĞIMSIZ sayı: çevirmen cümle başına tek `count` çekimliyor, bu yüzden
         * parçalar ayrı anahtarlardır ("1 areas · 1 accepted meetings" kusuru).
         */
        areaCount: '{count} areas',
        areaCount_one: '1 area',
        acceptedMeetingCount: '{count} accepted meetings',
        acceptedMeetingCount_one: '1 accepted meeting',
        freeSlotCount: '{count} free slots',
        freeSlotCount_one: '1 free slot',
        summary: '{areasLabel} · {meetingsLabel} · ≈ {freeLabel}',
        unplacedMeetingCount: '{count} meetings waiting for a spot',
        unplacedMeetingCount_one: '1 meeting waiting for a spot',
        summaryWithUnplaced: '{summary} · {unplacedLabel}',
        curationNote: 'Attendees cannot type a place; every accepted meeting is assigned one of these areas instead. Because there is no free text, nobody can share a private location or call the other person somewhere they did not choose.',
        add: 'Add an area',
        edit: 'Edit',
        remove: 'Delete',
        capacityLabel: 'Capacity',
        capacityValue: '{count} concurrent',
        capacityHint: 'How many meetings can run at the same time.',
        availabilityLabel: 'Availability',
        availabilityValue: '{from} - {to}',
        availabilityAll: 'Whole event',
        availabilityHint: 'Attendees can only propose times inside this window.',
        daysLabel: 'Open days',
        daysAll: 'All days',
        load: '≈ {used} / {total} slots taken',
        loadHint: 'Estimated load is capacity multiplied by available hours. The accept limit works per hour, so this ratio is only an indicator.',
        free: '≈ {count} free',
        loadTitle: 'Estimated occupancy',
        // Blok 16 — GERÇEK ölçü: kapasite kapısının baktığı büyüklük.
        peak: 'At its busiest {peak} / {capacity} meetings at once',
        peakEmpty: 'No accepted meetings overlap at the same time.',
        tagActive: 'Active',
        tagFull: 'Full',
        nameLabel: 'Area name',
        nameHint: 'Attendees will see this name. Use a floor, hall or table number.',
        namePlaceholder: 'Foyer table 4',
        draftTitle: 'New meeting area',
        editTitle: 'Edit meeting area',
        save: 'Save',
        cancel: 'Cancel',
        saving: 'Saving...',
        nameRequired: 'Area name is required.',
        hoursPairRequired: 'Enter the start and end time together.',
        hoursOrder: 'End time must be later than the start time.',
        deleteTitleOne: 'This area has 1 accepted meeting',
        deleteTitleMany: 'This area has {count} accepted meetings',
        deleteTitleEmpty: 'Delete {name}',
        deleteBody: 'If {name} is deleted these meetings lose their venue; their times are kept. You can move them to another area instead.',
        deleteBodyEmpty: 'No accepted meeting is assigned to this area. It will be removed from the list.',
        affectedTitle: 'Affected meetings',
        affectedPrivacyNote: 'Who meets whom is not shown to organizers; only the times are listed.',
        affectedMore: 'and {count} more meetings',
        affectedMore_one: 'and {count} more meeting',
        reassignLabel: 'Move the meetings',
        reassignNone: 'Do not move - keep the times only',
        reassignOption: '{name} · {capacity} concurrent',
        deleteConfirm: 'Delete',
        deleteConfirmMove: 'Move and delete',
        loadError: 'Could not load meeting areas.',
        retry: 'Try again',
        summaryUnknown: 'Area summary is unavailable right now',
        emptyBadge: 'No area defined',
        emptyTitle: 'Without an area, meetings have no venue',
        emptyBody: 'Every accepted meeting is assigned to an area. While this list is empty attendees can still propose times, but those meetings carry no venue.',
        emptyStep1: 'Name the area',
        emptyStep1Body: 'Pick a name attendees can find, such as Foyer table 3, Cafe area or Booth B12.',
        emptyStep2: 'Set the capacity',
        emptyStep2Body: 'How many meetings can run at once; the accept limit follows this number.',
        emptyStep3: 'Pick availability hours',
        emptyStep3Body: 'Attendees can only propose times inside this window.',
        emptyAction: 'Add the first area',
        previewLabel: 'A defined area looks like this',
        previewName: 'Foyer table 3',
        previewCapacity: '4 concurrent',
        previewHours: '09:00 - 18:00',
    },
    seniority: {
        student: 'Student',
        entry: 'Entry level',
        mid: 'Mid level',
        senior: 'Senior',
        lead: 'Team lead',
        manager: 'Manager',
        director: 'Director',
        executive: 'Executive',
        founder: 'Founder',
        freelance: 'Freelance',
    },
    sector: {
        software: 'Software',
        telecom: 'Telecom',
        finance: 'Finance',
        insurance: 'Insurance',
        healthcare: 'Healthcare',
        pharma: 'Pharma',
        education: 'Education',
        research: 'Research',
        manufacturing: 'Manufacturing',
        automotive: 'Automotive',
        construction: 'Construction',
        real_estate: 'Real estate',
        energy: 'Energy',
        agriculture: 'Agriculture',
        retail: 'Retail',
        ecommerce: 'E-commerce',
        logistics: 'Logistics',
        tourism: 'Tourism',
        food_beverage: 'Food and beverage',
        media: 'Media',
        marketing: 'Marketing',
        consulting: 'Consulting',
        legal: 'Legal',
        public_sector: 'Public sector',
        ngo: 'Nonprofit',
        arts: 'Arts',
        sports: 'Sports',
        other: 'Other',
    },
    organizations: {
        sectionTitle: 'My organizations',
        sectionLead: 'How you can publish an event depends on your organization’s verification status.',
        verificationCta: 'Verification',
        statusUnverified: 'Unverified',
        statusPending: 'Under review',
        statusVerified: 'Verified',
        statusRejected: 'Rejected',
        nameLabel: 'Organization name',
        nameHint: 'This is the name shown as the organizer on event pages.',
        emailLabel: 'Contact email (optional)',
        emailHint: 'An address on the organization’s domain speeds up verification.',
        websiteLabel: 'Website (optional)',
        descriptionLabel: 'Short description (optional)',
        descriptionHint: 'Shown on the organization page and in event details.',
        factWebsite: 'Web',
        factContact: 'Contact',
        factEvents: 'Events',
        createCta: 'Create organization',
        creating: 'Creating…',
        loadError: 'Your organizations could not be loaded.',
        // --- MOBILE ORGANIZATION SURFACE (verification turn, 2026-09-08) --------
        openCta: 'Open organization',
        emptyTitle: 'You do not have an organization yet',
        emptyBody: 'An event is always published on behalf of an organization. Create yours right away with the “+” in the top corner, or while opening an event. You run its verification from here.',
        emptyStepsTitle: 'Two steps to start',
        emptyStep1Title: 'Open your event',
        emptyStep1Body: 'Create your organization in the “Organizer” field; the event is published on its behalf.',
        emptyStep2Title: 'Get your organization verified',
        emptyStep2Body: 'A verified organization’s event also appears in discovery lists and recommendations.',
        emptyCta: 'Open an event',
        roleLine: 'Your role is {role}',
        verificationRowLabel: 'Verification',
        detailMissing: 'You cannot see this organization. Go back to the list and try again.',
        // --- ORGANIZATION CREATE SCREEN (M2-03 · reference "One form, two entries") ----
        // The standalone entry's band, title, body and button come from `orgCreate.*`.
        newCta: 'Create a new organization',
        createScreenTitle: 'New organization',
        createWizardBadge: 'Event setup paused',
        createWizardTitle: 'Create the organization that will host the event',
        createWizardBody: 'Every event is published under an organization. Create it now. The event details you entered are kept and you will continue where you left off.',
        createWizardCta: 'Create the organization and continue',
        createKeptTitle: 'Event details kept',
        createKeptEmpty: 'You have not filled in any event fields yet. Once the organization is created, setup reopens where you left off.',
        createKeptNote: 'Once the organization is created, setup continues from the step you left. No field has to be filled in again.',
    },
    organizerPage: {
        pastPageSubtitle: '{org} · {count} events',
        pastPageSubtitle_one: '{org} · {count} event',
        appBarTitle: 'Organizer',
        verified: 'Verified organization',
        logoAlt: '{org} logo',
        showMore: 'Show more',
        showLess: 'Show less',
        upcomingTitle: 'Upcoming events',
        accessPublic: 'Public',
        accessApproval: 'Application',
        registered: 'You’re registered',
        coverLabel: 'Event cover',
        noUpcoming: 'No events are planned right now. You can browse their past events.',
        emptyTitle: 'No events yet',
        emptyBody: 'You’ll see {org}’s first event here once it’s published.',
        pastTitle: 'Past events',
        seeAll: 'See all',
        errorTitle: 'Couldn’t load the page',
        errorBody: 'Check your connection and try again.',
        retry: 'Try again',
        notFoundTitle: 'This organization page wasn’t found',
        notFoundBody: 'The organization may have been removed or the link may have changed. There are other events you might like.',
        discover: 'Discover events',
        offline: 'You’re offline. Showing what you last saw.',
        loading: 'Loading',
        organizedBy: 'Organized by',
        organizedByLine: 'Organized by {org}',
        organizedByAria: 'Organized by {org}. Open organizer page',
        metaDescription: 'Events organized by {org}.',
        pastPageTitle: '{org} · Past events',
        backToOrganizer: 'Back to {org}',
        pastPrevious: 'Previous page',
        pastNext: 'Next page',
        pastPageStatus: 'Page {page} of {pages}',
        pastLoadMore: 'Show more',
        pastEmpty: 'No past events yet.',
        summaryOrganized: '{count} events organized',
        summaryOrganized_one: '{count} event organized',
        featuredLabel: 'Coming up next',
        pastYearCount: '{count} events',
        pastYearCount_one: '{count} event',
        cityLabel: 'City',
        cityHint: 'Optional. Shown on your organizer page. If you leave it empty, the line is hidden.',
        cityReadOnly: 'Only the organization owner can change the city.',
        // v3 §1.5 follow button and the small button on the "Organized by" row.
        followCta: 'Follow',
        followingState: 'Following',
        unfollowCta: 'Unfollow',
        followingShort: 'Following',
        followRowAria: 'Follow {org}',
        followHelp: 'We’ll let you know when they publish a new event.',
        unfollowedNotice: 'You unfollowed.',
        unfollowUndo: 'Undo',
        followError: 'Couldn’t follow right now. Try again.',
        unfollowError: 'Couldn’t unfollow right now. Try again.',
    },
    orgHome: {
        kicker: 'Organization',
        editCta: 'Edit',
        descriptionMissing: 'There is no description yet. Attendees read this on your event pages.',
        inviteCta: 'Invite a member',
        teamOpenCta: 'View the team',
        soloKicker: 'One-person organization',
        soloTitle: 'Event day is not run alone',
        soloBody: 'Checking people in at the door, watching registrations and updating the agenda can be different people. Splitting roles is safer than giving everyone full access.',
        soloCta: 'Invite your team',
        pendingTitle: 'Pending invitations',
        pendingEmpty: 'No pending invitations. Once you send one, you can follow it here.',
    },
    orgSettings: {
        pageTitle: 'Organization settings',
        subtitle: 'These settings apply to ALL of your events. An event that uploads its own brand overrides them.',
        navHeading: 'Organization',
        navBrand: 'Brand',
        navBrandDescription: 'Logo and colour',
        navVerification: 'Verification',
        navVerificationDescription: 'Organization identity',
        navTeam: 'Team',
        navTeamDescription: 'Members and roles',
        badgeVerified: '✓ Verified organization',
        badgeUnverified: 'Unverified organization',
        loadError: 'We could not load the organization.',
        openCta: 'Organization settings',
    },
    orgCreate: {
        badge: 'Settings · new organization',
        title: 'Create an organization',
        body: 'The same form event setup opens. You become the owner; from there you can invite your team and create events.',
        submitCta: 'Create the organization',
        afterTitle: 'After you create it',
        ownerNote: 'Whoever creates the organization becomes its owner. Ownership can be transferred later.',
        twoEntriesTitle: 'One form, two paths',
        twoEntriesBody: 'The fields are identical in both flows. Only where you start and where you return afterwards differ. If you came from event setup, setup resumes where you left off.',
    },
    orgTeam: {
        title: 'Members',
        ownershipTitle: 'Ownership and leaving',
        intro: 'Roles decide what each person can do in event management. The people who run check-in, follow registrations and edit the agenda can differ; separating roles is safer than giving everyone full access.',
        summaryMembers: '{count} members',
        summaryMembers_one: '{count} member',
        summaryPending: '{count} pending invitations',
        summaryPending_one: '{count} pending invitation',
        colMember: 'Member',
        colRole: 'Role',
        colPermission: 'Permissions',
        colActions: '',
        ownerLocked: '{role} · locked',
        ownerNote: 'Ownership can be transferred but not deleted; every organization has exactly one owner. Attendee data can be exported regardless of role and is never sold to sponsors.',
        youBadge: 'You',
        youBadgeSite: 'You',
        invitedBadge: 'Invitation pending',
        /** Expired invite (12.10.2026 decision): stays listed, not counted as pending. */
        expiredBadge: 'Expired',
        joinedAt: 'Joined on {date}',
        inviteExpiresAt: 'Invitation valid until {date}',
        inviteExpiredAt: 'Invitation expired on {date}',
        removeCta: 'Remove',
        removeTitle: 'Remove member',
        removeBody: '{name} will be removed from the organization and access ends immediately. The person can be invited again later.',
        cancelInviteCta: 'Cancel invitation',
        cancelInviteTitle: 'Cancel invitation',
        cancelInviteBody: 'The invitation sent to {name} will be cancelled and the link will stop working. They have not joined yet, so no access is being revoked. You can invite them again later.',
        roleChangeLabel: 'Role for {name}',
        /**
         * Accessible name of row buttons: visible text first (WCAG 2.5.3), then the
         * identity that tells rows apart. Organization accounts share a name (B-18).
         */
        removeCtaFor: 'Remove: {name}',
        cancelInviteCtaFor: 'Cancel invitation: {name}',
        inviteTitle: 'Invite a member',
        inviteIntro: 'If the invited person has no account they will sign up; otherwise they accept directly. Pick the role now; you can change it later.',
        inviteEmailLabel: 'Email address',
        inviteEmailPlaceholder: 'name@organization.com',
        inviteRoleLabel: 'Role',
        inviteDomainNote: 'Addresses outside your corporate domain are accepted, but they do not count towards verification.',
        inviteCta: 'Send invitation',
        inviteSent: 'Invitation sent. The link is valid for 14 days and can be used once.',
        inviteContentTitle: 'What the invitation email contains',
        inviteContentOrg: 'Organization name and verification status',
        inviteContentInviter: 'Name and role of the person inviting',
        inviteContentRole: 'The granted role and what it can do',
        inviteContentExpiry: 'A single-use link valid for 14 days',
        inviteEmailScopeNote: 'The invited address is only visible in this table; it never appears in attendee lists.',
        rolesTitle: 'What roles can do',
        rolePermOwner: 'Full access, organization settings and role management',
        rolePermAdmin: 'Manage events, registrations, invitations, team and brand',
        rolePermEditor: 'Edit events, manage registrations, run check-in and send invitations',
        rolePermViewer: 'View events and the team',
        roleDescOwner: 'Manages the organization, changes roles and can transfer ownership. Transferable but never empty.',
        roleDescAdmin: 'Creates events, manages registrations and invitations, runs check-in. Manages the team and the brand. Cannot change organization details or verification, cannot transfer ownership.',
        roleDescEditor: 'Edits events, manages registrations and applications, sends invitations, runs check-in at the door. Cannot publish or delete an event or change its access mode.',
        roleDescViewer: 'Sees events and the team, makes no changes.',
        transferTitle: 'Transfer ownership',
        transferIntro: 'Ownership moves to another active member. After the transfer you become an admin and may leave the organization.',
        transferSelectLabel: 'New owner',
        transferCta: 'Transfer ownership',
        transferConfirmTitle: 'Transfer ownership',
        transferConfirmBody: 'Ownership will move to {name}. You will drop to the admin role and cannot undo this. To get it back, the new owner would have to transfer it to you.',
        transferNoCandidate: 'There is no other active member to transfer to. Invite someone first and wait for them to accept.',
        transferDone: 'Ownership transferred. You are an admin now.',
        leaveTitle: 'Leave the organization',
        leaveIntro: 'When you leave, your access to the organization events ends. A user can belong to only one organization at a time; after leaving you can join another one by invitation.',
        leaveCta: 'Leave the organization',
        leaveConfirmTitle: 'Leave the organization',
        leaveConfirmBody: 'You will leave {organizationName} and access ends immediately. Rejoining requires a new invitation.',
        leaveBlockedOwner: 'The owner cannot leave. Transfer ownership to another active member first; after that you can leave.',
        leaveBlockedSoleOwner: 'The owner cannot leave and there is no other active member to transfer to. Invite a member, transfer ownership and then you can leave.',
        leftTitle: 'You left the organization',
        leftBody: 'You no longer have access to these organization settings.',
        leftCta: 'Back to my events',
        empty: 'You are the only person on the team. Invite a teammate to separate the roles.',
        emptyTitle: 'The team is a one-person team',
        loadError: 'The team list could not be loaded.',
        readOnlyNote: 'Your role does not allow team management; you can view the list.',
    },
    orgBrand: {
        title: 'Brand',
        logoLabel: 'Logo',
        logoHint: '{formats} · at least 256×256 px. Without a logo we use the organization initials.',
        logoPick: 'Choose a file',
        logoReplace: 'Replace the logo',
        logoRemove: 'Remove the logo',
        logoUploading: 'Uploading…',
        logoUploaded: 'Uploaded. It goes live when you save.',
        logoUploadError: 'We could not upload the logo.',
        colorLabel: 'Organization colour',
        colorHint: 'The colour is used on the badge top bar and the public page accents.',
        colorOption: 'Choose the colour {color}',
        colorNone: 'No colour chosen',
        colorClear: 'Remove the colour',
        contrastWarning: 'If the colour you pick does not meet AA contrast against text, the system automatically uses a darker shade. Accessibility comes before the brand.',
        contrastApplied: 'This shade does not meet AA contrast; a darker shade is used wherever text sits on it.',
        previewLabel: 'Badge preview',
        previewNote: 'Scanned at the door and during card exchange.',
        inheritTitle: 'Where it applies',
        inheritBody: 'The moment you save, every event without its own brand shows this value. No copy is stored; the event page reads the current brand on each visit.',
        save: 'Save the brand',
        saving: 'Saving…',
        saved: 'Brand saved. Event pages have been refreshed.',
        publishing: 'Brand saved. Event pages are refreshing…',
        saveError: 'We could not save the brand.',
        readOnlyTitle: 'Only the organization owner and admins can change this tab',
        readOnlyBody: 'The brand is the organization identity, so only owners and admins can change it. Your role is {role}. You can see the values but not save them.',
        emptyLogoAlt: '{name} logo',
        coverLabel: 'Cover image',
        coverIntro: 'Shown at the top of your organization page. Nothing is placed on top of it.',
        coverIntroSite: 'Shown at the top of your organization page. Nothing is placed on top of it.',
        coverLabelSite: 'Organization cover image',
        coverHelp: 'Recommended {recommended} px · at least {minimum} px · PNG, JPEG or WebP · up to {maxMb} MB',
        coverMobileArea: 'Visible on mobile',
        coverEmpty: 'No cover yet',
        coverPreviewAlt: 'Cover preview',
        coverUpload: 'Upload image',
        coverReplace: 'Replace',
        coverRemove: 'Remove',
        coverUploading: 'Uploading · {percent}%',
        coverProgressLabel: 'Uploading cover',
        coverCancel: 'Cancel',
        coverRetry: 'Try again',
        coverStillProcessing: 'The image is still being processed. Press Try again in a moment.',
        coverTooLarge: 'File is too large. The limit is {maxMb} MB.',
        coverWrongType: 'This file type isn’t supported. Upload a PNG, JPEG or WebP.',
        coverTooSmall: 'Image is too small. Use at least {minimum} px.',
        coverSaved: 'Cover saved. It’s live on your page.',
        coverRemoved: 'Cover removed. Your page shows no cover now.',
        coverReadOnly: 'Only owners and admins can change the cover. Your role is {role}.',
        coverReadOnlySite: 'Only owners and admins can change the cover. Your role is {role}.',
        coverStillProcessingSite: 'The image is still being processed. Press Try again in a moment.',
        coverTooLargeSite: 'File is too large. The limit is {maxMb} MB.',
        coverWrongTypeSite: 'This file type isn’t supported. Upload a PNG, JPEG or WebP.',
        coverTooSmallSite: 'Image is too small. Use at least {minimum} px.',
        coverSavedSite: 'Cover saved. It’s live on your page.',
        coverRemovedSite: 'Cover removed. Your page shows no cover now.',
    },
    orgVerification: {
        navLabel: 'Organization verification',
        kicker: 'Organization',
        pageTitle: 'Organization verification',
        // 15.10: while the application is closed by server setting, this replaces
        // the verification calls and leads to the organization request form.
        contactCta: 'Contact us about verification',
        badgeUnverified: 'Unverified organization',
        badgePending: 'Under review',
        badgeVerified: '✓ Verified organization',
        badgeRejected: 'Rejected',
        titleUnverified: 'Verify your organization',
        titlePending: 'Your application is with the platform team',
        titleVerified: '{name} is verified',
        titleRejected: 'Application not approved',
        bodyUnverified: 'Verification proves to attendees who is running the event. Your corporate email domain, your website and an authorisation document are enough.',
        bodyPending: 'Decisions take two business days on average. Meanwhile you can create and publish events; only the verified badge is not visible yet.',
        bodyVerified: 'The verified badge is shown on your event pages and organization profile. Update the documents if the domain or the authorised person changes.',
        bodyRejected: 'The reason is below. Complete what is missing and apply again; there is no waiting period.',
        ctaUnverified: 'Submit application',
        ctaRejected: 'Apply again',
        later: 'Later',
        limitsTitle: 'Limits while the organization is unverified',
        limitDiscoveryTitle: 'Not in discovery or search engines',
        limitDiscoveryBody: 'Your event does not appear in the discovery list, in suggested events or in search engine results.',
        limitBadgeTitle: 'No verified badge',
        limitBadgeBody: 'Event pages and the organization profile show “unverified organization”.',
        formTitle: 'Verification application',
        domainLabel: 'Corporate email domain',
        domainHint: 'Your account email must be on this domain.',
        domainMatched: '✓ Matches',
        domainMismatch: 'Does not match your account domain',
        domainPlaceholder: 'example.com',
        domainInvalid: 'Enter a valid domain (example.com).',
        websiteLabel: 'Website',
        websiteHint: 'Matching the domain speeds up the review.',
        documentLabel: 'Authorisation document',
        documentHint: 'Trade registry record, association statute or a signature authorisation statement. The document is used only for verification and is deleted {days} days after the decision.',
        documentChoose: 'Choose a PDF',
        documentReplace: 'Replace',
        documentUploading: 'Uploading…',
        documentRequired: 'The authorisation document is required.',
        documentTypeError: 'Only PDF files can be uploaded.',
        documentSizeError: 'The document can be at most {mb} MB.',
        documentUploadError: 'The document could not be uploaded. Try again.',
        documentBadge: 'PDF',
        documentUploaded: '{size} · uploaded',
        // Line shown before a file is picked; the retention note is written once, below.
        documentEmpty: 'PDF · at most {mb} MB',
        timelineTitle: 'Application status',
        timelineSubmitted: 'Application submitted',
        timelineSubmittedMeta: '{date} · domain and document sent',
        timelineReview: 'Review in progress',
        timelineReviewMeta: 'Two business days on average',
        timelineReviewDone: 'Review completed',
        timelineReviewDoneMeta: '{date} · approved',
        timelineBadge: 'Badge live',
        timelineBadgeMeta: 'Opens automatically after approval',
        timelineBadgeDoneMeta: 'Visible on all event pages',
        rejectTitle: 'Reason for rejection',
        badgePreviewTitle: 'Where the badge appears',
        previewVerifiedCaption: 'Public event page · organizer block',
        previewUnverifiedCaption: 'Unverified organization · same block',
        previewNote: 'A verified organization shows a green check badge. An unverified one raises no warning; it only carries a quiet grey label.',
        flowTitle: 'Status flow',
        flowUnverified: 'Unverified',
        flowPending: 'Under review · platform team',
        flowVerified: 'Verified · badge on',
        flowRejected: 'Rejected · with a reason, can be retried',
        flowNote: 'The platform team decides; two business days on average. A rejected application comes back with its reason and can be retried.',
        kvkkTitle: 'Data protection note',
        kvkkBody: 'Uploaded documents are processed only for verification, are never shared with third parties and are deleted {days} days after the decision. The organization owner can delete them earlier.',
        readOnlyNotice: 'Only the organization owner or an admin can submit the application.',
        loadError: 'The verification status could not be loaded.',
        retry: 'Try again',
        submitting: 'Submitting…',
        submitted: 'Your application has been received.',
        withdrawTitle: 'If you change your mind',
        withdrawBody: 'You can withdraw your application before a decision is made. The organization returns to unverified and you can fill in the form again whenever you want.',
        withdrawCta: 'Withdraw application',
        withdrawing: 'Withdrawing…',
        // A DECIDED application cannot be withdrawn; only the document is left.
        documentOnlyTitle: 'The authority document you uploaded',
        documentOnlyBody: 'Your application has been decided, so there is nothing left to withdraw. You can still delete your authority document before the retention period ends.',
        documentDeleteCta: 'Delete the document now',
        documentDeleting: 'Deleting…',
        documentDeleteHint: 'You do not have to wait for the retention period to end. An application awaiting a decision is reviewed without the document.',
        documentDeleteHintDecided: 'You do not have to wait for the retention period to end. A decided application is not affected.',
        documentDeleted: 'The document has been deleted.',
        actionError: 'The action could not be completed. Please try again.',
        requirementsTitle: 'Three things are needed for the application',
        documentWebOnly: 'The authorisation document is a PDF file and cannot be picked on a phone. You complete the application from the organization settings on the website, and keep following its status here.',
        webCta: 'Complete the application on the website',
        accountDomainLine: 'Your account domain is {domain}',
    },
    orgVerificationAdmin: {
        navLabel: 'Organization verification',
        title: 'Organization verification queue',
        lead: 'A person reviews every application and closes it with a reasoned decision.',
        segOpen: 'Pending',
        segDecided: 'Decided',
        colOrganization: 'Organization',
        colDomain: 'Domain',
        colSubmitted: 'Submitted',
        colWaiting: 'Waiting',
        colDocument: 'Document',
        colActions: 'Decision',
        waitingHours: '{count} h',
        documentView: 'Open document',
        documentDownload: 'Download document',
        documentPreparing: 'Preparing link…',
        documentMissing: 'No document',
        documentError: 'The document link could not be created.',
        approve: 'Verify',
        reject: 'Reject',
        approveTitle: 'Verify the organization',
        rejectTitle: 'Reject the application',
        approveBody: '{name} will be marked as verified and its badge will be turned on.',
        rejectBody: 'The reason is shown to the applicant verbatim. The organization can reapply without waiting.',
        reasonLabel: 'Reason',
        reasonHint: 'At least 10 characters; shown to the applicant verbatim.',
        reasonRequired: 'A reason is required for rejection.',
        confirm: 'Save decision',
        cancel: 'Cancel',
        deciding: 'Saving…',
        emptyTitle: 'No pending applications',
        emptyBody: 'New organization verification applications will be listed here.',
        emptyDecidedTitle: 'No decided applications',
        loadError: 'The queue could not be loaded.',
        decidedApproved: 'Verified',
        decidedRejected: 'Rejected',
        submittedBy: 'Submitted by: {name}',
        submittedByUnknown: 'Submitter unknown',
        headline: '{count} applications',
        headline_one: '{count} application',
        auditNote: 'The document is processed only for verification, is never shared with a third party and is deleted 90 days after the decision. Every document view is written to the audit log.',
    },
    settingsNav: {
        heading: 'Settings',
        ariaLabel: 'Settings sections',
        loadError: 'Couldn’t load. Check your connection and try again.',
    },
    settingsAppearance: {
        title: 'Appearance',
        themeSection: 'Theme',
        themeLight: 'Light',
        themeDark: 'Dark',
        themeHint: 'Until you choose, the light theme is applied. Once you choose, the preference is stored in this browser.',
        followingSystem: 'Your operating system preference is currently applied.',
        languageLabel: 'Interface language',
    },
    settingsPrivacy: {
        title: 'Privacy',
        listTitle: 'Attendee list',
        listBody: "When you register for an event, your name, title and profile photo appear on that event's attendee list. Only people registered for that event see the list and your contact details are never on it. There is no separate visibility setting. To leave the list, cancel your registration.",
        toggleOn: 'On',
        toggleOff: 'Off',
        changeFailed: 'The preference could not be saved. Please try again.',
        assuranceBand: 'Only people you exchange cards with through mutual approval see your card. The attendee list shows your name, title and photo, never your contact details.',
    },
    time: {
        now: 'just now',
        minutesAgo: '{count} min ago',
        minutesAgo_one: '{count} min ago',
        hoursAgo: '{count} hr ago',
        hoursAgo_one: '{count} hr ago',
        daysAgo: '{count} days ago',
        daysAgo_one: '{count} day ago',
        weeksAgo: '{count} weeks ago',
        weeksAgo_one: '{count} week ago',
        monthsAgo: '{count} months ago',
        monthsAgo_one: '{count} month ago',
        yearsAgo: '{count} years ago',
        yearsAgo_one: '{count} year ago',
        range: '{start}–{end}',
    },
    notifications: {
        title: 'Notifications',
        kicker: 'Your account',
        bellLabel: 'Notifications',
        bellLabelWithCount: 'Notifications, {count} unread',
        bellLabelWithCount_one: 'Notifications, {count} unread',
        unreadBadge: '{count} unread',
        unreadBadge_one: '{count} unread',
        unreadDot: 'Unread',
        menuTitle: 'Latest notifications',
        seeAll: 'See all',
        markAllRead: 'Mark all as read',
        loading: 'Loading notifications',
        error: 'Notifications could not be loaded.',
        retry: 'Try again',
        loadMore: 'Older notifications',
        olderGroup: 'Earlier',
        actorDeleted: 'Deleted account',
        organizationRemoved: 'Removed organization',
        emptyTitle: 'No notifications yet',
        emptyBody: 'Invitations, meeting requests, application results and event updates collect here. You have not missed anything.',
        emptyStepDiscover: 'Join an event',
        emptyStepDiscoverBody: 'Every change in an event you joined lands here.',
        emptyStepVisibility: 'Meet the attendees',
        emptyStepVisibilityBody: 'You appear on the attendee list of the event you register for and can receive meeting requests.',
        emptyStepPreferences: 'Set your channel preferences',
        emptyStepPreferencesBody: 'Email and push preferences live in settings; this list always fills.',
        emptyCta: 'Discover events',
        emptyPreviewLabel: 'This is how a notification row looks',
        item: {
            invitationTitle: 'Event invitation',
            invitationBody: '{actor} invited you to "{eventTitle}".',
            invitationAcceptedTitle: 'Your invitation was accepted',
            invitationAcceptedBody: '{actor} accepted your invitation to "{eventTitle}".',
            invitationDeclinedTitle: 'Your invitation was answered',
            invitationDeclinedBody: 'An invitation you sent for "{eventTitle}" was declined.',
            meetingRequestReceivedTitle: 'New meeting request',
            meetingRequestReceivedBody: '{actor} would like to meet you at "{eventTitle}".',
            meetingRequestAcceptedTitle: 'Your meeting request was accepted',
            meetingRequestAcceptedBody: '{actor} accepted your request at "{eventTitle}".',
            meetingRequestDeclinedTitle: 'Your meeting request was answered',
            meetingRequestDeclinedBody: 'Your request at "{eventTitle}" is not happening this time.',
            meetingRequestCancelledTitle: 'Meeting cancelled',
            meetingRequestCancelledBody: '{actor} cancelled the meeting at "{eventTitle}".',
            meetingAreaAssignedTitle: 'Your meeting has a spot',
            meetingAreaAssignedBody: 'The spot for your meeting with {actor} at "{eventTitle}" is on your agenda.',
            applicationApprovedTitle: 'Your application was approved',
            applicationApprovedBody: 'Your registration for "{eventTitle}" is complete.',
            applicationRejectedTitle: 'Your application was decided',
            applicationRejectedBody: 'You cannot join "{eventTitle}" this time.',
            cardExchangeOfferedTitle: 'Card exchange offer',
            cardExchangeOfferedBody: '{actor} wants to share a card. The exchange completes with mutual approval.',
            cardExchangeConfirmedTitle: 'Card exchange completed',
            cardExchangeConfirmedBody: '{actor} is now in your wallet.',
            eventUpdatedTitle: 'Event updated',
            eventUpdatedBody: 'Something changed in "{eventTitle}".',
            eventCancelledTitle: 'Event cancelled',
            eventCancelledBody: '"{eventTitle}" was cancelled. It stays in your history.',
            followUpReminderTitle: 'Follow-up reminder',
            followUpReminderBody: 'You planned to reach out to {actor}.',
            recommendationTitle: 'An event for you',
            recommendationBody: '"{eventTitle}" matches your interests.',
            organizationEventPublishedTitle: 'New event from an organization you follow',
            organizationEventPublishedBody: '{actor} published a new event: {eventTitle}',
            organizerAnnouncementTitle: '{subject}',
            organizerAnnouncementBody: '{actor} · {eventTitle}',
            postEventTitle: 'Thanks for attending',
            postEventCertificateAndSurveyBody: '"{eventTitle}" has ended. Your certificate and a short survey are waiting.',
            postEventCertificateBody: '"{eventTitle}" has ended. Your certificate is ready.',
            postEventSurveyBody: '"{eventTitle}" has ended. Share your thoughts in a short survey.',
            eventReminderTitle: 'Event reminder',
            eventReminderPeopleBody: '"{eventTitle}" is coming up. See who you should meet.',
            eventReminderBody: '"{eventTitle}" is coming up. Time and place are on the event page.',
        },
        announcement: {
            pageTitle: 'Announcement',
            back: 'Back to notifications',
            meta: '{date} · {kind}',
            viewEvent: 'View event',
            muteOrganization: 'Stop announcements from this organization',
            muting: 'Turning off…',
            mutedNote: 'Announcements from {orgName} will no longer reach you.',
            unmute: 'Turn back on',
            unmuting: 'Turning on…',
            actionFailed: 'That did not go through. Try again.',
            loading: 'Loading announcement',
            loadErrorTitle: 'The announcement could not be loaded',
            loadErrorBody: 'Check your connection and try again.',
            retry: 'Try again',
            notFoundTitle: 'This announcement was not found',
            notFoundBody: 'It may have been removed, or this link does not belong to your account.',
            organizationRemoved: 'Removed organization',
        },
        unsubscribe: {
            pageTitle: 'Announcement preference',
            processing: 'Checking the link',
            appliedTitle: 'You will no longer get announcements from this organization',
            appliedBody: 'Announcements from {orgName} will no longer reach you. You can turn them back on in Settings › Notifications.',
            undo: 'Undo',
            undoing: 'Undoing…',
            undoneTitle: 'Announcements are back on',
            undoneBody: 'Announcements from {orgName} will reach you again.',
            browseEvents: 'Browse events',
            invalidTitle: 'This link is no longer valid',
            invalidBody: 'To turn announcements off, sign in and go to Settings › Notifications.',
            signIn: 'Sign in',
            failedTitle: 'That did not go through',
            failedBody: 'Check your connection and try again.',
            retry: 'Try again',
            undoUnavailable: 'This can no longer be undone from this link. To turn announcements back on, sign in and open your settings.',
            undoFailed: 'Undo did not go through. Check your connection and try again.',
            settingsLink: 'Settings › Notifications',
            mandatoryNote: 'Essential updates, such as a change to the event time or venue, still reach you for events you are registered for.',
        },
    },
    settingsNotifications: {
        title: 'Notifications',
        channelEmail: 'Email',
        channelPush: 'Push',
        lockedHint: 'Notifications your account needs to work (invitations, application result emails, meeting requests) cannot be turned off. To stop receiving meeting requests entirely, use the switch in your privacy settings.',
        lockedHintOrganization: 'An organization account does not attend events as a participant, so application result, meeting request and card exchange notifications do not apply to it. Acceptances and declines of the invitations you send always appear in the app, and emails your account needs to work, such as verification codes, are always sent.',
        lockedHintOrganizationSite: 'An organization account does not attend events as a participant, so application result, meeting request and card exchange notifications do not apply to it. Acceptances and declines of the invitations you send always appear in the app, and emails your account needs to work, such as verification codes, are always sent.',
        lockedRowHint: 'Cannot be turned off',
        colType: 'Notification type',
        emptyTitle: 'Notification settings could not be loaded',
        emptyBody: 'All notifications use their default setting.',
        typeEventUpdate: 'Event updates',
        typeInvitation: 'Invitations',
        typeApplicationResult: 'Application results',
        typeProductNews: 'Product news',
        typeMeetingRequest: 'Meeting requests',
        typeCardExchange: 'Card exchange',
        typeFollowUpReminder: 'Follow-up reminders',
        typeRecommendation: 'Event recommendations',
        typeFollowedOrganizationEvent: 'New events from organizations you follow',
        typeOrganizerAnnouncement: 'Organizer announcements',
        typeEventReminder: 'Event reminders',
        followedOrganizationsHint: 'We’ll notify you when an organization you follow publishes a new event.',
        followedOrganizationsOn: 'On. You get it in the app and as a phone notification.',
        followedOrganizationsOff: 'Off. You keep following these organizations, but new event notifications won’t arrive.',
        saved: 'Your preferences were saved.',
        saving: 'Saving…',
        saveFailed: 'The preference could not be saved. Please try again.',
        organizerAnnouncementHint: 'Updates from the organizers of events you are registered for. No promotions.',
        eventReminderHint: 'A reminder the day before an event you registered for, and your certificate and survey link after an event you attended.',
        mutedOrganizationsTitle: 'Muted organizations',
        mutedOrganizationsHint: 'Announcements from these organizations reach you on no channel.',
        mutedOrganizationsError: 'Muted organizations could not be loaded.',
        mutedOrganizationsRetry: 'Try again',
        reopen: 'Turn back on',
        reopening: 'Turning on…',
        reopenAria: 'Turn announcements from {orgName} back on',
        reopenFailed: 'Announcements from this organization could not be turned back on. Try again.',
    },
    settingsData: {
        title: 'Your data and account',
        otpLabel: 'Verification code sent to your email',
        otpSend: 'Send code',
        otpSent: 'The code was sent to your email.',
        otpSentTo: 'We sent the code to {email}.',
        otpSendFirst: 'Press Send code first. Then enter the 6-digit code from your email here.',
        exportTitle: 'My personal data copy',
        exportHint: 'The legal copy of all personal data linked to your account. The ZIP file holds one spreadsheet per section that opens in Excel, a short summary and machine-readable JSON. Your data is never held hostage.',
        orgExportHint: 'The legal copy of your own account data. The ZIP file holds your profile, organization memberships and consent records as one spreadsheet per section, with a short summary and machine-readable JSON. The organization’s attendee data isn’t in this file.',
        walletExportPointer: 'To move the people you met to your phone contacts or Excel, use Export my contacts on the Wallet screen.',
        attendeeExportPointer: 'For an event you organize, the attendee list comes from Download attendee list on Event management › Registrations.',
        exportCta: 'Create export request',
        exportStatusQueued: 'Queued',
        exportStatusProcessing: 'Preparing…',
        exportStatusReady: 'Ready',
        exportStatusFailed: 'Failed',
        exportStatusExpired: 'Expired',
        exportExpiresAt: 'Link valid until {time}',
        downloadJson: 'Download JSON',
        downloadZip: 'Download data copy (ZIP)',
        downloadVcard: 'Download vCard',
        downloadOnceHint: 'Each format can be downloaded once; the link is time-limited.',
        exportsEmptyTitle: 'No requests',
        exportsEmptyBody: 'Export requests you create appear here.',
        walletSectionTitle: 'Wallet and registrations',
        walletCta: 'Go to wallet',
        deletionTitle: 'Delete account',
        deletionHint: 'Your account and personal data are permanently deleted. You can sign in and cancel within 14 days. Consent and audit records are kept in de-identified form as legally required; cards you shared with mutual approval may remain with the other party.',
        orgDeletionHint: 'Your account and personal data are deleted permanently. You can sign in within 14 days to cancel. Consent and audit records are kept de-identified as the law requires. Records of actions you took for the organization stay with the organization, with your name removed.',
        deletionDownloadFirst: 'We recommend downloading your data before deleting.',
        deletionCta: 'Delete my account',
        deletionPending: 'Your account will be permanently deleted on {date}.',
        deletionCancelCta: 'Cancel deletion request',
        deletionCancelled: 'Your deletion request was cancelled.',
        deletionBlockedSoleOwner: 'An organization you solely own has active events. Transfer ownership or cancel the event first.',
        rateLimitedHours: 'Too many attempts. You can try again within {count} hours.',
        rateLimitedHours_one: 'Too many attempts. You can try again within {count} hour.',
        resendWait: 'You can request a new code in {count} seconds.',
        resendWait_one: 'You can request a new code in {count} second.',
    },
    accountDeletion: {
        title: 'Delete my account',
        deleting: 'Deleting…',
        warnTitle: 'This cannot be undone after {count} days.',
        warnTitle_one: 'This cannot be undone after {count} day.',
        warnBody: 'Once you send the request your account is suspended right away. You can sign in and take the request back within {count} days.',
        warnBody_one: 'Once you send the request your account is suspended right away. You can sign in and take the request back within {count} day.',
        deletedTitle: 'Deleted',
        deletedItem1: 'Your name, email and password',
        deletedItem2: 'The phone, title, company, education and social addresses on your cards',
        deletedItem3: 'Your contact notes and meeting messages',
        deletedItem4: 'The cards in your wallet',
        anonymizedTitle: 'Anonymised',
        anonymizedItem1: 'Your event attendance counts',
        anonymizedItem2: 'Your encounter statistics',
        keptTitle: 'Kept',
        keptItem1: 'Your consent records. KVKK requires proof, so they are kept without identity.',
        keptItem2: 'Your security logs. Turkish law 5651 requires two years of retention.',
        exportTitle: 'Download your data first',
        exportBody: 'Your cards and encounters come out as CSV and vCard.',
        exportCta: 'Download',
        ackLabel: 'I understand my data will be erased for good after {count} days.',
        ackLabel_one: 'I understand my data will be erased for good after {count} day.',
        confirmSectionTitle: 'Confirm the deletion',
        otpIntro: 'Enter the code sent to your email to delete your account. That way a stolen session cannot delete it.',
        pendingTitle: 'Your deletion request is waiting',
        pendingBody: 'Your account will be erased for good on {date}. You can take the request back until then.',
        pendingRestore: 'When you take the request back your account returns as it was.',
        openFromSettings: 'Delete my account',
        openHint: 'Read what gets deleted, what gets anonymised and what stays, then decide.',
    },
    registerConsent: {
        step: 'Approvals',
        title: 'Your approvals',
        lead: 'We do not collect everything in one box. Required items and optional items stay apart and are recorded apart.',
        requiredGroup: 'Required to continue',
        optionalGroup: 'Optional, your account opens even if you decline',
        note: 'Your approval is recorded with a version number and a timestamp. You can change all of them later from My consents under Settings.',
        cta: 'Create the account',
        requiredMissing: 'Tick the required approvals to continue.',
        progress: '{step}/{total}',
        back: 'Fix my details',
    },
    publicEvent: {
        inviteOnlyTitle: 'This event is invite-only',
        inviteOnlyBody: 'Attendance is possible only with an organizer invitation. If you received an invite, use the link in your email; otherwise you can contact the organizer.',
        attendeeCount: '{count} people attending',
        attendeeCount_one: '{count} person attending',
        attendeesVisitorLocked: 'Sign up to see who is coming.',
        attendeesTitle: 'Attendees',
        heroAgendaCta: 'See the program',
        metaDateLabel: 'Date',
        metaVenueLabel: 'Venue',
        metaTicketsLabel: 'Tickets',
        metaOnlineValue: 'Online',
        ticketSummaryCount: '{count} ticket types',
        ticketSummaryCount_one: '{count} ticket type',
        ticketSummaryOpen: 'Registration open',
        aboutTitle: 'Event details',
        aboutWebsite: 'Website',
        similarTitle: 'Similar events',
        similarBody: 'Recommendations here get personal as your attendance history grows.',
        similarDiscoverCta: 'Discover events',
        sponsorsTitle: 'Sponsors',
        sponsorsEmpty: 'Sponsors for this event have not been announced yet.',
        sponsorsPrivacyNote: 'Attendee data is never shared with sponsors.',
        attendeesListNote: 'Everyone who registers for the event is on the list and only registered attendees can see it. Contact details are not on the list.',
        limitedNoindexNote: 'This page is closed to search engines (noindex) and never appears in event discovery lists.',
        limitedBasicsNote: 'Only the event name, date and organizer are shown. The attendee count is withheld as well.',
        approvalListNote: 'The attendee list opens after your application is approved. The page stays open to search engines.',
        invitedListNote: 'The attendee list opens once you accept the invitation.',
        approvalDataNote: 'Your profile details and an optional short note are sent with the application. Your business card is not sent; if you are rejected the application data is deleted after {days} days.',
        locationDirectionsTitle: 'Getting there',
        locationOnlineNote: 'This event runs online.',
        // G2: the link is shown only to confirmed attendees and the organizer team.
        onlineLinkLabel: 'Join link',
        onlineLinkLocked: 'The join link appears here once your registration is confirmed.',
        onlineLinkOpen: 'Open link',
        locationAddressMissing: 'The organizer has not entered a street address yet. It shows up here once added.',
        speakersFootnote: 'Opening a speaker card takes you to their page; contact details are never shown and card exchange always needs mutual consent.',
        limitedHiddenTitle: 'Not shown on this page',
        limitedHiddenIntro: 'These sections open only to invited attendees:',
        tabGeneral: 'Overview',
        sectionNavLabel: 'Page sections',
        descriptionTitle: 'About this event',
        dateTitle: 'Date & time',
        venueTitle: 'Location',
        onlineTitle: 'Online event',
        organizerTitle: 'Organizer',
        unverifiedBadge: 'Unverified',
        founderCredit: 'Added to the platform by: {name}',
        manageBarKicker: 'You manage this event',
        manageBarBody: 'You have management access. Handle applications, invitations, attendees, speakers and the agenda from the panel.',
        manageBarCta: 'Management panel',
        remainingQuota: 'Only {count} spots left',
        remainingQuota_one: 'Only {count} spot left',
        unlimitedQuota: 'Unlimited capacity',
        soldOut: 'Sold out',
        cancelledNotice: 'This event has been cancelled.',
        endedKicker: 'Event has ended',
        endedTicketsValue: 'Registration closed',
        /* Block 17B (finding D class): an ended event accepts no new registrations. */
        endedNotice: 'This event has ended. Registration is closed; if you attended, it stays in your history.',
        /* QA 17B+17E P2 class sweep: never say "none" when a section failed to load. */
        sectionUnavailable: 'This section could not be loaded right now. Refresh and try again.',
        registerCta: 'Register',
        applyCta: 'Apply',
        signInToContinue: 'Sign in to continue',
        manageCta: 'Manage participation',
        posterTitle: 'Stop installing a separate app for every event.',
        posterBody: 'Register to see who is coming, set up meetings in advance, and keep the people you meet.',
        registerCardApply: 'Application',
        registerCardRegister: 'Registration',
        registerCardRegistered: 'Your registration',
        registerCardEnded: 'Ticket',
        endedClosedBody: 'The event ended on {date}.',
        similarCta: 'See similar events',
    },
    participation: {
        statusRegistered: 'You are registered for this event',
        statusPendingApproval: 'Your application is awaiting organizer approval',
        statusRejected: 'Your application was not approved',
        statusCancelled: 'You cancelled your registration',
        statusWaitlisted: 'You are on the waitlist',
        statusAttended: 'You attended this event',
        outcomeEncounters: 'Met {count} people',
        outcomeEncounters_one: 'Met {count} person',
        outcomeMeetings: '{count} meetings',
        outcomeMeetings_one: '{count} meeting',
        outcomeNoCheckIn: 'No check-in',
        outcomeUnderReview: 'Application under review',
        invitationValidDays: 'Valid for {count} days',
        invitationValidDays_one: 'Valid for {count} day',
        invitationValidToday: 'Expires today',
        rowBadgeRegistered: 'Confirmed',
        rowBadgePending: 'Pending',
        rowBadgeWaitlisted: 'Waitlisted',
        rowBadgeCancelled: 'Cancelled',
        badgeAttended: 'Attended',
        badgeMissed: 'Did not attend',
        // --- Manual test 2 - finding 3: the action button reads the viewer's state ---
        organizerNoticeTitle: 'You manage this event',
        organizerNoticeBody: 'Organizers do not apply to their own event. Registrations, applications and the programme are handled from the management panel.',
        organizerNoticeCta: 'Go to the management panel',
        ticketLabel: 'Ticket type',
        applicationNoteLabel: 'Application note (optional, max {limit} characters)',
        registerSuccess: 'Your registration is complete.',
        applySuccess: 'Your application was received. The result will arrive by email.',
        cancelCta: 'Withdraw from event',
        cancelConfirm: 'Your registration will be cancelled and the spot freed. Are you sure?',
        cancelConfirmCta: 'Cancel registration',
        cancelling: 'Withdrawing…',
        cancelSuccess: 'Your registration was cancelled.',
        inviteOnlyRejoinHint: 'Rejoining this event requires a new invitation from the organizer.',
        // --- Screen G6: application result (rejected) ---
        rejectedKicker: 'Your application was not approved',
        rejectedTitle: 'You could not join this event',
        rejectedBody: 'The organizer did not approve, due to capacity or attendee profile. No reason is shared.',
        rejectedRetention: 'Your profile and note stay with the organizer for {days} days, then they are deleted. Your card was never shared.',
        alternativesTitle: 'Similar events',
        alternativesEmpty: 'No similar event right now. Browse everything from discovery.',
        alternativesCta: 'Discover events',
        applyProfileTitle: 'What the organizer will receive',
        applyProfileNote: 'These details from your profile are sent to the organizer. Your business card is not sent.',
        applyAgendaLabel: 'Add to my agenda if I am approved',
        applyAgendaState: 'Always on',
        applyAgendaLocked: 'This cannot be turned off. If your application is approved a registration is created and the event enters your agenda.',
        pendingKicker: 'Application submitted',
        pendingTitle: 'Waiting for the organizer',
        pendingBody: 'The organizer reviews applications one by one. You will hear back by notification and email.',
        pendingBandLabel: 'Pending',
        pendingStepSubmitted: 'Application sent',
        pendingStepReview: 'Under review',
        pendingStepReviewBody: 'The organizer is reading your application.',
        pendingStepRegistered: 'Registration is created',
        pendingStepRegisteredBody: 'If approved, your ticket and QR code open up.',
        pendingLimitNote: 'While your application is pending the attendee list and agenda stay closed; only the general information of the event is visible.',
        approvedKicker: 'Application approved',
        /* KÇ #2 · Y-3: poster states the REGISTRATION fact, not list visibility. */
        approvedPoster: 'You are registered',
        approvedTicketNote: 'Your ticket and QR code are ready; the agenda and attendee list are open.',
    },
    // --- Screen 28b: badge / ticket QR code (check-in input) ---
    badge: {
        openCta: 'Show my ticket QR',
        title: 'Your badge',
        kicker: 'Scanned at the door',
        hint: 'Show this to staff at the door. The code works for this event only.',
        ticketLabel: 'Ticket',
        brightnessHint: 'Turning up screen brightness makes scanning faster.',
        offlineNote: 'The code is saved on your device; it shows even without internet at the door.',
        cachedNote: 'You are offline; showing your saved code.',
        checkedInLabel: 'Checked in',
        checkedInAt: '{time}',
        loading: 'Preparing your badge',
        errorTitle: 'Badge could not be opened',
        errorBody: 'The code could not be fetched. Check your connection and retry; staff can also find you by name.',
        retryCta: 'Try again',
        screenTitle: 'Badge',
        dialogTitle: 'Your ticket',
        eventLabel: 'Event',
    },
    invitations: {
        pageTitle: 'Event invitation',
        invitedBy: '{name} invited you',
        expiresAt: 'Invitation valid until {time}',
        acceptCta: 'Accept invitation',
        acceptFull: 'This event is full and isn’t taking registrations. Your invitation stays valid, so you can try again if a spot opens up.',
        declineCta: 'Decline',
        acceptedNotice: 'You accepted the invitation. See you at the event!',
        declinedNotice: 'You declined the invitation. No further reminders will be sent.',
        wrongAccountTitle: 'This invitation was sent to a different email address',
        wrongAccountBody: 'To accept, you must sign in with the email address the invitation was sent to. Invitations are non-transferable.',
        switchAccountCta: 'Sign in with the correct account',
        invalidTitle: 'Invitation is not valid',
        invalidBody: 'The link may have expired, been revoked, or already been used. You can contact the organizer.',
        // --- Block 8-2 (G3): each reason for an unusable invitation gets its own screen ---
        unusableKicker: 'Invitation unusable',
        expiredTitle: 'This invitation has expired',
        expiredBody: 'Invitation links are time limited and this one has expired. The event may still take place; the organizer can send a new invitation.',
        expiredScopeNote: 'Because the link is invalid, the event agenda, speakers and attendee list are not shown.',
        revokedTitle: 'This invitation was revoked',
        revokedBody: 'The organizer withdrew this invitation. You can ask for a new one.',
        alreadyUsedTitle: 'This invitation was already used',
        alreadyUsedBody: 'Once accepted or declined, a link cannot be opened again. You can find your registration under your events.',
        requestNewTitle: 'How to ask for a new invitation',
        requestNewStep1: 'Write to the person who invited you',
        requestNewStep1Body: 'Whoever sent you the link can issue a new invitation.',
        requestNewStep2: 'Ask for the same email address',
        requestNewStep2Body: 'Invitations are non-transferable; the new one must go to the same address.',
        requestNewStep3: 'Browse other events',
        requestNewStep3Body: 'Public events can be joined without an invitation.',
        discoverCta: 'Discover events',
        myEventsCta: 'Go to my events',
        signInToAcceptCta: 'Sign in to accept',
        createAccountToAcceptCta: 'Create an account to accept',
        haveAccountSignInCta: 'Already have an account? Sign in',
        goToEventCta: 'Go to the event',
        respondCta: 'Respond to invitation',
        visibilitySeparateNote: 'An invitation alone does not put you on the attendee list. Once you accept and register, your name appears on the list, which only registered attendees can see.',
        // B-15/B-16 merge: note AFTER acceptance. Accepting creates the registration.
        acceptedListNote: 'Your registration is created and you appear on the attendee list. Only registered attendees can see the list. To leave the list, cancel your registration.',
        // --- Blok 8-3 (G3): event brief + "request a new invitation" ---
        briefTitle: 'The event you were invited to',
        briefOrganizer: 'Organizer: {name}',
        briefExpired: 'The invitation expired on {time}',
        requestNewCta: 'Request a new invitation',
        requestNewSuccess: 'Your request reached {name}. A new invitation will be sent to the same email address.',
        requestNewAlready: 'You already requested a new invitation. Wait for the organizer to respond.',
        requestNewUnavailable: 'A new invitation cannot be requested for this link.',
        acceptEffectsTitle: 'If you accept',
        acceptEffect1: 'The agenda, speakers and attendee list of the event open up.',
        acceptEffect2: 'Your QR ticket is created; you scan it at the door.',
        acceptEffect3: 'Accepting creates your registration and you appear on the attendee list. Only registered attendees see the list.',
        acceptedKicker: 'You are registered',
        acceptedPoster: 'You have your place on the list',
        acceptedTicketNote: 'Scan this at the door. The event agenda and attendee list are open now.',
        acceptedWhoIsComingCta: 'See who is coming',
        requestNewPrivacyNote: 'Your request goes to the organizer; your email address is shared, your business card is not.',
    },
    // --- Team invitation (K5): joining an organization team ---
    teamInvitations: {
        pageTitle: 'Team invitation',
        heading: 'You were invited to the {organizationName} team',
        invitedBy: '{name} invited you',
        roleLine: 'Your role: {role}',
        expiresAt: 'This invitation is valid until {time}',
        acceptCta: 'Accept invitation',
        declineCta: 'Decline',
        signInToAcceptCta: 'Sign in to accept',
        acceptedNotice: 'You joined the team. You can now manage this organization’s events.',
        declinedNotice: 'You declined the invitation. You will not have access to the organization.',
        scopeNote: 'If you accept, you only manage this organization’s events within the limits of your role; your personal profile and wallet are unaffected.',
        wrongAccountTitle: 'This invitation was sent to a different email address',
        wrongAccountBody: 'To accept, sign in with the email address the invitation was sent to. Team invitations cannot be transferred.',
        switchAccountCta: 'Sign in with the right account',
        unusableKicker: 'Invitation unavailable',
        invalidTitle: 'This invitation is not valid',
        invalidBody: 'The link may have expired, been revoked, or already been used. Contact the person who invited you.',
        expiredTitle: 'This invitation has expired',
        expiredBody: 'Team invitation links are time limited and this one expired. An organization admin can send a new one.',
        roleOwner: 'Owner',
        roleAdmin: 'Admin',
        roleEditor: 'Editor',
        roleViewer: 'Viewer',
        mobileTitle: 'Team invitation',
        validBadge: 'Team invitation · valid until {time}',
        landingTitle: 'You are invited to join the team',
        landingInvitedBy: '{name} invited you to the {organizationName} team as {role}.',
        landingInvitedByOrganization: '{organizationName} invited you to its team as {role}.',
        landingRoleBox: 'As {role}',
        landingValidity: 'Valid until {date}, single use.',
        errorExpiredTitle: 'This invitation has expired',
        errorExpiredBody: 'Invitation links are valid for {count} days. Ask the person who invited you for a new one.',
        errorExpiredBody_one: 'Invitation links are valid for {count} day. Ask the person who invited you for a new one.',
        errorRevokedTitle: 'This invitation was withdrawn',
        errorRevokedBody: 'The organization withdrew this invitation. If you think this is a mistake, write to your organization admin.',
        errorInvalidTitle: 'Invitation not found',
        errorInvalidBody: 'The link may be incomplete or already used. Open the link again from the invitation email.',
        errorLoadFailed: 'The invitation could not be loaded. Check your connection and try again.',
        tokenLabel: 'The code in the invitation link',
        tokenHint: 'Paste the code at the end of the link in your email. Team invitations are not transferable.',
        lookupCta: 'Fetch invitation',
        rowInvitedBy: 'Invited by',
        rowRole: 'Assigned role',
        rowValidity: 'Valid until',
        capabilitiesTitle: 'What the {role} role can do',
        capabilityEvents: 'Creates and edits events',
        capabilityAttendees: 'Manages registrations, invitations and applications',
        capabilityOrganization: 'Manages the organization profile and billing',
        capabilityGranted: 'Allowed',
        capabilityDenied: 'Not allowed',
        organizerVerified: 'Verified organization',
        acceptedCta: 'Go to my events',
    },
    orgAccount: {
        tag: 'Organization account',
        tagWithOrganization: 'Organization account · {organizationName}',
        navHome: 'Organization home',
        navEvents: 'Events',
        navOrganizationSettings: 'Organization settings',
        yourOrganizations: 'Your organizations',
        onlyOrganizations: 'An organization account switches only between organizations.',
        switcherLabel: 'Organization: {organizationName}',
        verified: 'Verified',
        selected: 'Selected',
        signInPersonal: 'Sign in with your personal account',
        signInPersonalNote: 'This session ends and the sign-in page opens.',
        accountSettings: 'Account settings',
        signOut: 'Sign out',
        loadingNav: 'Navigation is loading',
        accessPublic: 'Public',
        accessApproval: 'Application',
        accessInviteOnly: 'Invite only',
        eventTime: '{start}–{end} · {venue}',
        eventTimeNoVenue: '{start}–{end}',
        today: 'Today',
        noToday: 'No events today.',
        noTodayNext: 'No events today. Next up is {title} on {date}.',
        createEventCta: 'Create event',
        manageEventFor: 'Manage: {title}',
        registeredCount: '{count} registered',
        registeredCount_one: '{count} registered',
        followers: 'Followers',
        followerUnit: 'followers',
        followerUnit_one: 'follower',
        followersHint: 'We let them know when you publish a new event.',
        followersEmpty: 'You have no followers right now. Share your event pages and people will start following.',
        followersHintSite: 'We let them know when you publish a new event.',
        followersEmptySite: 'No followers yet. When you publish an event, people who follow your organization page appear here.',
        followersLoadError: 'Couldn’t load your follower count right now.',
        checkedInCount: '{count} check-ins',
        checkedInCount_one: '{count} check-in',
        upcoming: 'Upcoming events',
        allEvents: 'All',
        upcomingEmpty: 'No upcoming events.',
        drafts: 'Drafts',
        editedAgo: 'Last edited {time}',
        edit: 'Edit',
        pendingApplications: 'Pending applications',
        pendingApplicationCount: '{count} pending applications',
        pendingApplicationCount_one: '{count} pending application',
        review: 'Review',
        team: 'Team',
        manageTeam: 'Manage team',
        newTitle: 'No events yet',
        newBody: 'Once you create your first event, upcoming events, drafts and applications show up here.',
        newCta: 'Create your first event',
        pastOnlyTitle: 'You have no upcoming events',
        pastOnlyBody: 'Once you create a new event, upcoming events, drafts and applications show up here.',
        pastOnlyCta: 'Create a new event',
        pastOnlyTitleSite: 'You have no upcoming events',
        pastOnlyBodySite: 'Once you create a new event, upcoming events, drafts and applications show up here.',
        pastEventsLink: 'Past events ({count})',
        /* TW2 ve TW7 hata hâllerinin tek düğme metni (referans `f.L.retry`). */
        retry: 'Try again',
        eventsLoadError: 'Couldn’t load events.',
        teamLoadError: 'Couldn’t load the team.',
        organizationLoadError: 'Couldn’t load the organization.',
        orphanTitle: 'You’re not on an organization’s team right now',
        orphanBody: 'This organization account is for organization work only. You can use it again when an organization invites you. Use your personal account to attend events.',
        pendingInvitations: 'Pending team invites',
        accept: 'Accept',
        downloadData: 'Download my data',
        deleteAccount: 'Delete account',
        noConvert: 'This account doesn’t turn into a personal account.',
        invitationsLoadError: 'Couldn’t load invites.',
        gateTitle: 'This page is for personal accounts',
        gateWanted: 'Page you wanted to open:',
        gateBody: 'Meeting people, business cards and attending events happen with your personal account. You’re in the {organizationName} organization account right now.',
        gateBodyNoOrganization: 'Meeting people, business cards and attending events happen with your personal account. You’re in an organization account right now.',
        backHome: 'Back to organization home',
        gateAfter: '{page} opens after you sign in.',
        pageWallet: 'Wallet',
        pageCardExchange: 'Card exchange',
        pageMeetingRequests: 'Meeting requests',
        pageAgenda: 'Agenda',
        pageDiscover: 'Discover events',
        pageAttendees: 'Attendee list',
        pageProfile: 'Business card profile',
        pageParticipation: 'My events',
        pageVisibility: 'Privacy settings',
        pageSpeakerLinks: 'Speaker links',
        pageOnboarding: 'Profile setup',
        pageCardLink: 'Business card link',
        pageSurvey: 'Event survey',
        blockRegion: 'Registration',
        blockJoinTitle: 'You can’t attend events with an organization account.',
        blockJoinBody: 'Sign in with your personal account to attend.',
        blockApplyTitle: 'You can’t apply with an organization account.',
        blockApplyBody: 'Sign in with your personal account to apply.',
        blockOwnBody: 'This is a {organizationName} event. Sign in with your personal account to attend.',
        manage: 'Manage',
        viewAttendees: 'See attendees',
        blockInviteTitle: 'You can’t join this invite with an organization account.',
        blockInviteBody: 'This invite was sent to {email}, which is an organization account. To attend with your personal account, ask the organizer to invite your personal address.',
        declineInvite: 'Decline invite',
        staleRegistration: 'Registration didn’t go through. This tab is signed in to your organization account.',
        teamInvite: 'Team invite',
        validUntil: 'Valid until {date}',
        createTitle: 'Create an organization account',
        createNote: 'This account is only for managing {organizationName} work. You use your personal account to attend events and meet people.',
        emailLabel: 'Email',
        emailHint: 'The invite was sent to this address. It can’t be changed.',
        locked: 'Locked',
        nameLabel: 'Full name',
        methodLabel: 'Sign-in method',
        methodEmail: 'Continue with an email code',
        consentSentence: 'I’ve read the {terms} and the {notice}.',
        consentTerms: 'terms of use',
        consentNotice: 'organization account privacy notice',
        create: 'Create organization account',
        creating: 'Creating…',
        codeTitle: 'Verify your email',
        codeBody: 'We sent a code to {email}. Enter it and set your password.',
        codeLabel: 'Verification code',
        passwordLabel: 'Password',
        confirmCreate: 'Create account',
        conflict: 'This address is used by a personal account. Ask your organization to invite another work address.',
        otherAddress: 'This invite was sent to {email}.',
        otherNowPersonal: 'You’re signed in to the personal account {email}.',
        otherNowOrganization: 'You’re signed in to the organization account {email}.',
        otherCta: 'Create an organization account with that address',
        otherNote: 'This session ends and the invite page opens again.',
        expiredTitle: 'This invite has expired',
        expiredBody: 'Ask your organization admin for a new invite.',
        revokedTitle: 'This invite was cancelled',
        revokedBody: 'The organization withdrew this invite. If you think this is a mistake, contact your admin.',
        invalidTitle: 'This invite link isn’t valid',
        invalidBody: 'Open the link again from the invite email.',
        goHome: 'Go to home page',
        doneTitle: 'You joined the {organizationName} team',
        doneBody: 'Opening organization home…',
        pendingOptionLine: '{organizationName} has invited this address.',
        pendingOptionLink: 'Continue as an organization account',
        pendingOptionBack: 'Continue as a personal account',
        pendingOptionNote: 'An organization account is only for managing {organizationName} work. Your personal account stays separate.',
        settingsTitle: 'Account settings',
        settingsLeadSite: 'Your organization account’s preferences and data.',
        organizationNameLabel: 'Organization name',
        sectionProfile: 'Profile',
        sectionPreferences: 'Preferences',
        sectionOrganizations: 'My organizations',
        sectionData: 'My data',
        sectionAccount: 'Account',
        sectionType: 'Account type',
        workEmail: 'Work email',
        emailNote: 'Email can’t be changed.',
        identityNote: 'An organization account appears with the organization’s name and logo. It has no separate profile photo.',
        logoNoteLink: 'Change the organization logo',
        saving: 'Saving…',
        saved: 'Saved',
        nameRequired: 'Full name is required.',
        settingsLoadError: 'Couldn’t load settings.',
        language: 'Language',
        theme: 'Theme',
        timezone: 'Time zone',
        themeLight: 'Light',
        themeDark: 'Dark',
        themeSystem: 'System',
        leave: 'Leave organization',
        joinedOn: 'Joined {date}',
        soloLeave: 'You’re the only owner. Transfer ownership first.',
        soloLeaveSite: 'You’re the only owner. Transfer ownership first.',
        leaveTitle: 'Leave this organization?',
        leaveTitleSite: 'Leave this organization?',
        leaveBody: 'Your access to {organizationName} ends right away. You need a new invite to join again.',
        leaveBodySite: 'Your access to {organizationName} ends right away. You need a new invite to join again.',
        leaving: 'Leaving the organization…',
        dataNote: 'You download the legal copy of your own account data as a ZIP. The organization’s attendee data isn’t in this file. An event’s attendee list comes from Download attendee list on the Registrations screen.',
        dataNoteSite: 'You download the legal copy of your own account data as a ZIP. The organization’s attendee data isn’t in this file. An event’s attendee list comes from Download attendee list on the Registrations screen.',
        consentRecords: 'My consent records',
        notificationSettings: 'Notifications',
        soloDelete: 'Transfer ownership first.',
        soloDeleteSite: 'Transfer ownership first.',
        deleteNoteSite: 'Deletion is confirmed with a code sent to your email.',
        transferOwnership: 'Transfer ownership',
        transferOwnershipSite: 'Transfer ownership',
        typeNote: 'This is an organization account. Use your personal account to attend events.',
        typeNoteSite: 'This is an organization account. Use your personal account to attend events.',
        backToSettings: 'Back to settings',
    },
    manage: {
        title: 'Participation management',
        // Blok 16: yükleme HATASINDA boş durum çizilmez, bu satır + "Tekrar dene" gelir.
        loadError: 'Management data could not be loaded.',
        tabApplications: 'Applications',
        tabInvitations: 'Invitations',
        tabAttendees: 'Attendees',
        applicationsEmpty: 'No pending applications',
        applicationsEmptyBody: 'They will appear here as they arrive.',
        approve: 'Approve',
        reject: 'Reject',
        approveSelected: 'Approve selected ({count})',
        rejectSelected: 'Reject selected ({count})',
        bulkApproveTitle: 'Approve {count} applications?',
        bulkApproveTitle_one: 'Approve {count} application?',
        bulkRejectTitle: 'Reject {count} applications?',
        bulkRejectTitle_one: 'Reject {count} application?',
        decisionTicketLabel: 'Ticket type for approval',
        contactHiddenNote: 'Applicant and attendee contact details are hidden; communication happens through the platform.',
        rejectionNotSentNote: 'The rejection reason is not sent to the applicant; the note is internal only.',
        invitationsEmpty: 'No invitations sent yet',
        invitationsEmptyBody: 'Enter email addresses to send your first one.',
        inviteFilterAll: 'All',
        inviteFilterAccepted: 'Accepted',
        inviteFilterPending: 'Awaiting reply',
        inviteFilterClosed: 'Closed',
        inviteFilterEmpty: 'No invitations in this state.',
        inviteCsvCta: 'Upload CSV',
        inviteCsvRule: 'In the file the email column is required; name and company are optional. At most {limit} rows are read and the same address is never invited twice. The file is NOT uploaded; addresses are read in your browser into the field above and you confirm the send.',
        inviteCsvLoaded: '{count} addresses taken from the file; review the list before sending.',
        inviteCsvLoaded_one: '{count} address taken from the file; review the list before sending.',
        inviteCsvEmpty: 'No valid email address was found in the file.',
        inviteQuotaTitle: 'Ticket capacity',
        inviteQuotaRemaining: '{count} spots left',
        inviteQuotaRemaining_one: '{count} spot left',
        inviteQuotaFilled: '{count} registered out of a capacity of {total}.',
        inviteQuotaFilled_one: '{count} registered out of a capacity of {total}.',
        inviteQuotaUnlimited: 'Unlimited capacity',
        inviteQuotaUncappedCeiling: 'One ticket type has no capacity limit, so registration closes at the event limit. {count} people registered.',
        inviteQuotaUncappedCeiling_one: 'One ticket type has no capacity limit, so registration closes at the event limit. {count} person registered.',
        inviteQuotaUncapped: 'One ticket type has no capacity limit. {count} people registered.',
        inviteQuotaUncapped_one: 'One ticket type has no capacity limit. {count} person registered.',
        inviteQuotaExplain: 'Sent invitations don’t hold a spot. A spot is taken when someone accepts and registers.',
        inviteCeiling: 'The event limit is {count} attendees',
        inviteCeiling_one: 'The event limit is {count} attendee',
        inviteCeilingNone: 'No event limit',
        inviteCeilingBinds: 'Ticket capacity is larger than the event limit. Registration closes at the limit, so at most {count} people can register.',
        inviteCeilingBinds_one: 'Ticket capacity is larger than the event limit. Registration closes at the limit, so at most {count} person can register.',
        inviteOverflowNote: '{count} spots left. All invitations will be sent, and the first {count} people to accept can register.',
        inviteOverflowNote_one: '{count} spot left. All invitations will be sent, and the first person to accept can register.',
        inviteOverflowNoteFull: 'No spots left. All invitations will be sent, but nobody can register unless a spot opens up.',
        inviteQuotaAccepted: '{count} accepted',
        inviteQuotaAccepted_one: '{count} accepted',
        inviteQuotaUnset: 'No ticket capacity set, so the number of invitations is unlimited.',
        inviteQuotaPartial: 'The accepted count covers only the loaded invitations.',
        inviteVisibilityNote: 'An invitation alone does not put anyone on the attendee list. People who accept and register appear on it. Only registered attendees see the list.',
        inviteEmailsLabel: 'Email addresses (one per line)',
        inviteTicketLabel: 'Ticket type for this invitation',
        inviteMessageLabel: 'Invitation message (optional)',
        sendInvites: 'Send invitations',
        inviteParseReport: '{valid} valid · {invalid} invalid · {duplicate} duplicate',
        inviteSentReport: '{count} invitations sent.',
        inviteSentReport_one: '{count} invitation sent.',
        inviteSkippedReport: '{count} addresses were skipped (already invited or registered).',
        inviteSkippedReport_one: '{count} address was skipped (already invited or registered).',
        quotaWarning: 'The number of invitations exceeds the remaining quota of the selected ticket.',
        remainingQuota: 'Remaining quota: {count}',
        resend: 'Resend',
        revoke: 'Revoke',
        revokeConfirm: 'The invitation will be revoked; the link will stop working. Are you sure?',
        revokeConfirmCta: 'Revoke invitation',
        invitationStatusSent: 'Sent',
        invitationStatusOpened: 'Opened',
        invitationStatusAccepted: 'Accepted',
        invitationStatusRejected: 'Declined',
        invitationStatusExpired: 'Expired',
        invitationStatusRevoked: 'Revoked',
        attendeesEmpty: 'No attendees yet.',
        removeAttendee: 'Remove attendee',
        removeAttendeeConfirmCta: 'Remove',
        removeConfirm: 'The attendee will be removed from the list and notified. Are you sure?',
        changeTicket: 'Change ticket type',
        filterStatusLabel: 'Status',
        filterTicketLabel: 'Ticket type',
        searchPlaceholder: 'Search by name…',
        sourceLabel: 'Source',
        kicker: 'Organizer panel',
        colPerson: 'Person',
        colNote: 'Application note',
        colStatus: 'Status',
        colDate: 'Date',
        colActions: 'Actions',
        colEmail: 'Email',
        colSent: 'Sent',
        colExpires: 'Expires',
        colTicket: 'Ticket',
        colCompany: 'Company',
        downloadAttendeesCta: 'Download attendee list',
        sourceRegistration: 'Registration',
        sourceApplication: 'Application',
        sourceInvitation: 'Invitation',
        sourceOrganizerAuto: 'Organizer (not counted)',
        organizerAutoBadge: 'Organizer',
        registrationSummary: '{count} registrations · {remaining} spots left',
        registrationSummary_one: '{count} registration · {remaining} spots left',
        registrationSummaryNoCapacity: '{count} registrations',
        registrationSummaryNoCapacity_one: '{count} registration',
        registrationOrganizerNote: "The organizer's own registration is listed here; it is excluded from the registration count and capacity.",
        /* NEW LOOK 4c (registrations, invitations, applications) - web only. */
        registrationsIntro: 'People whose registration is confirmed.',
        invitationsIntro: 'People you invite accept through the link in the email and register.',
        applicationsIntro: 'People you approve are notified by email and join the attendee list.',
        statOccupancy: 'Occupancy',
        statRemaining: '{count} seats left',
        statRemaining_one: '{count} seat left',
        statPendingApplications: 'Pending applications',
        statApprovedApplications: 'Approved',
        statRejectedApplications: 'Rejected',
        invitationsSentSummary: '{count} invitations sent',
        invitationsSentSummary_one: '{count} invitation sent',
        inviteSendTitle: 'Send invitations',
        inviteListTitle: 'Sent invitations',
        noteEmpty: 'No note',
        pagerNavigation: 'Pages',
        pagerPrevious: 'Previous page',
        pagerNext: 'Next page',
        pagerPage: 'Page {page}',
        registrationsPagerInfo: 'Showing {from}-{to} of {count} rows',
        registrationsPagerInfo_one: 'Showing {from}-{to} of {count} row',
        registrationsPagerInfoSplit: 'Showing {from}-{to} of {count} rows ({registered} registered, {cancelled} cancelled)',
        registrationsPagerInfoSplit_one: 'Showing {from}-{to} of {count} row ({registered} registered, {cancelled} cancelled)',
        applicationsPagerInfo: 'Showing {from}-{to} of {count} applications',
        applicationsPagerInfo_one: 'Showing {from}-{to} of {count} application',
        invitationsPagerInfo: 'Showing {from}-{to} of {count} invitations',
        invitationsPagerInfo_one: 'Showing {from}-{to} of {count} invitation',
        registrationsSearchEmpty: 'No registrations match your search.',
        registrationsFilterEmpty: 'No registrations with this status.',
        applicationsSelectedBar: '{count} applications selected',
        applicationsSelectedBar_one: '{count} application selected',
        searchClear: 'Clear search',
        colRegisteredDate: 'Registered on',
        noActions: 'No actions',
        removeAttendeeConfirmWebCta: 'Remove attendee',
        /* Section hints (T1) - `MANAGE_SECTIONS.hintKey`; no numbers, only scope. */
        hintSetup: 'Basics, access mode and publishing',
        hintProgram: 'Sessions and speakers',
        hintRegistrations: 'Attendee list and registration actions',
        hintInvitations: 'Sending, status and revoking invitations',
        hintApplications: 'Application queue awaiting a decision',
        hintAnnouncements: 'Informational messages to attendees',
        hintTickets: 'Ticket kinds and capacity',
        hintAreas: 'Meeting areas and time ranges',
        hintReport: 'Live registrations, results and PDF',
        hintSponsor: 'Booth QR code and lead report',
        hintBrand: 'Logo, cover and colour',
        hintPostEvent: 'Certificate of attendance and post-event survey',
        hintBilling: 'Credits, participant limit and usage',
        kpiPendingApplications: 'Pending applications',
        kpiInvitationsSent: 'Invitations sent',
        kpiAttendees: 'Registered attendees',
        // Reference `appSummary` (line 858): pending / approved / rejected - same query as the list.
        applicationSummary: '{pending} pending · {approved} approved · {rejected} rejected',
        applicationStatusSubmitted: 'Pending',
        applicationStatusApproved: 'Approved',
        applicationStatusRejected: 'Rejected',
        applicationStatusWithdrawn: 'Withdrawn',
        attendeeStatusPendingApproval: 'Pending approval',
        attendeeStatusRegistered: 'Registered',
        attendeeStatusWaitlisted: 'Waitlisted',
        attendeeStatusRejected: 'Rejected',
        attendeeStatusCancelled: 'Cancelled',
        attendeeStatusAttended: 'Attended',
        tabProgram: 'Speakers & agenda',
        inviteTicketDefault: 'Default type',
        // --- Blok 8-3: bulk action bar (screen 19) ---
        tabTickets: 'Tickets',
        tabAnalytics: 'Analytics',
        selectAll: 'Select all',
        selectRow: 'Select {name}',
        selectedCount: '{count} registrations selected',
        selectedCount_one: '{count} registration selected',
        clearSelection: 'Clear selection',
        bulkRemove: 'Remove selected',
        bulkChangeTicket: 'Change ticket type',
        bulkReasonLabel: 'Reason (written to the audit log)',
        bulkReasonHint: 'This action cannot be undone. A reason is required and stored in the audit log.',
        bulkRemoveConfirm: '{count} registrations will be cancelled, attendees will be notified and the action will be written to the audit log. This cannot be undone.',
        bulkRemoveConfirm_one: '{count} registration will be cancelled, the attendee will be notified and the action will be written to the audit log. This cannot be undone.',
        bulkTicketConfirm: 'The ticket type of {count} registrations will change and attendees will be notified.',
        bulkTicketConfirm_one: 'The ticket type of {count} registration will change and the attendee will be notified.',
        bulkResult: '{succeeded} processed · {failed} could not be processed.',
        bulkPartialNote: 'Unprocessed rows may have changed while you were looking or run out of quota.',
        // T5 QA P2: failed items of a bulk application decision (200 + `failed`).
        decisionPartialDone: '{count} applications processed.',
        decisionPartialDone_one: '{count} application processed.',
        decisionFailedCeiling: 'Attendee limit reached, {count} applications could not be approved.',
        decisionFailedCeiling_one: 'Attendee limit reached, the application could not be approved.',
        decisionFailedOther: '{count} applications could not be processed. {reason}',
        decisionFailedOther_one: 'The application could not be processed. {reason}',
        bulkTicketTargetLabel: 'Target ticket type',
        // --- Blok 8-3: ticket editing (Story 3.8) ---
        ticketsIntro: 'You can add ticket types, change quota and close sales after publishing too.',
        ticketsEmpty: 'No ticket types yet',
        ticketsEmptyBody: 'A default type is created automatically on publish.',
        ticketNameLabel: 'Type name',
        ticketDescriptionLabel: 'Description (optional)',
        ticketQuotaLabel: 'Quota (empty = unlimited)',
        ticketStatusLabel: 'Status',
        ticketStatusActive: 'On sale',
        ticketStatusHidden: 'Hidden',
        ticketStatusClosed: 'Closed',
        ticketDefaultLabel: 'Default type',
        ticketRemaining: '{count} places left',
        ticketRemaining_one: '{count} place left',
        ticketsCaptionQuota: 'total quota {count}',
        ticketsCaptionQuota_one: 'total quota {count}',
        ticketsCaptionCapacity: 'event capacity {count}',
        ticketsCaptionCapacity_one: 'event capacity {count}',
        ticketKindHelp: 'The type is a label. "Paid" does not take payment either.',
        ticketEditTitle: 'Edit ticket type',
        ticketAdd: 'Add ticket type',
        ticketSave: 'Save',
        ticketEdit: 'Edit',
        ticketDelete: 'Delete',
        ticketDeleteConfirm: 'This ticket type will be deleted. Types with registrations cannot be deleted.',
        ticketReserved: '{count} registrations on this type',
        ticketReserved_one: '{count} registration on this type',
        ticketUnlimited: 'Unlimited',
        ticketColUsage: 'Usage',
        ticketCancel: 'Cancel',
        ticketQuotaHint: 'Quota cannot go below the current number of registrations.',
        ticketQuotaShort: 'Quota',
    },
    organizer: {
        brandSuffix: 'Organizer',
        navHeading: 'Event',
        navSetup: 'Event setup',
        navProgram: 'Speakers and agenda',
        navRegistrations: 'Registrations',
        navInvitations: 'Invitations',
        navApplications: 'Applications',
        navAnnouncements: 'Announcements',
        navTickets: 'Tickets',
        navAreas: 'Meeting areas',
        navReport: 'Report',
        navSponsor: 'Sponsor leads',
        navBrand: 'Brand settings',
        navBilling: 'Payment and credits',
        navPostEvent: 'Certificate and survey',
        navPendingLabel: '{section}, {count} pending',
        navPendingLabel_one: '{section}, {count} pending',
        backToMyEvents: 'Managing',
        openNavMenu: 'Open menu',
        viewPublicPage: 'View event page',
        gateLoading: 'Opening the panel…',
        setup: {
            title: 'Event setup',
            stepCounter: 'Step {step}/4',
            step1: 'Basics',
            step2: 'Access and tickets',
            step3: 'Agenda',
            step4: 'Publish',
            basicsTitle: 'Basics',
            accessTitle: 'Access mode',
            accessIntro: 'Decides who can join. You can change it later; the consequence of each change is stated below.',
            ticketsTitle: 'Registration and tickets',
            ticketsHint: 'Ticket types and quota are managed in their own section.',
            agendaTitle: 'Agenda and speakers',
            agendaIntro: 'As you add sessions, the public event page and the attendee agenda update automatically.',
            agendaCta: 'Go to the agenda section',
            checklistTitle: 'Pre-publish checklist',
            checkBasics: 'Basics complete',
            checkBasicsOk: 'Name, date and venue are set.',
            checkBasicsMissing: 'Name, date or venue is missing.',
            checkTickets: 'Tickets and quota defined',
            checkTicketsOk: '{count} ticket types defined.',
            checkTicketsOk_one: '{count} ticket type defined.',
            checkTicketsMissing: 'A default type is created automatically on publish.',
            checkAgenda: 'Agenda ready',
            checkAgendaOk: '{count} sessions defined.',
            checkAgendaOk_one: '{count} session defined.',
            checkAgendaMissing: 'No sessions added yet.',
            checkPublished: 'Publication status',
            checkPublishedOk: 'The event is live.',
            checkPublishedMissing: 'The event is a draft; it has not been published.',
            // The publish gate (Decision 1) is VISIBLE in the checklist: no 403 surprise.
            checkBrand: 'Brand logo',
            checkBrandMissing: 'No logo uploaded. The badge and public page are published with the default typography.',
            checkBrandOwn: 'This event has its own logo.',
            checkBrandInherited: 'Your organization logo is inherited; you do not need to upload one.',
            checkVerification: 'Organization verification',
            checkVerificationOk: 'The organization is verified; the badge is visible and the event is listed in discovery.',
            checkVerificationMissing: 'The organization is not verified. This does not block publishing; once verified the badge appears and the event is listed in discovery.',
            checkVerificationUnknown: 'We could not read the verification status. This does not block publishing; it appears here once the status refreshes.',
            // The check mark's DECISION reaches screen readers too (not colour only).
            checkStateOk: 'done',
            checkStateMissing: 'missing',
            publish: 'Publish',
            preview: 'Preview',
            previewKicker: 'Public page preview',
            previewNote: 'This page is open to search engines. The title and description are used for SEO.',
            saved: 'Changes saved.',
            savedPublishing: 'Changes saved. Pages are refreshing…',
            save: 'Save',
            titleLabel: 'Event name',
            summaryLabel: 'Summary',
            descriptionLabel: 'Description',
            venueLabel: 'Venue',
            venueAddressLabel: 'Street address',
            websiteLabel: 'Website',
            capacityLabel: 'Capacity (empty = unlimited)',
            startsAtLabel: 'Starts',
            endsAtLabel: 'Ends',
            // New look 4a (PanelKurulum cards).
            subtitle: 'The details shown on the event page and the rules for taking part.',
            timezoneNote: 'Times are in the {timezone} time zone.',
            ticketsSummary: '{count} ticket types',
            ticketsSummary_one: '{count} ticket type',
            ticketsQuotaTotal: '{count} places',
            ticketsQuotaTotal_one: '{count} place',
            ticketsRegistered: '{count} registrations',
            ticketsRegistered_one: '{count} registration',
            ticketUsage: '{used} of {quota} used',
            ticketUsageUnlimited: '{used} used · unlimited',
            manageTickets: 'Manage tickets',
            programTitle: 'Program',
            programCount: '{count} sessions',
            programCount_one: '{count} session',
            programEmpty: 'No sessions added yet.',
            programSpeakersNote: 'Speakers are managed on the Speakers and agenda screen. Sessions are the same there.',
            addSession: 'Add session',
            colTime: 'Time',
            colSession: 'Session',
            colRoom: 'Room',
            colSpeakers: 'Speakers',
            noSpeakers: 'No speakers',
            // Only an invite-only event page is hidden from search engines (access mode table).
            previewNoteInviteOnly: 'This page is hidden from search engines. It opens only through the invitation link.',
        },
        brand: {
            title: 'Brand settings',
            intro: 'Your event brand appears on the public page and on the badge. It never overrides the app identity, because the platform frame stays fixed and the event brand lives only in the content area.',
            logoLabel: 'Logo',
            logoHint: '{formats} · at least 240×80 · transparent background',
            logoPick: 'Choose a file',
            logoReplace: 'Replace the logo',
            logoUploading: 'Uploading…',
            logoUploaded: 'Uploaded. It goes live when you save.',
            coverLabel: 'Cover image',
            coverHint: '{formats} · 16:9 · up to {megabytes} MB',
            coverNote: 'If you do not upload a cover, a system cover appears on the event page and in lists. The cover is optional.',
            coverSystemBadge: 'System cover',
            coverPick: 'Upload a cover',
            coverReplace: 'Replace the cover',
            coverRemove: 'Remove the cover',
            coverUploading: 'Uploading the cover…',
            coverUploaded: 'Cover uploaded. It goes live when you save.',
            coverUploadedDraft: 'Cover uploaded. It is added as the cover when you save the event.',
            coverRemovedPending: 'The cover will be removed. Once you save, the system cover returns.',
            coverErrorType: 'We do not accept this file type. Pick one of {formats}.',
            coverErrorSize: 'The file is over the {megabytes} MB limit. Pick a smaller image.',
            colorLabel: 'Event colour',
            colorHint: 'The colour is used on the badge top bar and the public page accents.',
            colorOption: 'Choose the colour {color}',
            colorNone: 'No colour chosen',
            colorClear: 'Remove the colour',
            contrastWarning: 'If the colour you pick does not meet AA contrast against text, the system automatically uses a darker shade. Accessibility comes before the brand.',
            contrastApplied: 'This shade does not meet AA contrast; a darker shade is used wherever text sits on it.',
            inheritedTitle: 'This event uses the organization brand',
            inheritedBody: 'Your organization logo and colour apply here. Change the organization brand and this event changes instantly.',
            inheritedEmptyBody: 'Your organization has no logo or colour yet; the badge and public page are published with the platform default tone.',
            overriddenTitle: 'This event overrides the organization brand',
            overriddenBody: 'The values below apply to this event only. Clear them to fall back to the organization brand.',
            orgBrandLink: 'Organization brand',
            clearCta: 'Fall back to the organization',
            save: 'Save the brand',
            saving: 'Saving…',
            saved: 'Brand saved. The event page has been refreshed.',
            publishing: 'Brand saved. The event page is refreshing…',
            badgePreviewLabel: 'Badge preview',
            previewPersonName: 'Full Name',
            previewPersonHeadline: 'Product Manager',
            previewPersonCompany: 'Sample Company',
            badgeScanNote: 'Scanned at the door and during card exchange.',
            goToSetup: 'Go to event setup',
        },
        sponsor: {
            title: 'Sponsor lead module',
            intro: 'Each booth gets its own QR code. When an attendee scans it, they consent to sharing their card; no consent, no lead.',
            emptyTitle: 'The sponsor lead module is not open yet',
            emptyBody: 'Booth, lead and consent-rate data is not collected yet. Until this section opens we show no zero or sample numbers.',
            step1: 'Card exchange is mutual',
            step1Body: 'A scan without consent counts, but carries no identity.',
            step2: 'Attendee data is never sold to sponsors',
            step2Body: 'A lead is released only with the explicit consent of the attendee.',
            step3: 'The module opens in a later release',
            step3Body: 'Once open, the booth list and the printable board document live here.',
            privacyNote: 'Lead data is released to a sponsor only with attendee consent. Scans without consent count, but carry no identity.',
            goToRegistrations: 'Go to registrations',
        },
    },
    gate: {
        title: 'Gate',
        navLabel: 'Gate',
        openCta: 'Open gate',
        registered: 'registered',
        checkedIn: 'check-ins',
        checkedIn_one: 'check-in',
        counterA11y: '{registered} registered, {checkedIn} checked in',
        noCheckIns: 'No check-ins yet',
        counterNoneA11y: '{registered} registered, no check-ins yet',
        countError: 'Couldn’t load the counter.',
        retry: 'Try again',
        soundOn: 'Sound on',
        soundOff: 'Sound off',
        cameraPick: 'Camera',
        cameraFallbackName: 'Camera {index}',
        exit: 'Exit',
        camLive: 'Live camera view',
        hold: 'Hold the badge code inside the frame',
        liveNote: 'Nothing is recorded.',
        cameraOff: 'Turn off camera',
        ok: 'Checked in',
        duplicate: 'Already checked in · {time}',
        duplicateAction: 'Check in again',
        forcedSub: 'Recorded. The first check-in time didn’t change.',
        cancelled: 'Registration cancelled',
        cancelledSub: 'This person has no valid registration for this event.',
        otherEvent: 'This code is for another event',
        otherEventSub: 'Ask for this event’s badge or search by name.',
        unknown: 'Code not recognized',
        unknownSub: 'Scan again or search by name.',
        queued: 'Queued',
        queuedSub: 'Verified when the connection is back.',
        queuedShort: 'Queued',
        queuedPerson: 'Scanned code',
        failed: 'Couldn’t record the check-in',
        failedSub: 'Check your connection and scan again.',
        sending: 'Recording…',
        waiting: 'Hold a code up to the camera. The result shows here.',
        searchLabel: 'Search by name',
        searchPlaceholder: 'First or last name',
        searchHint: 'Type at least 2 letters, tap a row to check in.',
        searchEmpty: 'No registered attendee with that name.',
        searchError: 'Couldn’t search.',
        hitIn: 'In {time}',
        hitNo: 'Not here',
        hitCancelled: 'Cancelled',
        hitPending: 'Awaiting approval',
        hitWaitlisted: 'On the waitlist',
        ticketNone: 'No ticket type',
        recent: 'Recent check-ins',
        recentEmpty: 'No check-ins yet.',
        recentError: 'Couldn’t load recent check-ins.',
        failedListTitle: 'Couldn’t send · {count}',
        failedListTitle_one: 'Couldn’t send · {count}',
        mobileNote: 'You can use the mobile app at the gate.',
        preTitle: 'We use the camera to read codes',
        preBody: 'Nothing is recorded. Your browser will ask for permission.',
        preCta: 'Turn on camera',
        deniedTitle: 'Camera permission is off',
        deniedBody: 'Click the lock icon in the address bar, allow Camera and reload the page. Name search works in the meantime.',
        noneTitle: 'No camera found',
        noneBody: 'No camera is connected or another app is using it. Close the other app and try again. Name search works in the meantime.',
        insecureTitle: 'The camera can’t open on this connection',
        insecureBody: 'Browsers open the camera only on a secure (HTTPS) connection. Name search works in the meantime.',
        closedTitle: 'The gate opens on {date}',
        closedOverTitle: 'The gate closed on {date}',
        closedBody: 'Check-ins are recorded only on the event day. You can see registered attendees by name.',
        offlineBar: 'You’re offline. Scanning continues and check-ins wait in this tab.',
        offlineChip: '{count} check-ins waiting to send',
        offlineChip_one: '{count} check-in waiting to send',
        syncBar: 'Back online. Sending queued check-ins.',
        syncChip: '{sent} / {total}',
        noPermissionTitle: 'You can’t record check-ins',
        noPermissionBody: 'The Viewer role can’t open the gate. Ask an organization admin for the Editor role.',
        noPermissionCta: 'Back to organization home',
        accountKindTitle: 'The gate opens with an organization account',
        accountKindBody: 'This is a personal account. Record check-ins with the organization account your organization set up for you.',
        accountKindCta: 'Sign in with your organization account',
    },
    billing: {
        title: 'Payment and credits',
        intro: 'This event’s credit status and participant usage. No payment is taken on the site. Our team loads credits to your organization.',
        eventKicker: 'This event',
        eventDraftTag: 'No credit used',
        eventDraftBody: 'Publishing uses 1 credit.',
        eventDraftBodyCeiling: 'Publishing uses 1 credit. The limit becomes {ceiling}.',
        eventDraftBodyContract: 'Publishing falls under your Enterprise contract. No credit is used.',
        draftEmptyTitle: 'This event is not published yet',
        rowStatus: 'Status',
        rowCreditUsed: 'Credits used',
        creditUsedNone: 'No credit used',
        rowCeiling: 'Participant limit',
        rowSource: 'Charged to',
        sourcePack: '12-pack',
        sourceSingle: 'Single',
        sourceTrial: 'Welcome credit',
        sourceContract: 'Enterprise contract',
        rowPublishedBy: 'Published by',
        rowPublishedAt: 'Published on',
        publishedByValue: '{name}, {date}',
        usageCountsNote: 'Registrations, invitation acceptances and application approvals all count.',
        eventTagUnlimited: 'Unlimited',
        eventBodyNone: 'There is no credit record for this event.',
        usageSectionKicker: 'Participant usage',
        usageRegisteredContract: 'Registered · contract',
        alertCounts: '{used} / {ceiling}.',
        usageLeft: '{count} spots left.',
        usageLeft_one: '{count} spot left.',
        usageWarnNote: 'Past 80%. The organizer gets a one-time warning email.',
        usageFullNote: 'Limit reached. New registrations, invitation acceptances and application approvals are closed. Check-in stays open for registered participants.',
        usageFullLocks: 'Sending invites, bulk actions, industry and seniority breakdowns are locked.',
        usageContractNote: 'Enterprise contract. No participant limit.',
        creditKicker: 'Organization credits',
        refundTitle: 'Refund rule',
        refundBody: 'If an event is cancelled before its first registration, its credit, welcome credit included, is returned to the organization. After the first registration, the credit is not refunded.',
        contactLine: 'Talk to our team to load credits or set up an Enterprise contract.',
        contactCta: 'Contact us',
        trustLine: '{brandName} is free for attendees. Credits are only spent by the organizing organization.',
        plansKicker: 'Plans',
        planNameFree: 'Starter',
        planNamePro: 'Pro',
        planNameEnterprise: 'Enterprise',
        fundingTrial: 'Published with the welcome credit',
        fundingSingle: 'Published with a single credit',
        fundingPack12: 'Published with a credit from the 12-pack',
        fundingContract: 'Published under the Enterprise agreement',
        fundingNone: 'No credit record for this event',
        currentTag: 'Current',
        currentPlanAction: 'Plan in use',
        priceFree: 'Free',
        priceQuote: 'On request',
        pricePerEvent: '{amount} ₺ per event',
        pricePerYear: '{amount} ₺ per year',
        vatNote: 'Amounts exclude VAT.',
        limitAttendees: '{count} attendees per event',
        limitAttendees_one: '{count} attendee per event',
        limitUnlimited: 'Unlimited attendees',
        featureInvites: '{count} invitations per event',
        featureInvites_one: '{count} invitation per event',
        featureInvitesUnlimited: 'Unlimited invitations',
        featureTeam: '{count} team members',
        featureTeam_one: '{count} team member',
        featureTeamUnlimited: 'Unlimited team members',
        featureBreakdownsUntilCeiling: 'Sector and seniority breakdown for up to {count} attendees',
        featureBreakdownsUntilCeiling_one: 'Sector and seniority breakdown for up to {count} attendee',
        featureBreakdownsAlways: 'Sector and seniority breakdown with no ceiling',
        featureWalletFree: 'Wallet, business cards and CSV/vCard export are free and unlimited on every plan',
        ctaUpgradePro: 'Upgrade to Pro',
        ctaContactSales: 'Book a call',
        ctaPlanChange: 'Change plan',
        invoicesKicker: 'Invoices',
        invoiceColDate: 'Date',
        invoiceColDescription: 'Description',
        invoiceColAmount: 'Amount',
        invoiceColStatus: 'Status',
        invoicesEmptyTitle: 'No invoices yet',
        invoicesEmptyBody: 'Payments are not open yet, so nothing is invoiced. Once they are, this table will carry the date, description, amount, status and a PDF link; we do not show sample rows.',
        invoicesEmptyStep1: 'Nothing is due today',
        invoicesEmptyStep1Body: 'Nothing is collected on any plan today, because payments are not open yet.',
        invoicesEmptyStep2: 'Track your usage in the usage panel',
        invoicesEmptyStep2Body: 'The attendee count in the panel is the same one an invoice would be based on.',
        invoicesEmptyStep3: 'Invoices will land here once payments open',
        invoicesEmptyStep3Body: 'No past document will be lost.',
        usageKicker: 'This event',
        usageMeta: 'Plan limit {ceiling} · {percent}% used',
        usageMetaUnlimited: 'This plan has no attendee ceiling.',
        usageBarLabel: 'Attendee usage',
        usageUnavailable: 'Plan status could not be read.',
        paymentMethodCta: 'Update payment method',
        statusOk: 'Below the ceiling',
        statusWarning: 'Close to the ceiling',
        statusExceeded: 'Ceiling exceeded',
        bannerWarning: 'You are at {percent}% of the attendee ceiling of {ceiling} ({used}/{ceiling}).',
        bannerExceeded: 'You passed the attendee ceiling ({used}/{ceiling}). Right now {capabilities} are locked.',
        bannerExceededExempt: 'You passed the attendee ceiling ({used}/{ceiling}).',
        bannerGrandfathered: 'This event follows the earlier rule; no lock is applied until {date}.',
        bannerLink: 'Plan and billing',
        bannerDismiss: 'Hide this bar',
        capabilityInvitationsSend: 'sending and resending invitations',
        capabilityAttendeesBulk: 'bulk attendee actions',
        capabilityAnalyticsBreakdowns: 'sector and seniority breakdown in analytics',
        lockedNoticeTitle: 'Locked because the ceiling was passed',
        lockUnknownTitle: 'Limit status could not be read',
        lockStatusUnknown: 'The numbers could not be read right now, so without the plan status we cannot say where you are against the limit. If this event is above its limit the server may still reject the action. Use “Try again” above to refresh.',
        shellLoadError: 'The plan status could not be read, so the ceiling bar and lock information cannot be shown on this screen. Limits still apply on the server.',
        lockInvitationsSend: 'The attendee ceiling was passed ({used}/{ceiling}); sending and resending invitations are locked. Revoking an invitation and viewing the list stay open. Contact us about an Enterprise agreement for unlimited attendance.',
        lockInvitationsResend: 'The attendee ceiling was passed ({used}/{ceiling}); resending is locked too, because it revokes the old invitation and writes a NEW one, so the server rejects it as well. Revoking an invitation and viewing the list stay open. Contact us about an Enterprise agreement for unlimited attendance.',
        lockAttendeesBulk: 'The attendee ceiling was passed ({used}/{ceiling}); bulk actions are locked. Removing a single attendee and changing a single ticket stay open. Contact us about an Enterprise agreement for unlimited attendance.',
        lockAnalyticsBreakdowns: 'The attendee ceiling was passed ({used}/{ceiling}); the sector and seniority breakdown is locked. Registration counts, the hourly distribution and the ticket breakdown stay open. Contact us about an Enterprise agreement for unlimited attendance.',
        lockAnalyticsBreakdownsNoCount: 'The sector and seniority breakdown is locked because the attendee ceiling was passed. Registration counts, the hourly distribution and the ticket breakdown stay open. (The numbers could not be read right now.)',
        lockedPreviewTitle: 'Breakdown locked',
        lockedPreviewCollected: 'In this event {count} people shared their sector and seniority.',
        lockedPreviewCollected_one: 'In this event {count} person shared their sector and seniority.',
        registrationsNoticeTitle: 'The list was not shortened',
        registrationsNoticeBody: 'The attendee limit is full ({used}/{ceiling}). Every registration stays visible and searchable; only bulk actions are locked.',
        dialogTitle: 'Purchasing is not open yet',
        dialogBody: 'Self-service upgrades are closed in this release, because the payment provider connection is not finished yet. That is why we do not show a payment step that would not work.',
        dialogCeilingNote: 'On the {plan} plan, registrations, sending invitations, bulk actions and analytics breakdowns stay open up to the per-event attendee ceiling ({ceiling}). Once the ceiling is reached, new registrations, invitation acceptances and application approvals close; check-in stays open for registered attendees.',
        dialogFreeNote: 'Wallet, business cards and export are free and unlimited on every plan; they are never locked.',
        dialogClose: 'Close',
    },
    salesInquiry: {
        metaTitle: 'Contact us',
        pageTitle: 'Run your event on {brandName}',
        fieldCompany: 'Company name',
        fieldEventType: 'Event type',
        eventTypePlaceholder: 'Choose an event type',
        eventTypeConference: 'Conference',
        eventTypeSummit: 'Summit',
        eventTypeMeetup: 'Meetup',
        eventTypeFair: 'Trade fair',
        eventTypeInternal: 'Internal',
        eventTypeTraining: 'Training / workshop',
        eventTypeOther: 'Other',
        fieldAttendees: 'Estimated attendees',
        fieldDate: 'Estimated date',
        fieldContact: 'Contact person',
        fieldEmail: 'Work email for the organization account',
        fieldPhone: 'Phone · optional',
        fieldNote: 'Note · optional',
        noticeLink: 'privacy notice',
        noticeSentence: 'I have read the {link}.',
        submit: 'Request a call',
        submitting: 'Sending…',
        stepsTitle: 'What happens next?',
        step1: 'Your request reaches us.',
        step2: 'We get in touch with you.',
        step3: 'We open your organization and send you an invitation.',
        valueLine: 'Attendees meet before the event starts.',
        sentTitle: 'We received your request',
        sentBody: 'We will get back to you as soon as possible.',
        sentCompany: 'Company',
        sentEmail: 'Email',
        sentHome: 'Back to home',
        errorRateLimited: 'Too many attempts. Your details are kept. Try again in a few minutes.',
        errorUnknown: 'We could not confirm whether your request reached us. Your details are kept. You can send it again.',
        errorRejected: 'Your request could not be sent. Your details are kept.',
        retry: 'Try again',
        siteTitle: 'Run your event with {brandName}.',
        siteLead: 'Fill in the form and our team will set up the right plan with you.',
        siteEventTypePlaceholder: 'Choose',
        siteEventTypeHint: 'Conference, Summit, Meetup, Fair, Internal, Training or workshop, Other',
        sitePlaceholderContact: 'Full name',
        siteFieldEmail: 'Work email',
        siteEmailHint: 'The organization account is opened with this address.',
        sitePlaceholderEmail: 'name@company.com',
        siteFieldPhone: 'Phone',
        sitePlaceholderPhone: '+90',
        siteFieldNote: 'Note',
        siteOptional: '(optional)',
        siteSubmit: 'Request a meeting',
        siteStep1: 'Your request reaches us.',
        siteStep2: 'We get in touch with you.',
        siteStep3: 'We open your organization and send an invitation to your work email.',
        siteSentTitle: 'Thank you, your request has reached us',
        siteSentBody: 'We will get in touch with you.',
        siteErrorRateLimited: 'Too many attempts. Your details are kept. Try again in a few minutes.',
        siteErrorUnknown: 'We could not confirm whether your request reached us. Your details are kept. Check your connection and send it again.',
        siteErrorRejected: 'Your request could not be sent. Your details are kept.',
    },
    orgRequest: {
        metaTitle: 'Contact us',
        pageTitle: 'Contact us',
        pageTitleSite: 'Contact us',
        pageLeadSite: 'Write to our team about a credit top-up, an Enterprise contract or support.',
        identityOrganizationSite: 'Organization name',
        pricesTitleSite: 'Credit packages',
        savingRowSite: 'Saving per event',
        summaryOrganization: 'Organization',
        summaryEmail: 'Email',
        fieldTopic: 'Topic',
        topicPlaceholder: 'Choose a topic',
        topicCreditTopup: 'Credit top-up',
        topicEnterpriseContract: 'Enterprise contract',
        topicSupport: 'Support and issue report',
        topicOther: 'Other',
        fieldPackage: 'Package',
        fieldReferralCode: 'Referral code',
        referralCodeHint: 'Letters a-z and digits only, no spaces. {min} to {max} characters.',
        packageSingle: 'Single',
        packagePack: '12-pack',
        packageEnterprise: 'Enterprise',
        priceValue: '{amount} USD',
        priceEnterprise: 'Set by contract',
        saving: 'about {percent}%',
        priceNote: 'Prices are in USD, exclude taxes and are for information only. There is no checkout on the site.',
        fieldPhone: 'Phone · optional',
        fieldNote: 'Note · optional',
        noticeSentence: 'Your request is processed under the {link}.',
        noticeSentenceSite: 'Your request is processed under the {link}.',
        noticeLink: 'privacy notice',
        submit: 'Send request',
        stepsTitle: 'What happens next?',
        step1: 'Your request reaches our team.',
        step2: 'We get back to you by email.',
        step3: 'Credits or the contract are set up for your organization.',
        sentBack: 'Back to organization home',
        sentTitleSite: 'Request received',
        sentBodySite: 'We will get back to you by email.',
        sentReferralCode: 'Your referral code was sent with your request. If it is valid, our team will add it when loading your credits.',
        loadError: 'Organization details could not be loaded.',
        pricesLoadError: 'Package details could not be loaded right now. The request can still be sent.',
    },
    orgEntry: {
        pageTitle: 'My organizations',
        emptyTitle: 'You are not in an organization yet',
        emptyBody: 'To run your event on {brandName}, contact us and we will open your organization.',
        emptyCta: 'Contact us',
        emptyInviteHint: 'If you are on an organization’s team, ask its admin for an invitation.',
        menuContact: 'Run your event with us',
        organizerOnlyManageable: 'Only organizations you can manage are listed.',
        organizerViewerRole: 'Your role at {organizationName} is {role}.',
        organizerViewerRoleMany: 'You cannot create events in the organizations you belong to.',
        organizerNoOrganization: 'There is no organization you can create events for.',
        organizerContactSentence: 'For a new event, {link}.',
        organizerContactLink: 'contact us',
        organizerHint: 'The event is published on behalf of this organization.',
        myEventsStep1: 'Choose your organization',
        myEventsStep1Body: 'Events are always published on behalf of an organization. If you have none, contact us and we will open it for you.',
    },
    credits: {
        kicker: 'Credits',
        unitRemaining: 'credits left',
        unitRemaining_one: 'credit left',
        unitTrial: 'welcome credit',
        unitContract: 'Enterprise contract',
        numberUnlimited: 'Unlimited',
        rowTrial: 'Welcome',
        rowSingle: 'Single',
        rowPack: '12-pack',
        rowExpiry: '12-pack expiry',
        expiryValue: '{count} credits · {date}',
        expiryValue_one: '{count} credit · {date}',
        rowCeiling: 'Participant limit',
        ceilingNone: 'None',
        ceilingPerEvent: '{ceiling} / event',
        moreCredits: 'Contact us for more credits',
        rowContract: 'Contract',
        contractUntil: 'until {date}',
        rowPublishedThisYear: 'Published this year',
        publishedThisYearValue: '{count} events',
        publishedThisYearValue_one: '{count} event',
        rowPublishedThisMonth: 'Published this month',
        publishedThisMonthValue: '{count} events',
        publishedThisMonthValue_one: '{count} event',
        rowLive: 'Published events',
        liveUnaffected: 'Not affected',
        noteCeiling: 'One credit publishes one event. Participant limit {ceiling}.',
        notePublish: 'Credits are spent on publishing. Drafts do not use credits.',
        noteRefund: 'If you cancel an event before anyone registers, its credit comes back.',
        noteOrder: 'Credits that expire first are used first. The welcome credit is used last.',
        noteTrialCeiling: 'An event published with the welcome credit closes registration at {ceiling} participants.',
        noteTrialMore: 'For larger events, our team loads credits for you.',
        noteContract: 'Publishing does not use credits during the contract.',
        noteContractRenew: 'Your account manager will contact you about renewal.',
        exhaustedAlert: 'Out of credits. Contact us to publish a new event. Your drafts are kept.',
        exhaustedCta: 'Contact us',
        requestCta: 'Contact us',
        noteRefundSite: 'If you cancel an event before anyone registers, its credit comes back.',
        noteContractRenewSite: 'Your account manager will contact you about renewal.',
        loadErrorSite: 'Could not load credit details. Your published events are not affected.',
        loadError: 'Could not load credit details. Your published events are not affected.',
        retry: 'Try again',
        inlineExpiry: '{count} 12-pack credits expire on {date}',
        inlineExpiry_one: '{count} 12-pack credit expires on {date}',
    },
    publishDialog: {
        title: 'Publish event',
        leadCredit: 'Once published, the event page opens and registration starts.',
        leadTrial: 'Your welcome credit is used for this event.',
        leadContract: 'Published under your Enterprise contract. No credit is used.',
        rowUse: 'Uses',
        valueOneCredit: '1 credit',
        valueTrial: 'Welcome credit',
        rowRemaining: 'Credits left',
        rowRemainingTrial: 'Welcome credit left',
        valueTransition: '{from} → {to}',
        rowCeiling: 'Participant limit',
        ceilingUnlimited: 'Unlimited',
        rowCredit: 'Credit',
        valueNotUsed: 'Not used',
        rowContract: 'Contract',
        contractUntil: 'until {date}',
        capacityInfo: "You set the capacity to {capacity}. This credit's attendee limit is {ceiling}. To publish, lower the capacity to {ceiling} or less.",
        confirm: 'Publish',
        publishing: 'Publishing…',
        exhaustedTitle: 'Publishing needs a credit',
        exhaustedLead: 'You can save your event as a draft. Nothing you entered is lost.',
        exhaustedAlert: 'Your organization is out of credits.',
        exhaustedPrimary: 'Contact us',
        exhaustedSecondary: 'Save as draft',
        raceLead: 'Your organization needs credits to publish. Nothing you entered is lost.',
        raceAlert: 'The last credit was used by another publish. Your event was saved as a draft.',
        raceSecondary: 'Back to draft',
        rowEvent: 'Event',
        valueDraft: 'Draft',
        errorRejected: 'Could not publish. No credit was used. Try again.',
        errorUnknown: 'We could not confirm the result of publishing. Your event and credits are being reloaded. Check the status and try again.',
        creditLoadError: 'Could not load credit details. If you publish, credits are still checked on the server.',
        retry: 'Try again',
    },
    announcements: {
        // Organizer announcements Story 3 (web, event management › Announcements).
        title: 'Announcements',
        listIntro: 'The organization never sees recipients’ names or emails, only the count.',
        write: 'Write announcement',
        colTitle: 'Title',
        colAudience: 'Recipients',
        colChannels: 'Channels',
        colStatus: 'Status',
        colDate: 'Date',
        channelsLabel: 'Channels: {list}',
        channel: {
            email: 'Email',
            push: 'Push notification',
            inApp: 'In-app',
        },
        status: {
            draft: 'Draft',
            scheduled: 'Scheduled',
            sending: 'Sending',
            sent: 'Sent',
            cancelled: 'Cancelled',
            failed: 'Not sent',
        },
        audience: {
            registered: 'Registered',
            pending_applicants: 'Pending applicants',
            checked_in: 'Checked in',
            attended: 'Attended',
            invitees: 'Invitees',
            followers: 'Followers',
        },
        kind: {
            informational: 'Informational',
            promotional: 'Promotional',
        },
        people: '{count} people',
        people_one: '{count} person',
        draftEdited: 'Last edited {date}',
        emptyTitle: 'No announcements yet',
        emptyBody: 'Tell attendees about a time, venue or agenda change from here. The organization never sees recipients’ names, only the count.',
        loadError: 'Could not load announcements',
        loadErrorBody: 'Check your connection and try again.',
        composeTitle: 'Write announcement',
        fieldSubject: 'Title',
        fieldBody: 'Text',
        bodyCounter: '{used} / {max}',
        audiencesLegend: 'Recipients',
        channelsLegend: 'Channels',
        kindLegend: 'Type',
        opensAfterEvent: 'Opens when the event ends',
        summaryKicker: 'Summary',
        summaryCount: 'Goes to {count} people',
        summaryCount_one: 'Goes to {count} person',
        audienceLoadError: 'Could not load the recipient count',
        noAttendeesTitle: 'This event has no attendees yet',
        noAttendeesBody: 'The recipient count shows here once the first registration arrives. You can write your draft now.',
        preview: 'Preview',
        sendTest: 'Send me a test',
        saveDraft: 'Save draft',
        complianceNote: 'This message is for information about the event. Promotional and advertising content cannot be sent.',
        saved: 'Draft saved.',
        testSent: 'The test message was sent to your account’s email address.',
        previewTitle: 'Preview',
        previewIntro: 'Fixed texts go out in the recipient’s language.',
        previewLanguage: 'Preview language',
        previewChannel: 'Channel',
        pushHint: 'Only the first {count} characters of the text show in the notification.',
        pushHint_one: 'Only the first {count} character of the text shows in the notification.',
        now: 'now',
        unread: 'Unread',
        notOnChannel: 'This announcement will not go out on this channel.',
        previewLoadError: 'Could not load the preview',
        backToEdit: 'Back to editing',
        backToList: 'Back to announcements',
        // Story 4 (T3b): send, schedule, cancel and delivery status.
        sendNow: 'Send now',
        scheduleAction: 'Schedule',
        // User decision 21.10.2026: "Send now" asks for confirmation (not in the design, cancel dialog pattern).
        sendConfirmTitle: 'Send this announcement now?',
        sendConfirmAudience: 'Goes to {count} people.',
        sendConfirmAudience_one: 'Goes to {count} person.',
        sendConfirmReach: 'Goes to {count} people by {channels}.',
        sendConfirmReach_one: 'Goes to {count} person by {channels}.',
        sendConfirmChannels: 'Goes out by {channels}.',
        sendConfirmIrreversible: 'Sending starts right away. Messages that go out cannot be recalled.',
        listAnd: 'and',
        sendConfirmSubmit: 'Send',
        sendConfirmCancel: 'Cancel',
        sendClosedNote: 'Sending announcements is not open yet. Your draft stays saved, and once sending opens you can send or schedule it from here.',
        sendSettingError: 'Could not load sending details.',
        scheduleTitle: 'Schedule announcement',
        scheduleDate: 'Date',
        scheduleTime: 'Time',
        scheduleZone: 'The time follows the event’s time zone, {zone}.',
        scheduleWhen: 'Goes to {count} people from {when}.',
        scheduleWhen_one: 'Goes to {count} person from {when}.',
        scheduleSubmit: 'Schedule',
        scheduleCancel: 'Cancel',
        scheduleRequired: 'Pick a date and time.',
        schedulePast: 'You picked a time in the past. Pick a time after now.',
        scheduleSaveError: 'Could not save the schedule',
        cancelAction: 'Cancel',
        cancelActionLabel: 'Cancel the announcement “{title}”',
        cancelTitle: 'Cancel this announcement?',
        cancelBodyScheduled: 'It will not go out at the scheduled time. This cannot be undone.',
        cancelBodySending: 'Recipients it has not reached yet will not get it. Messages already sent cannot be recalled. This cannot be undone.',
        cancelConfirm: 'Cancel announcement',
        cancelKeep: 'Keep it',
        cancelled: 'Announcement cancelled.',
        colActions: 'Action',
        statusMeta: '{audience} · {date}',
        statusSender: 'Sender: {name}',
        statusTarget: 'people targeted',
        statusQueued: 'messages queued',
        statusSent: 'messages sent',
        statusFailed: 'messages failed',
        statusTotalsLabel: 'Delivery totals',
        statusChannelsLabel: 'Channel breakdown',
        statusColChannel: 'Channel',
        statusColQueued: 'Queued',
        statusColSent: 'Sent',
        statusColFailed: 'Failed',
        statusColQueuedShort: 'Queue',
        statusColSentShort: 'Sent',
        statusColFailedShort: 'Fail',
        statusPrivacyNote: 'Only numbers are shown. Who a failed message belonged to is never shown to the organization. Attendees who turned off push notifications are not counted in that channel.',
        statusChannelsHidden: 'The per-channel split shows once every channel has at least {count} messages. Totals are above.',
        statusChannelsHidden_one: 'The per-channel split shows once every channel has at least {count} message. Totals are above.',
        statusEmptyTitle: 'This announcement has not gone out yet',
        statusEmptyBody: 'Sending starts at the scheduled time, and the numbers show up here.',
        statusLoadError: 'Could not load delivery status',
        statusNotFound: 'Announcement not found',
        statusForbidden: 'You do not have access to this announcement’s status',
        listSubtitle: 'Event information sent to attendees by email and mobile notification.',
        emptyBodyOrg: 'Time, venue or agenda changes are announced to registered attendees here.',
        rowView: 'View',
        rowEditLabel: 'Edit the draft “{title}”',
        rowViewLabel: 'View the announcement “{title}”',
        bodyHelp: 'Attendees are addressed informally.',
        audienceGroupsLegend: 'Recipient groups',
        summaryLine: '{audience} · {channels}',
        statusSectionChannels: 'Channels',
    },
    ceiling: {
        warnLead: 'This event is at {percent}% of its {ceiling}-participant limit.',
        warnRest: 'New registrations close at the limit. A warning email has also been sent.',
        fullLead: 'The {ceiling}-participant limit has been reached.',
        fullRest: 'New registrations, invitation acceptances and application approvals are closed. Check-in stays open for registered participants. Sending invites, bulk actions and industry/seniority breakdowns are locked.',
        trialLine: 'Published with a welcome credit',
        cta: 'Contact us about Enterprise',
        progressLabel: 'Participant limit usage',
        errorLine: 'Could not load plan status. The limit and locks are not shown. Actions are still checked on the server.',
        retry: 'Try again',
        lockInvite: 'Limit reached. New invites cannot be sent.',
        lockBulk: 'Bulk actions lock once the limit is reached. You can still manage rows one at a time.',
        lockBreakdown: 'Breakdowns lock once the limit is reached. Registration counts, hourly and ticket breakdowns stay open.',
        exemptLine: 'This event follows the earlier rule. Locks do not apply until {date}.',
    },
    analytics: {
        kicker: 'Analytics',
        title: 'Analytics',
        subtitle: 'Numbers are live; recomputed every time the page opens.',
        privacyNote: 'Analytics only shows aggregate numbers. No breakdown includes names, emails or phone numbers. Breakdowns are calculated from the people on the attendee list.',
        kpiRegistered: 'Registrations',
        kpiRegisteredDelta: '+{count} in the last 7 days',
        kpiApplications: 'Pending applications',
        kpiInvitations: 'Invitations',
        kpiInvitationsDelta: '{accepted} accepted',
        kpiCheckedIn: 'Checked in',
        kpiCheckedInDelta: '{percent}% of all registrations',
        kpiCancelled: 'Cancelled',
        kpiCapacity: 'Remaining capacity',
        kpiCapacityUnlimited: 'Unlimited capacity',
        hourlyTitle: 'Registrations by hour',
        hourlyHint: 'Hours are in the event timezone ({timezone}).',
        hourlyEmpty: 'No registrations yet; the distribution starts with the first one.',
        hourLabel: '{hour}:00',
        hourBarLabel: '{hour}:00 · {count} registrations',
        hourBarLabel_one: '{hour}:00 · {count} registration',
        sectorTitle: 'Sector breakdown',
        seniorityTitle: 'Seniority breakdown',
        ticketTitle: 'Ticket type breakdown',
        unknownBucket: 'Not specified',
        percentValue: '{percent}%',
        otherBucket: 'Other (below threshold)',
        suppressedTitle: 'Breakdowns are not open yet',
        suppressedBody: "Breakdowns appear once at least {min} people are on the attendee list. With fewer people the percentages would reveal a single person's profile data.",
        breakdownBaseHint: 'Breakdowns are based on the {count} people on the attendee list.',
        breakdownBaseHint_one: 'Breakdowns are based on the {count} person on the attendee list.',
        bucketSuppressedTitle: 'This breakdown is hidden',
        bucketSuppressedBody: 'The remaining groups do not meet the {min} person threshold. Publishing them would expose a single person sector or seniority.',
        emptyTitle: 'Analytics starts with the first registration',
        emptyBody: 'Once your event is published and registrations arrive, the counts and breakdowns fill in here.',
        emptyStep1: 'Publish the event',
        emptyStep2: 'Send invitations or share the link',
        emptyStep3: 'Follow registrations here',
        emptyAction: 'Send invitations',
        loadError: 'Analytics could not be loaded.',
        report: {
            kicker: 'POST-EVENT REPORT',
            kickerLive: 'INTERIM REPORT · LIVE',
            badgeFinal: 'Final',
            badgePreliminary: 'Preliminary',
            organizedBy: 'Organized by {name}',
            metaDate: 'DATE',
            metaFormat: 'FORMAT',
            metaAccess: 'ACCESS',
            metaGenerated: 'GENERATED',
            statusFinal: 'The report was frozen on {date}. The numbers won’t change.',
            statusPreliminary: 'The report updates until it becomes final on {date}. Last updated {updated}.',
            sectionAttendance: 'Attendance',
            sectionCheckIn: 'Check-in',
            sectionMeetings: 'Meeting requests',
            sectionSurvey: 'Survey summary',
            registered: 'Confirmed registrations',
            cancelled: 'Cancellations',
            applicationsReceived: 'Applications received',
            applicationsApproved: 'Approved',
            applicationsApprovedSub: '{rate} of applications',
            invitationsSent: 'Invitations sent',
            invitationsAccepted: 'Invitations accepted',
            fillRate: 'Capacity filled',
            fillRateSub: '{registered} of {limit} capacity',
            checkedIn: 'Checked in',
            attendanceRate: 'Attendance rate',
            attendanceRateSub: '{checkedIn} of {registered} confirmed',
            noShows: 'No-shows',
            attendanceRateLive: 'Attendance so far',
            noShowsLive: 'Not here yet',
            hourlyTitle: 'Check-ins by hour',
            meetingsTotal: 'Total requests',
            meetingsAccepted: 'Accepted',
            meetingsUnresolved: 'Unresolved',
            meetingsUnresolvedSub: 'Declined, unanswered or withdrawn',
            meetingsParticipants: 'Attendees who requested',
            meetingsParticipantsSub: 'attendees',
            meetingsHiddenNote: 'A number derived from one below {min} isn’t shown.',
            meetingsBothHiddenNote: 'Accepted and unresolved counts aren’t shown because at least one is below {min}.',
            fewerThanMin: 'Fewer than {min}',
            countHidden: 'Not shown',
            summaryFewResponses: 'Too few responses, not shown',
            surveyNone: 'No survey was run for this event.',
            surveyResponses: 'Responses',
            surveyResponseRateSub: '{rate} response rate',
            surveySatisfaction: 'Overall satisfaction',
            surveySatisfactionSub: 'out of 5',
            surveyRecommendation: 'Recommendation score',
            surveyRecommendationSub: 'from −100 to +100',
            surveyAllResults: 'All results',
            overviewLabel: 'Overview',
            downloadPdf: 'Download PDF',
            downloadingPdf: 'Preparing PDF',
            share: 'Share',
            pdfError: 'Couldn’t create the PDF.',
            retry: 'Try again',
            loading: 'Loading report',
            loadErrorTitle: 'Couldn’t load the report',
            loadErrorBody: 'Check your connection and try again.',
            notEndedTitle: 'This event hasn’t ended yet',
            draftTitle: 'This event is a draft',
            draftBody: 'The report opens when you publish the event and updates live.',
            draftAction: 'Back to setup',
            refresh: 'Refresh',
            refreshing: 'Refreshing',
            notEndedBody: 'The report opens on {date}.',
            cancelledTitle: 'This event was canceled',
            cancelledBody: 'Reports aren’t created for canceled events.',
            hourColumn: 'Time',
            countColumn: 'Check-ins',
            thresholdNote: 'To protect attendees’ privacy, numbers below {min} aren’t shown.',
            pdfGenerated: 'Generated {date}',
            pdfPage: 'Page {page} of {total}',
            pdfLiveStatus: 'Live as of {date}. Numbers update until the event ends.',
            fileName: 'event-report',
            badgeLive: 'Live',
            metaUpdated: 'LAST UPDATED',
            statusLiveBefore: 'This report is live. Numbers update until the event ends. Last updated {time}.',
            statusLiveDuring: 'The event is underway. Numbers update live. Last updated {time}.',
            privacyNote: 'The report is made of totals. It contains no names, meeting pairs or announcement content.',
            checkInStartsOnEventDay: 'Check-in starts on event day.',
            checkInOngoing: 'Check-in is ongoing. The {hour} bar is counted up to {time}.',
            offlineNotice: 'You’re offline. Showing numbers from {time}.',
            listAnd: ' and ',
            live: {
                title: 'Live summary',
                registeredDelta: '+{count} in the last 7 days',
                registeredDelta_one: '+{count} in the last 7 days',
                checkedIn: 'Check-in',
                checkInPending: 'Starts on event day',
                checkInSub: '{rate} of {registered} confirmed',
                invitationsSub: '{accepted} accepted',
                capacity: 'Spots left',
                capacitySub: 'of {capacity} capacity',
                capacityUnlimited: 'Unlimited',
                pending: 'Pending applications',
                pendingSub: 'Waiting for your review',
                emptyTitle: 'No registrations or invitations yet',
                emptyBody: 'The report is live. Numbers appear here with the first registration.',
                emptyStepShare: 'Share your event link',
                emptyStepInvite: 'Send invitations',
                emptyStepFill: 'The live summary and timeline fill in as people register',
                emptyStepEnd: 'When the event ends, the report becomes preliminary',
                emptyActionShare: 'Share event',
                emptyActionInvite: 'Send invitations',
                linkCopied: 'Link copied',
            },
            preEvent: {
                title: 'Meeting before the event',
                acceptanceRate: 'Acceptance rate',
                acceptanceRateSub: '{accepted} of {count} requests',
                acceptanceRateSub_one: '{accepted} of {count} request',
                sentBefore: 'Sent before the event day',
                sentBeforeSub: '{sent} of {count} requests',
                sentBeforeSub_one: '{sent} of {count} request',
                notStarted: 'The event hasn’t started',
                leadTitle: 'When requests were sent',
                leadDays14Plus: '14+ days before',
                leadDays7To13: '7–13 days before',
                leadDays1To6: '1–6 days before',
                leadEventDay: 'On the day and during',
                leadEventDayPending: 'Starts on event day',
                leadValue: '{value} · {share}',
                leadHiddenNote: '{count} groups are below {min} or could be worked out from the total, so their shares aren’t shown.',
                leadHiddenNote_one: 'One group is below {min}, so its share isn’t shown.',
                hourlyTitle: 'Accepted meetings by hour',
                peakCaption: 'Busiest hour is {range} with {count} meetings (orange bar).',
                peakCaption_one: 'Busiest hour is {range} with {count} meeting (orange bar).',
                countColumn: 'Meetings',
            },
            timeline: {
                title: 'Registration timeline',
                daily: 'Registrations by day',
                weekly: 'Registrations by week',
                weekLabel: 'Week {week}',
                weekLabelShort: 'W{week}',
                weeklyCaption: '{range} · {count} weeks. Shown by week because registration was open for more than 6 weeks.',
                weeklyCaption_one: '{range} · {count} week. Shown by week because registration was open for more than 6 weeks.',
                dailyCaption: '{range} · by day',
                markLegend: 'Day an announcement was sent. A number means several that day.',
                markSuffix: '{count} announcements',
                markSuffix_one: '{count} announcement',
                honestyNote: 'A marker shows the day an announcement was sent. It doesn’t prove an effect on registrations.',
                noAnnouncements: 'No announcements were sent for this event.',
                empty: 'No registrations yet. The timeline starts with the first one.',
                dayColumn: 'Day',
                weekColumn: 'Week',
                countColumn: 'Registrations',
                hourlyTitle: 'Registrations by time of day',
            },
            profile: {
                title: 'Attendee profile',
                heading: 'Industry and seniority',
                sector: 'Industry',
                seniority: 'Seniority',
                tickets: 'Capacity by ticket type',
                other: 'Other',
                otherNote: 'Groups below {min} are shown under “Other”.',
                suppressed: 'The attendee list is below {min} right now. Industry and seniority appear once it reaches {min}.',
                bucketHidden: 'This breakdown isn’t shown because the remaining groups are below the threshold.',
            },
            comparison: {
                title: 'Compared with previous events',
                firstEvent: 'This is your first event, so there’s nothing to compare with yet.',
                colMetric: 'METRIC',
                colThis: 'THIS EVENT',
                colAverage: 'PREVIOUS AVERAGE',
                colDelta: 'DIFFERENCE',
                registered: 'Confirmed registrations',
                attendanceRate: 'Check-in rate',
                satisfaction: 'Satisfaction',
                noSurvey: 'No survey',
                noCheckIn: 'No check-in',
                points: '{delta} pp',
                satisfactionBasis: 'from {count} events',
                satisfactionBasis_one: 'from {count} event',
                basisNote: 'Based on the average of your last {count} events.',
                basisNote_one: 'Based on your last event.',
                rateExcludedNote: '{count} events without check-in were left out of the check-in rate average.',
                rateExcludedNote_one: '{count} event without check-in was left out of the check-in rate average.',
                repeatTitle: 'Returning attendees',
                repeatSub: '{rate} · checked in at an earlier event of yours',
                repeatRateHidden: 'Share not shown',
                repeatHidden: 'Not shown',
                repeatCurrentUnused: 'Returning attendees aren’t counted because check-in wasn’t used at this event.',
                repeatPreviousUnused: 'Returning attendees aren’t counted because check-in wasn’t used at your earlier events.',
                repeatExcludedNote: '{count} earlier events without check-in were left out of the returning count.',
                repeatExcludedNote_one: '{count} earlier event without check-in was left out of the returning count.',
            },
            supplement: {
                note: 'This report became final on {frozen}. {sections} were added on {date}.',
                tag: 'Added section · {date}',
                sectionRegistrations: '§6',
                sectionProfile: '§7',
                sectionMeetingTiming: '“Meeting before the event” in §4',
                sectionComparison: '§8',
            },
        },
    },
    speakerPage: {
        kicker: 'Speaker',
        breadcrumbLabel: 'Page path',
        breadcrumbEvents: 'Events',
        breadcrumbSpeakers: 'Speakers',
        aboutTitle: 'About',
        sessionsTitle: 'Session at this event',
        sessionsEmpty: 'Session details are not published yet.',
        pastTalksTitle: 'Past talks',
        pastTalksEmpty: 'First talk on the platform.',
        cardTitle: 'Business card',
        cardBody: 'These details are the public speaker card published on the event page; they contain no personal contact information.',
        cardDownload: 'Download as vCard',
        cardWalletNote: 'Adding to the wallet opens after a mutual approval by scan.',
        linksTitle: 'Links',
        linkWebsite: 'Website',
        linkLinkedin: 'LinkedIn',
        linkX: 'X',
        backToEvent: 'Back to the event page',
        notFoundTitle: 'Speaker not found',
        fieldName: 'Name',
        fieldTitle: 'Title',
        fieldCompany: 'Company',
    },
    program: {
        linkConsentNote: 'The link is not established until the user approves it. Until then the event page shows the details you entered.',
        speakersKicker: 'Speakers',
        speakersLabel: 'Speakers · {count}',
        speakersEmptyTitle: 'No speakers yet',
        speakersEmptyBody: 'Add a speaker; the public event page and the attendee agenda update automatically.',
        addSpeaker: 'Add speaker',
        fullNameLabel: 'Full name',
        jobTitleLabel: 'Title',
        companyLabel: 'Company',
        bioLabel: 'Bio',
        websiteLabel: 'Website',
        linkedinLabel: 'LinkedIn',
        xUrlLabel: 'X (Twitter)',
        photoLabel: 'Photo',
        photoUploading: 'Uploading photo…',
        photoUploaded: 'Photo uploaded.',
        saveSpeaker: 'Save speaker',
        statusDraft: 'Draft',
        moveUp: 'Move up',
        moveDown: 'Move down',
        speakerSessions: 'Sessions: {sessions}',
        deleteSpeakerTitle: 'Delete speaker',
        deleteSpeakerBody: 'This speaker will be removed from the event page. Are you sure?',
        deleteSpeakerBoundBody: 'This speaker is linked to {count} sessions ({sessions}). Deleting also removes them from those sessions.',
        deleteSpeakerBoundBody_one: 'This speaker is linked to {count} session ({sessions}). Deleting also removes them from that session.',
        linkCta: 'Link to user',
        linkEmailLabel: 'User’s email address',
        linkEmailHint: 'The request is emailed to the user; the response does not reveal whether an account exists.',
        linkSend: 'Send link request',
        linkPending: 'Awaiting approval',
        linkLinked: 'Linked profile',
        linkWithdraw: 'Withdraw request',
        linkRemove: 'Remove link',
        agendaKicker: 'Agenda',
        agendaLabel: 'Programme',
        pageSub: 'Speakers and program shown on the event page.',
        colDay: 'Day',
        colTime: 'Time',
        colSession: 'Session',
        noSpeakers: 'No speakers',
        editSpeakerTitle: 'Edit speaker',
        editSessionTitle: 'Edit session',
        agendaEmptyTitle: 'The programme is empty',
        agendaEmptyBody: 'Add a session; the day view is generated automatically.',
        addSession: 'Add session',
        sessionTitleLabel: 'Title',
        sessionDescriptionLabel: 'Description',
        sessionTypeLabel: 'Session type',
        typeTalk: 'Talk',
        typePanel: 'Panel',
        typeWorkshop: 'Workshop',
        typeBreak: 'Break',
        typeNetworking: 'Networking',
        typeOther: 'Other',
        startsAtLabel: 'Starts',
        endsAtLabel: 'Ends',
        locationLabel: 'Room',
        sessionSpeakersLabel: 'Speakers',
        saveSession: 'Save session',
        deleteSessionTitle: 'Delete session',
        deleteSessionBody: 'This session will be removed from the agenda. Are you sure?',
        endBeforeStart: 'End time cannot be before the start time.',
        outsideEventRange: 'The session must fall within the event dates ({start} to {end}).',
        outsideEventBadge: 'Outside the event dates',
        outsideEventNote: '{count} sessions fall outside the event dates and are hidden from the event page. Edit their times or delete them.',
        outsideEventNote_one: 'This session falls outside the event dates and is hidden from the event page. Edit its time or delete it.',
        conflictTitle: 'Time conflict',
        conflictBody: 'Another session overlaps in the same room. If you confirm, it is saved anyway and the attendee agenda shows both.',
        conflictConfirm: 'Save anyway',
        conflictBox: '“{a}” and “{b}” overlap in the same room. The attendee agenda shows this conflict.',
        dayLabel: 'Day {index} · {date}',
        timezoneNote: 'Times are shown in the event time zone ({timezone}).',
    },
    speakerLinks: {
        title: 'Speaker links',
        intro: 'When an organizer wants to link you as a speaker profile at their event, the request appears here. Your profile is never published as a speaker without your approval.',
        emptyTitle: 'No pending link requests',
        emptyBody: 'When an organizer wants to link you as a speaker, the request is listed here and you are notified by email.',
        statusPending: 'Awaiting approval',
        statusLinked: 'Linked',
        approve: 'Approve',
        reject: 'Reject',
        remove: 'Remove link',
        removeTitle: 'Remove speaker link for {event}',
        removeBody: 'Your profile is detached from the speaker card of this event. The card stays visible with the details the organizer entered. To link again, the organizer has to send a new request.',
        visibilityNote: 'Being a speaker does not put you on the attendee list. Only people who register for the event are on the attendee list.',
        mobileTitle: 'Waiting for approval',
        reviewBadge: 'Speaker link approval',
        reviewTitle: 'You are about to be listed as a speaker at an event',
        reviewBody: 'If you do not approve, your name and profile will not appear on the event page.',
        rowEvent: 'Event',
        rowSession: 'Session',
        rowDate: 'Date',
        rowListedName: 'Listed name',
        rowRequestedAt: 'Requested on',
        organizerVerified: 'Verified organization',
        organizerUnverified: 'Unverified organization',
        consequenceNote: 'If you accept, your profile shows a "speaker" badge and attendees see your session. Your place on the attendee list does not change.',
        listIntro: 'Events where you are listed as a speaker. If you remove the link your name leaves the event page and the organizer is notified.',
        reviewCta: 'Review',
        doneKicker: 'Link established',
        donePoster: 'You are approved as a speaker',
        doneRoleTag: 'Speaker',
        doneCta: 'See my links',
        rejectedNotice: 'You rejected the link; your name will not appear as a speaker on the event page.',
        removedNotice: 'The link was removed and the organizer was notified.',
        sessionUnknown: 'No session information is published for this event.',
    },
    admin: {
        brandSuffix: 'Admin',
        queueBadge: '{count} items in the queue',
        queueBadge_one: '{count} item in the queue',
        account: 'platform@{appDomain}',
        nav: {
            heading: 'Administration',
            overview: 'Overview',
            users: 'Users',
            moderation: 'Moderation',
            reports: 'Reports',
            emailDeliveries: 'Email deliveries',
            staff: 'Staff',
            salesInquiries: 'Sales leads',
            organizations: 'Organizations',
            referralCodes: 'Referral codes',
        },
        sales: {
            title: 'Sales leads',
            total: '{count} applications in total',
            total_one: '{count} application in total',
            tabsLabel: 'Status filter',
            all: 'All',
            statuses: {
                new: 'New',
                contacted: 'Contacted',
                won: 'Won',
                lost: 'Lost',
                spam: 'Spam',
            },
            eventTypes: {
                conference: 'Conference',
                summit: 'Summit',
                meetup: 'Meetup',
                fair: 'Trade fair',
                internal: 'Internal event',
                training: 'Training and workshop',
                other: 'Other',
            },
            topics: {
                credit_topup: 'Credit request',
                enterprise_contract: 'Contract request',
                support: 'Support request',
                other: 'Organization request',
            },
            packages: {
                single: 'Single',
                pack_12: '12-pack',
                enterprise: 'Enterprise',
            },
            searchLabel: 'Company name',
            searchPlaceholder: 'Search by company name',
            auditNote: 'Opening details accesses personal data and is logged under your name.',
            table: {
                date: 'Received',
                company: 'Company',
                type: 'Type',
                attendees: 'Est. attendees',
                estimatedDate: 'Est. date',
                contact: 'Contact person',
                status: 'Status',
                organization: 'Linked organization',
            },
            hiddenColumns: '{count} columns hidden',
            hiddenColumns_one: '{count} column hidden',
            anonymized: 'anonymized',
            range: '{from}–{to} of {total}',
            loading: 'Loading applications',
            emptyTitle: 'No applications yet',
            emptyBody: 'Applications come from the organizations page on the website and the contact form in the mobile app.',
            filterEmptyTitle: 'No applications match this filter',
            filterEmptyBoth: 'No company containing “{q}” is in “{status}”.',
            filterEmptyStatus: 'No applications are in “{status}”.',
            filterEmptyQuery: 'No company contains “{q}”.',
            clearFilter: 'Clear filter',
            errorTitle: 'Applications could not be loaded',
            errorBody: 'The server could not be reached. No application was deleted. Try again to load the list.',
            retry: 'Try again',
            detail: {
                label: 'Sales lead details',
                kicker: 'Sales lead · {date}',
                close: 'Close',
                contactRole: 'Contact person',
                contactLinked: 'Contact person · linked to an account',
                fields: {
                    company: 'Company name',
                    eventType: 'Event type',
                    attendees: 'Estimated attendees',
                    estimatedDate: 'Estimated date',
                    email: 'Email',
                    phone: 'Phone',
                    note: 'Note',
                    locale: 'Application language',
                    createdAt: 'Received on',
                    linkedUser: 'Linked account',
                    notice: 'Privacy notice',
                    organization: 'Linked organization',
                    referralCode: 'Referral code',
                },
                referral: {
                    none: 'Not entered',
                    statuses: {
                        valid: 'Active',
                        not_found: 'Not found',
                        inactive: 'Inactive',
                        not_started: 'Not started',
                        expired: 'Expired',
                        cap_reached: 'Limit reached',
                        already_used: 'Used by this org',
                        no_organization: 'No organization',
                    },
                    usedOn: 'Used by this org · {date}',
                    reasons: {
                        not_found: 'This code doesn’t exist. No gift is added.',
                        inactive: 'The code is turned off. No gift is added.',
                        not_started: 'The code hasn’t started yet.',
                        notStartedOn: 'The code starts on {date}.',
                        expired: 'The code has expired.',
                        expiredOn: 'The code expired on {date}.',
                        cap_reached: 'The code has reached its usage limit.',
                        capOf: 'Code usage is {used} / {cap}.',
                        already_used: 'This code was already used by {org}. Each organization can use a code once.',
                        usedOn: 'This code was used by {org} on {date}. Each organization can use a code once.',
                        no_organization: 'The lead isn’t linked to an organization. No gift is added.',
                    },
                },
                notProvided: 'not provided',
                copy: 'Copy',
                copied: 'Copied',
                linkedYes: 'Yes · {name}',
                linkedNo: 'No',
                removedAccount: 'Deleted account',
                noticeValue: 'Version {version} · accepted {date}',
                organizationNone: 'None',
                statusLabel: 'Status',
                openOrganization: 'Create organization from this lead',
                historyTitle: 'History · audit trail',
                historyViewed: 'viewed the details',
                historyStatus: 'changed the status · {from} → {to}',
                historyCreated: 'recorded the application · form, {locale}',
                system: 'System',
                saving: 'Saving status…',
                saveError: 'The status could not be saved. The previous status ({status}) was restored.',
                notFound: 'This application was not found. It may have been deleted or the link is wrong.',
                backToList: 'Back to the list',
                loadError: 'The application could not be loaded. The server could not be reached.',
                loading: 'Loading application',
                anonymizedBanner: 'Anonymized on {date} because it did not become a customer.',
            },
        },
        orgs: {
            title: 'Organizations',
            total: '{count} organizations in total',
            total_one: '{count} organization in total',
            create: 'Create organization',
            searchLabel: 'Organization name',
            searchPlaceholder: 'Search by organization name',
            auditNote: 'Creating organizations and credit actions are logged under your name.',
            table: {
                organization: 'Organization',
                verification: 'Verification',
                owner: 'Owner',
                credits: 'Credits left',
                contractEnd: 'Contract ends',
                source: 'Source',
                createdAt: 'Created',
            },
            verification: {
                verified: 'Verified',
                unverified: 'Not verified',
                pending: 'Verification pending',
                rejected: 'Verification rejected',
            },
            sources: {
                panel: 'Panel',
                self_serve: 'Self-created, legacy',
            },
            owner: {
                invited: 'Owner invite pending',
                expired: 'Owner invite expired',
                declined: 'Owner invite declined',
                revoked: 'Owner invite cancelled',
                none: 'No owner',
                removedAccount: 'Deleted account',
            },
            loading: 'Loading organizations',
            emptyTitle: 'No organizations yet',
            emptyBody: 'Organizations are created only from the panel. Create one here once the sale closes.',
            searchEmptyTitle: 'No organization named “{q}”',
            searchEmptyLead: '{name} is in sales leads.',
            searchEmptyLeadAction: 'Open lead',
            errorTitle: 'Organizations could not be loaded',
            errorBody: 'The server could not be reached. Try again to load the list. Creating an organization works independently of the list.',
            auditLine: 'This action is logged under {name} at the moment it is submitted.',
            cancel: 'Cancel',
            retry: 'Try again',
            optional: '· optional',
            createDialog: {
                title: 'Create organization',
                fromLead: 'Sales lead · {name}',
                name: 'Organization name',
                namePlaceholder: 'Public name of the organization',
                ownerEmail: 'Work email for the organization account',
                ownerEmailPlaceholder: 'name@company.com',
                website: 'Organization website',
                contactEmail: 'Contact email',
                contactEmailPlaceholder: 'contact@company.com',
                lead: 'Related sales lead',
                leadNone: 'Not selected',
                leadOption: '{name} · {date}',
                info: 'The organization will be created as verified · An invite will be sent to the owner · 1 welcome credit will be added',
                submit: 'Create and send invite',
                submitting: 'Creating…',
                nameRequired: 'Organization name is required.',
                nameShort: 'Organization name must be at least 2 characters.',
                emailRequired: 'Owner email is required.',
                emailInvalid: 'Enter a valid email address.',
                websiteInvalid: 'Enter a valid web address.',
                nameClash: 'This name is very close to a verified organization. Enter a different name.',
                nameClashNamed: 'This name is very close to a verified organization: {name}',
                serverError: 'The organization could not be created. Nothing was saved and no invite was sent. Your entries are kept.',
                successTitle: '{name} was created',
                successVerified: 'Organization created as verified',
                successInvited: 'Invite sent',
                successTrial: '1 welcome credit added',
                successAudit: 'Logged under {name} · {date}',
                goDetail: 'Go to organization',
                goGrant: 'Add credits',
            },
            detail: {
                back: 'Organizations',
                openedAt: 'Created {date}',
                openedBy: 'Created by {name}',
                members: '{count} members',
                members_one: '{count} member',
                notFound: 'This organization was not found. It may have been deleted or the link is wrong.',
                backToList: 'Back to organizations',
                loadError: 'Organization details could not be loaded.',
                loading: 'Loading organization details',
                ownerTitle: 'Owner',
                ownerActive: 'Active',
                ownerPendingKicker: 'Owner invite pending',
                ownerExpiredKicker: 'Owner invite expired',
                ownerDeclinedKicker: 'Owner invite declined',
                ownerNoneKicker: 'The organization has no active owner',
                ownerStepsLabel: 'Invite status',
                stepSent: 'Sent',
                stepAccepted: 'Accepted',
                stepDeclined: 'Declined',
                stepExpired: 'Expired',
                invitedTo: 'Invited address',
                sentAt: 'Sent {date}',
                expiresAt: 'Valid until {date}',
                resend: 'Resend invite',
                replace: 'Cancel invite and send to another address',
                inviteNew: 'Send a new owner invite',
                resent: 'The invite was sent again. The old link no longer works.',
                replaced: 'A new owner invite was sent.',
                replaceTitle: 'Send owner invite to another address',
                inviteTitle: 'New owner invite',
                currentEmail: 'Current invite address',
                currentEmailNote: 'The previous invite stops working.',
                inviteInfo: 'Once the invited person accepts, they become the owner of the organization.',
                sendInvite: 'Send invite',
                sending: 'Sending…',
                actionError: 'The action could not be completed. The owner invite did not change.',
            },
            reports: {
                title: 'Event reports',
                colEvent: 'EVENT',
                colDate: 'DATE',
                colRegistered: 'CONFIRMED',
                colRate: 'ATTENDANCE RATE',
                colSatisfaction: 'SATISFACTION',
                colStatus: 'STATUS',
                statusFinal: 'Final',
                statusPreliminary: 'Preliminary',
                fewerThanMin: 'Fewer than {min}',
                hidden: 'Not shown',
                ratePercent: '{rate}%',
                thresholdNote: 'Numbers below {min} are not shown to protect attendee privacy.',
                emptyTitle: 'No ended events',
                emptyBody: 'Reports appear here once one of the organization’s events ends.',
                loading: 'Loading reports',
                loadError: 'Reports could not be loaded.',
                retry: 'Try again',
            },
            postEvent: {
                title: 'After the event',
                colEvent: 'EVENT',
                colDate: 'DATE',
                colCertificate: 'CERTIFICATE',
                colSurvey: 'SURVEY',
                colResponses: 'RESPONSES',
                colAction: 'Action',
                certificateOn: 'On',
                certificateOff: 'Off',
                survey: {
                    none: 'No survey',
                    draft: 'Draft',
                    open: 'Open',
                    closed: 'Closed',
                    disabled_by_staff: 'Turned off by staff',
                },
                surveyOpen: 'Open · {count} days left',
                surveyOpen_one: 'Open · {count} day left',
                fewerThanMin: 'Fewer than {min}',
                noResponses: 'No response count',
                note: 'Staff do not see individual answers. Counts below {min} read “Fewer than {min}”.',
                emptyTitle: 'No ended events',
                emptyBody: 'Certificate and survey status appear here once one of the organization’s events ends.',
                loading: 'Loading after-event status',
                loadError: 'This section could not be loaded.',
                retry: 'Try again',
                close: 'Turn off survey',
                closeAria: 'Turn off the {eventTitle} survey',
                closedNotice: 'The {eventTitle} survey was turned off.',
                dialogTitle: 'Turn off this survey?',
                dialogBody: 'The {eventTitle} survey closes right away. Attendees can no longer respond. Answers and results already received stay. This cannot be undone.',
                reason: 'Reason',
                reasonRequired: '(required)',
                reasonHint: 'The reason is written to the audit log under your name.',
                reasonEmpty: 'Write a reason for turning it off.',
                reasonShort: 'The reason must be at least {count} characters.',
                reasonShort_one: 'The reason must be at least {count} character.',
                cancel: 'Cancel',
                submit: 'Turn off survey',
                submitting: 'Turning off…',
                serverError: 'The survey could not be turned off. Nothing changed.',
                reloaded: 'The after-event list was reloaded.',
            },
            cover: {
                title: 'Cover image',
                previewAlt: '{name} cover image',
                previewPending: 'The preview is being prepared. It appears here once the image is processed.',
                previewError: 'The preview could not be loaded. The cover can still be removed.',
                none: 'This organization has no cover image.',
                remove: 'Remove cover',
                dialogTitle: 'Remove cover',
                dialogSub: 'The cover on the {name} page is removed and the file is deleted from storage. This cannot be undone.',
                reason: 'Reason',
                reasonHint: 'Briefly explain why you are removing it.',
                warn: 'The organization owner is not notified. The owner can upload a new cover from Brand settings.',
                submit: 'Remove cover',
                submitting: 'Removing…',
                removed: 'Cover removed.',
                changed: 'The cover changed in the meantime. The current version is loaded; check it again before removing.',
                notSet: 'This organization’s cover has already been removed.',
                serverError: 'The cover could not be removed. It is still in place.',
                lastRemovalTitle: 'Last removal',
                lastRemovalBy: 'Removed by {name}',
            },
            ownerCheck: {
                checking: 'Checking address…',
                unavailable: 'This address couldn’t be checked right now.',
                retry: 'Check again',
                warn: 'This address is used by a personal account. The owner invite can’t be accepted with it. Ask the customer for another work address.',
                confirmTitle: 'Send the invite anyway?',
                confirmBody: 'is used by a personal account. The owner invite can’t be accepted with it.',
                confirmAudit: 'The decision is logged in the audit log as {name}.',
                sendAnyway: 'Send anyway',
                change: 'Change address',
            },
        },
        referralCodes: {
            title: 'Referral codes',
            lead: 'An organization enters the code in the credit request form. You add the gift credits when loading credits.',
            create: 'Create code',
            edit: 'Edit',
            editCode: 'Edit code {code}',
            retry: 'Try again',
            tabsLabel: 'Status filter',
            tabs: {
                all: 'All',
                active: 'Active',
                inactive: 'Inactive',
            },
            table: {
                code: 'Code',
                gift: 'Gift',
                valid: 'Valid',
                usage: 'Uses',
                status: 'Status',
                actions: 'Action',
            },
            statuses: {
                active: 'Active',
                inactive: 'Inactive',
                not_started: 'Not started',
                expired: 'Expired',
                cap_reached: 'Limit reached',
            },
            gift: '+{count} credits',
            gift_one: '+{count} credit',
            validOpen: 'No end date',
            validRange: '{from} – {to}',
            validFrom: 'From {date}',
            validUntil: 'Until {date}',
            usage: '{used} / {cap}',
            usageUnlimited: '{used} / unlimited',
            rowHint: 'Click a row to open usage details.',
            loading: 'Loading referral codes',
            emptyTitle: 'No referral codes yet',
            emptyBody: 'Your first code shows up here. When an organization enters it in the credit request form, you add the gift credits while loading credits.',
            filterEmptyTitle: 'No codes in this tab',
            filterEmptyBody: 'Every code is listed under All.',
            showAll: 'Show all',
            listError: 'Couldn’t load referral codes.',
            created: 'created.',
            dialog: {
                createTitle: 'Create code',
                editTitle: 'Edit code',
                usedBy: 'Used by {count} organizations',
                usedBy_one: 'Used by {count} organization',
                notUsed: 'Not used yet',
                code: 'Code',
                gift: 'Gift credits',
                start: 'Start date',
                end: 'End date',
                cap: 'Total use limit',
                note: 'Internal note',
                required: 'required',
                optional: 'optional',
                codeHint: '4–32 characters. Letters and numbers only. Not case-sensitive.',
                giftHint: 'Between 1 and 12.',
                capHint: 'Leave empty for unlimited.',
                noteHint: 'Only staff can see this.',
                savedAs: 'Saved as:',
                codePlaceholder: 'e.g. originn100',
                giftPlaceholder: 'e.g. 1',
                capPlaceholder: 'Unlimited',
                notePlaceholder: 'Where this code came from',
                locked: 'Locked',
                lockNote: 'Code and gift can’t change later, so a used code keeps its meaning.',
                giftInfo: 'A gift credit works like a single credit. It never expires and covers 1,000 attendees per event.',
                active: 'Active',
                activeHint: 'When off, the code isn’t accepted in the credit request form. The code isn’t deleted.',
                audit: 'This action is logged in the audit log under your name.',
                save: 'Save',
                cancel: 'Cancel',
                close: 'Close',
                creating: 'Creating…',
                saving: 'Saving…',
                codeRequired: 'Code is required.',
                codeFormat: 'Use letters and numbers only. 4–32 characters.',
                codeTaken: 'This code already exists. Codes aren’t case-sensitive.',
                giftRange: 'Enter a number between 1 and 12.',
                endBeforeStart: 'End date can’t be before the start date.',
                capInvalid: 'Enter a whole number of 1 or more.',
                createError: 'Couldn’t create the code. Nothing was saved. Your entries are still here.',
                updateError: 'Couldn’t save the code. Nothing changed. Your entries are still here.',
                refresh: 'Refresh list',
            },
            drawer: {
                label: 'Usage details',
                close: 'Close',
                gift: 'Gift',
                valid: 'Valid',
                usage: 'Uses',
                remaining: '{count} uses left',
                remaining_one: '{count} use left',
                noCap: 'No limit',
                note: 'Internal note',
                uses: 'Uses',
                emptyTitle: 'Not used yet',
                emptyBody: 'It shows here once an organization enters this code in the credit request form and its credits are loaded.',
                by: 'Loaded by',
                usedAt: 'Used',
                usesLoading: 'Loading uses',
                usesError: 'Couldn’t load the usage list.',
                removedStaff: 'Deleted user',
                once: 'Each organization can use this code once.',
                notFound: 'This code wasn’t found. The link may be wrong.',
                backToList: 'Back to list',
            },
        },
        write: {
            unknown: 'The result could not be confirmed. The action may have been saved on the server.',
            reloadedLedger: 'The credit summary and ledger were reloaded, check the ledger behind this dialog. Sending the same details again will not create a second entry.',
            reloadedContracts: 'Contracts were reloaded, check the card behind this dialog. Sending the same details again will not create a second entry.',
            reloadedTermination: 'Contracts were reloaded, check the card behind this dialog.',
            reloadedOrganizations: 'The organization list was reloaded. Sending the same details again will not create a second organization.',
            reloadedOwner: 'The owner status was reloaded, check the card.',
            reloadedCover: 'The organization details were reloaded, check the cover card.',
            reloadedStatus: 'The application was reloaded, check its status.',
            reloadedReferralCodes: 'The list was reloaded. Check it before sending again. If the code is listed, don’t create it again.',
        },
        credit: {
            summaryTitle: 'Credit summary',
            remainingLabel: 'credits left',
            remainingLabel_one: 'credit left',
            kinds: {
                trial: 'Welcome',
                single: 'Single',
                pack_12: '12-pack',
            },
            kindDesc: {
                trial: 'Attendee limit {ceiling} · 1 when the organization is created',
                single: 'Attendee limit {ceiling} · does not expire',
                pack_12: 'Attendee limit {ceiling} · 1 year from purchase',
            },
            burnTitle: '12-pack · expiry',
            burnBought: 'Purchased {date}',
            burnExpires: 'Expires {date}',
            burnLeft: '{count} left',
            burnLeft_one: '{count} left',
            grant: 'Add credits',
            exhaustedTitle: 'No credits left',
            exhaustedBody: 'The organization cannot publish new events. Published events are not affected.',
            contractCovers: 'Contract active. Publishing uses no credits until the end of {date}.',
            loading: 'Loading credit summary',
            loadError: 'The credit summary could not be loaded.',
            contract: {
                title: 'Enterprise contract',
                active: 'Active',
                start: 'Start',
                end: 'End',
                reference: 'Reference',
                createdBy: 'Created by',
                terminatedAt: 'Ended {date}',
                none: 'This organization has no contract.',
                define: 'Define contract',
                terminate: 'End contract',
                blockedNote: 'A new contract cannot be defined while one is active.',
                loadError: 'Contracts could not be loaded.',
            },
            ledger: {
                title: 'Credit ledger',
                immutable: 'The ledger cannot be edited or deleted. Corrections are made only by reversal.',
                at: 'Date',
                kind: 'Type',
                amount: 'Amount',
                event: 'Event',
                actor: 'Actor',
                reference: 'Reference',
                reason: 'Reason',
                actions: 'Action',
                kinds: {
                    grant: 'Top-up',
                    consume: 'Spend',
                    consume_contract: 'Under contract',
                    refund: 'Refund',
                    expire: 'Expiry',
                    reversal: 'Reversal',
                },
                reasons: {
                    trialProvision: 'Welcome credit · organization created',
                    consume: 'Published',
                    consume_contract: 'Published under contract',
                    refund: 'Cancelled before any registration',
                    expire: '12-pack expired',
                },
                system: 'System',
                removedActor: 'Deleted account',
                reverse: 'Reverse',
                range: 'Entries {from}–{to} of {total}',
                empty: 'This organization has no ledger activity.',
                onlyTrial: 'No other activity',
                giftTag: 'Gift · {code}',
                loading: 'Loading credit ledger',
                loadError: 'The credit ledger could not be loaded.',
            },
            grantDialog: {
                title: 'Add credits',
                packLabel: 'Package',
                single: 'Single',
                singleDesc: '1 credit · attendee limit {ceiling} per event · does not expire',
                pack: '12-pack',
                packDesc: 'Pack of {credits} · 1 year from purchase',
                giftOnly: 'Gift only',
                giftOnlyDesc: 'No paid credits, gift credits only.',
                giftOnlyOff: 'Needs a valid code.',
                sourceLead: '{org} · from sales lead',
                sourceOrganization: '{org} · from organization details',
                giftBlock: 'Gift code',
                giftValue: '+{count} gift credits',
                giftValue_one: '+{count} gift credit',
                addGift: 'Add the gift',
                giftFromLead: 'The code came from the lead’s credit request form.',
                giftNoCode: 'The lead’s form has no gift code.',
                giftField: 'Gift code',
                giftFieldPlaceholder: 'e.g. originn100',
                giftFieldHint: 'The code is checked when you submit.',
                giftCodeFormat: 'Use letters and numbers only. 4–32 characters.',
                giftCodeNotFound: 'This code wasn’t found. Fix it or leave the field empty.',
                purchaseDay: 'Purchase date',
                purchaseHint: 'Future dates and dates older than 1 year cannot be selected.',
                reference: 'Contract or quote no.',
                reason: 'Reason or note',
                confirmSingle: '1 single credit will be added to {org}. It does not expire.',
                confirmPack: '{count} credits will be added to {org}. Purchased {buy}, they expire at the end of {burn}.',
                confirmPack_one: '{count} credit will be added to {org}. Purchased {buy}, it expires at the end of {burn}.',
                confirmSingleGift: '1 single credit and {count} gift credits ({code}) will be added to {org}. Neither expires.',
                confirmSingleGift_one: '1 single credit and {count} gift credit ({code}) will be added to {org}. Neither expires.',
                confirmPackGift: 'A pack of {credits} and {count} gift credits ({code}) will be added to {org}. The pack expires at the end of {burn}. The gift credits do not expire.',
                confirmPackGift_one: 'A pack of {credits} and {count} gift credit ({code}) will be added to {org}. The pack expires at the end of {burn}. The gift credit does not expire.',
                confirmGiftOnly: 'Only {count} gift credits ({code}) will be added to {org}.',
                confirmGiftOnly_one: 'Only {count} gift credit ({code}) will be added to {org}.',
                confirmTypedGift: 'The gift of code {code} is added in the same step. The code is checked when you submit.',
                confirmTypedGiftOnly: 'Only the gift of code {code} will be added to {org}. The code is checked when you submit.',
                conflict: 'The gift couldn’t be added because {reason}. No credits were added.',
                conflictReasons: {
                    not_found: 'the code wasn’t found',
                    inactive: 'the code was turned off in the meantime',
                    not_started: 'the code hasn’t started yet',
                    expired: 'the code has expired',
                    cap_reached: 'the code reached its limit in the meantime',
                    already_used: 'this organization already used the code',
                },
                continueWithoutGift: 'Continue without gift',
                inquiryMismatch: 'The sales lead doesn’t match this organization or this code. No credits were added. Close the window and reopen the lead.',
                reloadedUnknown: 'The credit ledger was reloaded. Check the ledger before sending again. If the entry is there, don’t add again.',
                openLedger: 'Open credit ledger',
                goOrganization: 'Go to organization',
                successPaidSingle: '{count} single credits added',
                successPaidSingle_one: '{count} single credit added',
                successPaidPack: '{count} credits added · 12-pack',
                successPaidPack_one: '{count} credit added · 12-pack',
                successGift: '{count} gift credits added ({code})',
                successGift_one: '{count} gift credit added ({code})',
                successUsage: 'Code use recorded for {org}',
                successAudit: 'Logged · {name} · {date}',
                submit: 'Add',
                submitting: 'Adding…',
                referenceRequired: 'A reference is required (contract or quote number).',
                purchaseRequired: 'A purchase date is required.',
                purchaseFuture: 'The purchase date cannot be in the future.',
                purchaseOld: 'The purchase date cannot be older than 1 year.',
                serverError: 'Couldn’t add credits. Nothing was saved. Your entries are still here.',
            },
            reverseDialog: {
                title: 'Reverse',
                sub: '{date} · {kind} · added by {name}',
                amount: 'Amount',
                amountHint: 'Used {used} of {total}. Up to {count} can be reversed.',
                amountHint_one: 'Used {used} of {total}. Up to {count} can be reversed.',
                amountHintReversed: 'Used {used} of {total}, {reversed} already reversed. Up to {count} can be reversed.',
                amountHintReversed_one: 'Used {used} of {total}, {reversed} already reversed. Up to {count} can be reversed.',
                reason: 'Reason',
                warn: 'The original entry is kept. A reversal entry is added to the ledger.',
                submit: 'Reverse {count} credits',
                submit_one: 'Reverse {count} credit',
                submitPlain: 'Reverse',
                submitting: 'Saving…',
                amountMax: 'Up to {count} credits can be reversed.',
                amountMax_one: 'Up to {count} credit can be reversed.',
                amountMin: 'The amount must be at least 1.',
                reasonRequired: 'A reason is required.',
                serverError: 'The reversal could not be saved. No ledger entry was added and the balance did not change.',
            },
            contractDialog: {
                title: 'Define enterprise contract',
                start: 'Start',
                end: 'End',
                reference: 'Reference',
                info: 'During the contract this organization publishes unlimited events without using credits, and the attendee limit is lifted on all its events.',
                submit: 'Define contract',
                submitting: 'Saving…',
                startRequired: 'A start date is required.',
                endRequired: 'An end date is required.',
                endBeforeStart: 'The end date cannot be before the start date.',
                serverError: 'The contract could not be saved. The credit status did not change.',
            },
            terminateDialog: {
                title: 'End contract',
                sub: '{reference} · from {start} to {end}',
                reason: 'Reason for ending',
                info: 'After the contract ends, new publishing uses credits. Published events are not affected.',
                submit: 'End contract',
                submitting: 'Ending…',
                reasonRequired: 'A reason for ending is required.',
                serverError: 'The contract could not be ended. It remains active.',
                alreadyTerminated: 'This contract is already ended (an earlier attempt may have gone through). Contracts were reloaded, check the card behind.',
            },
            conflict: 'This request was already sent with different details. Close the dialog and open it again.',
        },
        emailDeliveries: {
            title: 'Email deliveries',
            lead: 'Delivery log for transactional email. Message bodies and recipient addresses are not stored; the recipient appears only as a shortened digest. The log is read only; nothing is resent from here.',
            headline: '{count} records',
            headline_one: '{count} record',
            loading: 'Loading delivery records',
            filters: {
                heading: 'Filters',
                status: 'Status',
                template: 'Template',
                locale: 'Language',
                from: 'From',
                to: 'To',
                all: 'All',
                reset: 'Clear filters',
            },
            statuses: {
                queued: 'Queued',
                sent: 'Sent',
                failed: 'Failed',
                dead: 'Given up',
            },
            table: {
                template: 'Template',
                recipient: 'Recipient',
                locale: 'Language',
                status: 'Status',
                queued: 'Queued at',
                result: 'Result',
                reason: 'Reason',
            },
            recipientHint: 'The address itself is not stored; this is the first 8 characters of its digest.',
            recipientShort: '{prefix}…',
            copyRecipient: 'Copy recipient digest: {prefix}',
            copied: 'Copied',
            attempts: '{count} attempts',
            attempts_one: '{count} attempt',
            notYet: 'Not yet',
            reasonNone: 'The provider gave no reason',
            maskNote: 'Email addresses inside provider text are masked automatically.',
            providerMessageId: 'Provider trace id: {id}',
            relatedUser: 'Open user',
            emptyTitle: 'No delivery matches these filters',
            emptyBody: 'Widen the filters; every transactional email the platform sends appears here.',
            emptyStepReset: 'Clear the filters and return to all records',
            emptyStepRange: 'Widen the date range',
            emptyAllTitle: 'No email has been sent yet',
            emptyAllBody: 'Once the first transactional email goes out (sign-in code, registration confirmation, invitation) a row appears here.',
            templates: {
                otp_login: 'Sign-in code',
                registration_confirmed: 'Registration confirmed',
                event_invitation: 'Event invitation',
                application_result: 'Application result',
                event_updated: 'Event updated',
                event_cancelled: 'Event cancelled',
                team_invitation: 'Team invitation',
                claim_owner_notice: 'Claim notice',
                claim_decision: 'Claim decision',
                claim_transferred: 'Claim transferred',
                claim_info_request: 'Claim info request',
                data_export_ready: 'Data export ready',
                account_deletion_confirmed: 'Account deletion confirmed',
                speaker_link_request: 'Speaker link request',
                invitation_request: 'Invitation request',
                meeting_request_received: 'Meeting request received',
                meeting_request_accepted: 'Meeting request accepted',
                meeting_request_declined: 'Meeting request declined',
                meeting_request_cancelled: 'Meeting request cancelled',
                organization_verification_decision: 'Organization verification decision',
                card_exchange_confirmed: 'Card exchange confirmed',
                follow_up_reminder: 'Follow-up reminder',
                staff_login_code: 'Panel sign-in code',
                staff_invitation: 'Panel invitation',
                email_verification: 'Email verification',
                password_reset: 'Password reset',
                account_exists_notice: 'Account already exists notice',
                event_ceiling_warning: 'Attendee limit warning',
                organizer_announcement: 'Organizer announcement',
                event_report_ready: 'Report ready',
                post_event_message: 'After-event message',
                event_reminder: 'Event reminder',
            },
        },
        staff: {
            title: 'Staff',
            headline: '{total} in total · {admins} with the admin role',
            lead: 'Staff accounts that can sign in to the panel. New staff join by invitation only; the panel has no password reset, so a locked-out colleague is re-invited from here.',
            loading: 'Loading the staff list',
            invite: 'Invite staff',
            table: {
                person: 'Staff member',
                role: 'Role',
                status: 'Status',
                lastLogin: 'Last sign-in',
                createdAt: 'Added',
            },
            roles: { admin: 'Admin', moderator: 'Moderator' },
            statuses: { active: 'Active', suspended: 'Suspended', disabled: 'Closed' },
            passwordNotSet: 'No password yet',
            you: 'You',
            selfNote: 'Your own account',
            neverLoggedIn: 'Never signed in',
            reinvite: 'Re-invite',
            cancelInvitation: 'Cancel invitation',
            suspend: 'Suspend',
            unsuspend: 'Lift suspension',
            invitationSent: 'Invitation sent to {email}. The link is single-use and valid for a {hours}-hour window.',
            invitationCancelled: 'The pending invitation for {name} is cancelled.',
            emptyTitle: 'No staff accounts yet',
            emptyBody: 'Open the first account by invitation. The invited person sets a password from the link in their email and signs in.',
            inviteDialog: {
                kicker: 'New staff account',
                title: 'Invite staff',
                body: 'The account opens right away; the person receives a single-use setup link valid for a {hours}-hour window. They choose their own password.',
                emailLabel: 'Work email',
                emailHint: 'Only addresses on the allowed domains can become staff accounts.',
                nameLabel: 'Display name',
                roleLabel: 'Role',
                roleAdminHint: 'An admin also manages staff accounts.',
                roleModeratorHint: 'A moderator uses the moderation screens only.',
                submit: 'Send invitation',
            },
            reinviteTitle: 'Re-invite: {name}',
            reinviteBody: 'The person receives a new single-use setup link valid for a {hours}-hour window. The previous link stops working; this is the only way back for staff who forgot their password.',
            reinviteDomainNote: 'If the address is outside the allowed domains the invitation cannot be sent; open a new account on an allowed address instead.',
            cancelTitle: 'Cancel invitation: {name}',
            cancelBody: 'The pending setup link stops working. You can re-invite later if needed.',
            suspendTitle: 'Suspend account: {name}',
            suspendBody: 'Open sessions end immediately and any pending invitation is dropped. When you lift the suspension they return with the same password.',
            unsuspendTitle: 'Lift suspension: {name}',
            unsuspendBody: 'The account becomes active again; the person signs in with their existing password, no new invitation needed.',
            auditNote: 'Invitation, suspension and cancellation decisions are written to the audit log.',
        },
        overview: {
            title: 'Overview',
            lead: 'How many events exist, who is joining and what is happening in each city. Every number comes from a live query.',
            statsHeading: 'Platform numbers',
            stats: {
                eventsTotal: 'Events',
                eventsTotalNote: '{count} of them published',
                eventsTotalNote_one: '{count} of them published',
                users: 'Registered users',
                usersNote: 'Deleted and anonymised accounts excluded',
                attendances: 'Registrations',
                attendancesNote: 'Cancelled and rejected ones excluded',
                exchanges: 'Card exchanges',
                exchangesNote: 'Only mutually confirmed ones',
                pendingModeration: 'Moderation waiting',
                pendingModerationNote: 'Open items and items awaiting more info',
                pendingVerifications: 'Organization checks waiting',
                pendingVerificationsNote: 'Submitted and under-review applications',
            },
            citiesTitle: 'Events by city',
            citiesNote: 'Bars show each city’s share of events that have a location; online events have no city.',
            cityEvents: '{count} events',
            cityEvents_one: '{count} event',
            onlineRow: 'Online (no city)',
            citiesEmptyTitle: 'No located events yet',
            citiesEmptyBody: 'Once an in-person or hybrid event opens, the city split shows up here.',
            eventsTitle: 'Events',
            statusFilterLabel: 'Event status',
            statuses: {
                all: 'All',
                draft: 'Draft',
                published: 'Published',
                cancelled: 'Cancelled',
                archived: 'Archived',
            },
            table: {
                event: 'Event',
                city: 'City',
                date: 'Date',
                status: 'Status',
                attendees: 'Attendees',
            },
            noCity: 'Online',
            noOrganization: 'No organization',
            checkedIn: '{count} checked in',
            checkedIn_one: '{count} checked in',
            attendeesCta: 'Attendees',
            eventsEmptyTitle: 'No event matches this filter',
            eventsEmptyBody: 'Widen the status filter; every event on the platform shows in this list.',
            eventsEmptyStepFilter: 'Go back to the “All” filter',
            eventsEmptyStepWait: 'A row appears here once an organizer opens an event',
            eventsEmptyResetCta: 'Reset the filter',
            pageStatus: 'Page {page} of {pageCount}',
            prevPage: 'Previous',
            nextPage: 'Next',
            attendees: {
                kicker: 'Attendee breakdown',
                summary: '{total} registered · {checkedIn} checked in',
                table: {
                    person: 'Person',
                    role: 'Role',
                    status: 'Status',
                    registered: 'Registered',
                    checkedIn: 'Check-in',
                },
                roles: {
                    attendee: 'Attendee',
                    speaker: 'Speaker',
                    staff: 'Staff',
                    sponsor: 'Sponsor',
                },
                statuses: {
                    pending_approval: 'Awaiting approval',
                    registered: 'Registered',
                    waitlisted: 'Waitlisted',
                    rejected: 'Rejected',
                    cancelled: 'Cancelled',
                    attended: 'Attended',
                },
                notCheckedIn: '—',
                emptyTitle: 'Nobody has registered for this event yet',
                emptyBody: 'Attendees are listed here as soon as the first registration arrives.',
                auditNote: 'Opening this breakdown was written to the audit log. This list is for administration and is never shown to attendees.',
            },
        },
        users: {
            title: 'Users',
            headline: '{total} registered · {recent} in the last 7 days',
            searchPlaceholder: 'Email, name or company',
            searchLabel: 'Search users',
            filterLabel: 'User filter',
            filters: { all: 'All', organizers: 'Organizers', flagged: 'Flagged' },
            table: {
                user: 'User',
                role: 'Role',
                events: 'Events',
                wallet: 'Wallet',
                lastSeen: 'Last seen',
                status: 'Status',
            },
            roles: {
                user: 'Attendee',
                organizer: 'Organizer',
                speaker: 'Speaker',
                moderator: 'Moderator',
                admin: 'Admin',
            },
            statuses: {
                active: 'Active',
                pending: 'Pending',
                suspended: 'Suspended',
                flagged: 'Under review',
                deleted: 'Deleted',
            },
            neverSeen: 'Never signed in',
            detail: 'Details',
            detailKicker: 'User record',
            detailFields: {
                email: 'Email',
                role: 'Role',
                status: 'Status',
                events: 'Events',
                wallet: 'Wallet',
                lastSeen: 'Last seen',
                registered: 'Registered',
                suspendedAt: 'Suspended at',
            },
            detailNote: 'Every access to this record is written to the audit log. Only the fields needed for account management are shown here.',
            suspend: 'Suspend',
            unsuspend: 'Lift suspension',
            suspendTitle: 'Suspend account: {name}',
            suspendBody: 'Sessions end immediately and no new content can be created. The reason is written to the audit log and shown in the appeal view.',
            unsuspendTitle: 'Lift suspension: {name}',
            unsuspendBody: 'The account becomes active again. The reason is written to the audit log.',
            reasonLabel: 'Reason (required)',
            reasonPlaceholder: 'Briefly state your decision - it is written to the audit log.',
            reasonTooShort: 'The reason must be at least 20 characters.',
            emptyTitle: 'No users match these criteria',
            emptyBody: 'Clear the search or change the filter; everyone who registers appears here.',
            emptyStepSearch: 'Clear the search',
            emptyStepFilter: 'Return to the "All" filter',
            emptyResetCta: 'Reset search and filter',
        },
        moderation: {
            title: 'Moderation queue',
            lead: 'Claim requests, suspicious events and reported content live in a single queue. Both the organizer and attendee side stay visible while deciding.',
            types: {
                report: 'Report',
                suspicious_event: 'Suspicious event',
                appeal: 'Appeal',
                claim: 'Claim',
            },
            statuses: {
                all: 'All',
                open: 'Open',
                info_requested: 'Info requested',
                resolved: 'Approved',
                dismissed: 'Rejected',
            },
            statusFilterLabel: 'Queue status',
            sources: { user_report: 'Attendee', auto_signal: 'Automatic alert', system: 'System' },
            reasons: {
                fake: 'Fake',
                spam: 'Spam',
                inappropriate: 'Inappropriate content',
                personal_data: 'Personal data violation',
                other: 'Other',
            },
            priorityHigh: 'Priority',
            reportCount: '{count} reports',
            reportCount_one: '{count} report',
            facts: {
                reportCount: 'Reports',
                eventStatus: 'Status',
                eventOrigin: 'Origin',
                attendanceCount: 'Registrations',
                creatorAccountAge: 'Account created',
                hiddenAt: 'Hidden at',
                accountCreatedAt: 'Account created',
                eventCount: 'Events',
                walletCount: 'Wallet',
                suspendedAt: 'Suspended at',
                suspensionReason: 'Suspension reason',
                claimStatus: 'Claim status',
                evidenceCount: 'Evidence',
                claimantName: 'Claimant',
                objectionWindowEndsAt: 'Objection window ends',
                objectedAt: 'Objected at',
            },
            origins: { organizer: 'Organizer', shadow: 'Shadow event' },
            reportNotes: 'Report notes',
            decisionLabel: 'Decision rationale (required)',
            decisionPlaceholder: 'Briefly state your decision. It is sent to both parties.',
            approve: 'Approve',
            reject: 'Reject',
            requestInfo: 'Request more info',
            deciding: 'Saving…',
            auditNote: 'Every decision is written to the audit log with who made it, when, and on what grounds.',
            claimPanel: {
                claimant: 'Claimant',
                founder: 'Shadow event creator',
                reason: 'Claim rationale',
                evidences: 'Evidence',
                evidenceTypes: {
                    corporate_email: 'Corporate email',
                    website_match: 'Event page',
                    document: 'Authorization document (PDF)',
                    other: 'Other evidence',
                },
                windowOpen: 'The objection window is open until {date}. Approval is not possible without the owner’s explicit consent; rejection is always available.',
                windowEnded: 'The objection window ended on {date}; the owner did not respond.',
                consented: 'The shadow owner explicitly consented to the transfer ({date}).',
                objected: 'The shadow owner objected on {date}. Their reason was “{reason}”.',
                infoRequestedSent: 'The information request was emailed to the claimant.',
                noteTooShort: 'The rationale must be at least 10 characters.',
                loadError: 'Could not load the claim details.',
            },
            decidedNote: 'Decided: {decision} · {date}',
            emptyTitle: 'Queue is clear',
            emptyBody: 'There are no pending reports, suspicious events or appeals right now.',
            emptyStepSignals: 'Evaluate automatic alerts',
            emptyStepReports: 'Review the indicators on the reports screen',
            selectPrompt: 'Select an item on the left to review',
        },
        reports: {
            title: 'Reports and abuse',
            lead: 'Spam card requests, harassment reports and bulk-scraping suspicion. Automatic alerts never decide; they only move items to the queue.',
            kpiOpen: 'Open reports',
            kpiOpenNote: '{count} newer than 24 hours',
            kpiAvg: 'Average response',
            kpiAvgValue: '{hours} h',
            kpiAvgNote: 'target 12 hours',
            kpiSuspended: 'Suspended',
            kpiSuspendedNote: 'last 30 days',
            kpiAppeals: 'Appeals',
            kpiAppealsNote: 'under review',
            table: {
                kind: 'Report',
                about: 'About',
                signal: 'Alert',
                source: 'Source',
                status: 'Status',
            },
            review: 'Review',
            dismiss: 'Close',
            dismissTitle: 'Close the report',
            dismissBody: 'The item leaves the queue and the reporter is told the review is finished. The reason is written to the audit log verbatim.',
            signalSummary: '{count} reports · {reason}',
            signalSummary_one: '{count} report · {reason}',
            signalsTitle: 'Automatic alerts',
            runSignals: 'Evaluate alerts',
            runningSignals: 'Evaluating…',
            runResult: '{count} items moved to the queue',
            runResult_one: '{count} item moved to the queue',
            signals: {
                new_account_event_burst: {
                    name: 'New account + high volume',
                    desc: '3+ published events on an account younger than 7 days moves to the queue.',
                },
                report_threshold: {
                    name: 'Report threshold',
                    desc: 'Items with 3+ reports on the same target are raised to high priority.',
                },
                one_sided_request_rate: {
                    name: 'One-sided request rate',
                    desc: 'Hourly request threshold will enqueue once card exchange launches.',
                },
                device_fingerprint_match: {
                    name: 'Device fingerprint match',
                    desc: 'Many accounts on the same device. It raises an alert only, never an automatic decision.',
                },
            },
            signalStates: { active: 'Active', monitor: 'Monitoring' },
            healthTitle: 'Platform health · last 30 days',
            health: {
                encounter_accept_rate: 'Card exchange approval rate',
                meeting_accept_rate: 'Meeting request acceptance rate',
                checkin_completion_rate: 'Check-in completion',
                shadow_claim_rate: 'Shadow event claim rate',
            },
            healthNote: 'If the card exchange approval rate drops, something is wrong in the flow; this ratio is the primary product health indicator.',
            noData: 'No data yet',
            emptyTableTitle: 'No reports yet',
            emptyTableBody: 'Reports are grouped here as they arrive; automatic alerts land in this table too.',
        },
        /* Standalone panel app (`apps/admin`) - same product area as web `/admin`. */
        panel: {
            signIn: {
                title: 'Sign in to the admin panel',
                body: 'Continue with your work email and password. We then send a code to that address.',
                emailLabel: 'Email',
                emailPlaceholder: 'name@{appDomain}',
                passwordLabel: 'Password',
                continueWithPassword: 'Continue',
                sendCode: 'Send code',
                codeTitle: 'Enter the code',
                codeBody: 'We sent a 6-digit code to {email}.',
                codeLabel: 'Verification code',
                verify: 'Sign in',
                changeEmail: 'Change email',
                reenterPassword: 'Enter your password again',
                restricted: 'The panel is open to authorised accounts only.',
                haveInvitation: 'I have an invitation',
            },
            /*
             * FIRST-TIME SETUP — INVITATION ONLY. There is no self-service password
             * reset in the panel; the copy says so instead of pointing people at a
             * recovery path that does not exist.
             */
            setup: {
                title: 'Set your panel password',
                body: 'Use the link from your invitation email. Once you set a password you go straight into the panel.',
                tokenLabel: 'Invitation code',
                tokenHint: 'It is part of the link in your invitation email. Paste it manually if the link was truncated.',
                submit: 'Set password and sign in',
                backToSignIn: 'Back to sign-in',
                emptyTitle: 'An invitation code is required',
                emptyBody: 'The panel has no password reset. If you do not have a code, ask an admin for a new invitation.',
            },
            password: {
                changeTitle: 'Change password',
                changeAction: 'Change password',
                currentLabel: 'Current password',
                newLabel: 'New password',
                repeatLabel: 'New password (again)',
                hint: 'No uppercase or symbol is required; a long sentence is safest.',
                repeatMismatch: 'The two passwords do not match.',
                save: 'Save',
                cancel: 'Cancel',
                close: 'Close',
                saved: 'Your password is updated. You were signed out on your other devices.',
            },
            permission: {
                title: 'You do not have access to this screen',
                body: 'Your account does not carry the permission this screen needs. Ask a panel administrator if you need it.',
                action: 'Go to a screen you can use',
                signOut: 'Sign in with another account',
                surfaceNote: 'You do not have permission for this decision.',
            },
            sessions: {
                action: 'Sessions',
                kicker: 'Account',
                title: 'Open sessions',
                body: 'Panel sessions open with this account. If you see a device you do not recognise, end that session and change your password.',
                loading: 'Loading sessions',
                table: {
                    device: 'Device',
                    time: 'Time',
                    openedAt: 'Opened',
                    lastUsedAt: 'Last used',
                },
                current: 'This device',
                unknownDevice: 'Unknown browser',
                neverUsed: 'Not used yet',
                revoke: 'End',
                revokeAll: 'End all',
                revokeTitle: 'End session',
                revokeBody: 'The panel session on that device closes immediately; signing in again needs the password and a code.',
                revokeAllTitle: 'End all sessions',
                revokeAllBody: 'Every panel session, including this device, closes and you return to the sign-in screen.',
                emptyTitle: 'No open sessions',
                emptyBody: 'This account has no open panel session right now.',
            },
            loading: 'Loading the panel',
            signOut: 'Sign out',
            account: 'Signed in as',
        },
    },
    claims: {
        // Block 8-2: the ownership user surface was removed. The remaining keys are
        // used ONLY by the admin moderation panel (historical claims stay readable
        // and decidable — auditability).
        decisionNoteLabel: 'Decision note',
        statusSubmitted: 'Draft, evidence pending',
        statusEvidenceAdded: 'Evidence added',
        statusOwnerNotified: 'Owner notified',
        statusObjectionWindow: 'Objection window open',
        statusObjected: 'Objected',
        statusApproved: 'Approved',
        statusRejected: 'Rejected',
        statusWithdrawn: 'Withdrawn',
        statusTransferred: 'Transferred',
    },
    errors: {
        INTERNAL_ERROR: 'An unexpected error occurred. Please try again.',
        VALIDATION_FAILED: 'Some of the information is invalid. Please review it.',
        NOT_FOUND: 'The record you are looking for was not found.',
        CONFLICT: 'This action conflicts with the current state.',
        RATE_LIMITED: 'Too many attempts. Please try again later.',
        PAYLOAD_TOO_LARGE: 'The content you sent is too large.',
        UNSUPPORTED_MEDIA_TYPE: 'This file type is not supported.',
        AUDIT_UNAVAILABLE: "The access record can't be written right now, so personal data is withheld. Try again shortly.",
        SERVICE_OVERLOADED: "We're very busy right now. Please try again in a few seconds.",
        UNAUTHENTICATED: 'You need to sign in to continue.',
        TOKEN_EXPIRED: 'Your session expired. Please sign in again.',
        TOKEN_INVALID: 'Your session is invalid. Please sign in again.',
        SESSION_REVOKED: 'Your session was closed. Please sign in again.',
        FORBIDDEN: 'You are not allowed to perform this action.',
        ACCOUNT_SUSPENDED: 'Your account is suspended.',
        ACCOUNT_DELETED: 'This account has been deleted.',
        ACCOUNT_KIND_NOT_ALLOWED: 'This isn’t available for this account type. You attend events with a personal account and manage them with an organization account.',
        STAFF_CREDENTIALS_INVALID: 'That email or password is wrong.',
        STAFF_CODE_INVALID: 'That code is wrong. Try again.',
        STAFF_CODE_NETWORK_CHANGED: 'This code is no longer valid, or your network changed. Enter your password again.',
        STAFF_CODE_EXPIRED: 'That code expired. Request a new one.',
        STAFF_CODE_ATTEMPTS_EXCEEDED: 'Too many wrong attempts. Request a new code.',
        STAFF_INVITATION_INVALID: 'This invite link is not valid. Ask an admin for a new one.',
        STAFF_PASSWORD_MISMATCH: 'Your current password is not correct.',
        STAFF_PASSWORD_REUSED: 'Your new password cannot be the same as the current one.',
        STAFF_ACCOUNT_SUSPENDED: 'Your staff account is suspended.',
        STAFF_EMAIL_TAKEN: 'A staff account already exists for this address.',
        STAFF_EMAIL_DOMAIN_NOT_ALLOWED: 'This address is not in an allowed staff domain.',
        STAFF_UNAUTHENTICATED: 'Sign in with your staff account to continue.',
        STAFF_SESSION_EXPIRED: 'Your panel session expired. Sign in again.',
        STAFF_FORBIDDEN: 'You are not allowed to perform this panel action.',
        CREDENTIALS_INVALID: 'That email or password is wrong.',
        EVENT_NOT_FOUND: 'Event not found.',
        EVENT_NOT_PUBLISHED: 'This event is not published yet.',
        EVENT_CANCELLED: 'This event was cancelled.',
        EVENT_ACCESS_MODE_INVALID: 'This access mode is not valid for this event.',
        EVENT_CAPACITY_EXCEEDED: 'The event is at full capacity.',
        EVENT_ATTENDEE_CEILING_REACHED: 'This event is full.',
        EVENT_REPORT_NOT_READY: 'The report is prepared once the event ends.',
        EVENT_REPORT_EVENT_CANCELLED: 'Reports aren’t created for canceled events.',
        EVENT_REPORT_SPONSOR_FORBIDDEN: 'Event reports are only available to the organizer.',
        CREDIT_EXHAUSTED: 'You have no event credits left to publish. Your published events are not affected. Contact us for new credits or an Enterprise agreement.',
        REFERRAL_CODE_NAME_TAKEN: 'A referral code with this name already exists.',
        REFERRAL_CODE_NOT_FOUND: 'This code wasn’t found. Fix it or leave the field empty.',
        REFERRAL_CODE_INACTIVE: 'The gift couldn’t be added because the code is turned off. No credits were loaded.',
        REFERRAL_CODE_NOT_STARTED: 'The gift couldn’t be added because the code hasn’t started yet. No credits were loaded.',
        REFERRAL_CODE_EXPIRED: 'The gift couldn’t be added because the code has expired. No credits were loaded.',
        REFERRAL_CODE_CAP_REACHED: 'The gift couldn’t be added because the code reached its limit in the meantime. No credits were loaded.',
        REFERRAL_CODE_ALREADY_USED: 'The gift couldn’t be added because this organization already used the code. No credits were loaded.',
        EVENT_INVALID_STATE: 'The event is not in a valid state for this action.',
        EVENT_DATE_IN_PAST: 'You cannot create an event with a past date.',
        EVENT_HAS_ATTENDEES: 'An event with registered attendees cannot be deleted; it can only be cancelled.',
        EVENT_SHADOW_MODE_LOCKED: 'An attendee-created event can only be public.',
        EVENT_ORGANIZATION_REQUIRED: 'This event is not linked to an organization. Public or approval-based publishing requires a verified organization; you can still publish it as invite-only.',
        EVENT_VENUE_GEO_REQUIRED: 'Pick the location on the map.',
        VENUE_SEARCH_UNAVAILABLE: 'Search is unavailable right now. Try again shortly.',
        VENUE_SEARCH_DISABLED: 'Address search is turned off right now. You can save the event without picking a map location.',
        EMAIL_NOT_VERIFIED: 'You must verify your email for this action.',
        TICKET_QUOTA_EXCEEDED: 'This ticket type is sold out.',
        TICKET_CLOSED: 'This ticket type is closed for registration.',
        TICKET_NOT_FOUND: 'Ticket type not found.',
        TICKET_QUOTA_BELOW_RESERVED: 'Quota cannot be set below the current number of registrations.',
        TICKET_CAPACITY_EXCEEDED: 'Total ticket quotas exceed the event capacity.',
        ALREADY_REGISTERED: 'You are already registered for this event.',
        REGISTRATION_NOT_FOUND: 'Registration not found.',
        CHECK_IN_NOT_OPEN: 'Check-in is only available on the event day.',
        CHECK_IN_REGISTRATION_CANCELLED: 'Registration cancelled.',
        CHECK_IN_OTHER_EVENT: 'This code is for another event.',
        EVENT_ALREADY_ENDED: 'This event has ended; your attendance record can no longer be changed.',
        EVENT_ORGANIZER_CANNOT_JOIN: 'You manage this event; organizers cannot register for or apply to their own event.',
        APPLICATION_ALREADY_SUBMITTED: 'You have already applied to this event.',
        APPLICATION_NOT_FOUND: 'Application not found.',
        APPLICATION_ALREADY_DECIDED: 'This application has already been decided.',
        INVITATION_NOT_FOUND: 'Invitation not found.',
        INVITATION_EXPIRED: 'The invitation has expired.',
        INVITATION_REVOKED: 'This invitation was revoked.',
        INVITATION_EMAIL_MISMATCH: 'This invitation was sent to a different email address.',
        INVITATION_EMAIL_IN_PERSONAL_ACCOUNT: 'This address is used by a personal account. Ask your organization to invite another work address.',
        INVITATION_ALREADY_USED: 'This invitation has already been used.',
        CLAIM_INVALID_TRANSITION: 'The ownership claim cannot move to this state.',
        CLAIM_ALREADY_OPEN: 'There is already an open ownership claim for this event.',
        CLAIM_NOT_FOUND: 'Ownership claim not found.',
        CLAIM_EVENT_NOT_CLAIMABLE: 'This event cannot be claimed.',
        CLAIM_SELF_CLAIM: 'You cannot claim an event you created; you already manage it.',
        CLAIM_EVIDENCE_REQUIRED: 'Add at least one piece of evidence before sending the claim.',
        CLAIM_OBJECTION_WINDOW_CLOSED: 'The objection window has closed.',
        CLAIM_OBJECTION_WINDOW_OPEN: 'The objection window is still open; the claim cannot be approved without the founder’s consent.',
        CARD_NOT_FOUND: 'Card not found.',
        ENCOUNTER_CONSENT_REQUIRED: 'Exchanging cards requires both sides to confirm.',
        ENCOUNTER_NOT_FOUND: 'This wallet record was not found.',
        CARD_EXCHANGE_SELF: 'You cannot scan your own card.',
        CARD_EXCHANGE_UNAVAILABLE: 'This exchange is no longer open. Scan the code again.',
        MEDIA_TYPE_NOT_ALLOWED: 'This image type is not supported.',
        MEDIA_TOO_LARGE: 'The image is too large.',
        MEDIA_DIMENSIONS_TOO_SMALL: 'That image is too small. Pick a file that meets the minimum size shown under the field.',
        MEDIA_UPLOAD_INCOMPLETE: 'The image is still being processed. Save again in a few seconds.',
        MEDIA_NOT_FOUND: 'Image not found.',
        MEDIA_IN_USE: 'This image is used on a page. Change or remove it there first.',
        MEDIA_UPLOAD_FAILED: 'The image could not be uploaded. Please try again.',
        CONSENT_REQUIRED: 'You need to give the required consents to continue.',
        OTP_INVALID: 'The code is incorrect. Please check and try again.',
        OTP_EXPIRED: 'The code has expired. Request a new one.',
        OTP_ATTEMPTS_EXCEEDED: 'Too many incorrect attempts. You need to request a new code.',
        OAUTH_TOKEN_INVALID: 'Sign-in could not be verified. Please try again.',
        OAUTH_EMAIL_UNVERIFIED: 'The email of this account is not verified by the provider. Sign in with an email code.',
        OAUTH_PROVIDER_NOT_CONFIGURED: 'This sign-in method is currently unavailable.',
        SESSION_NOT_FOUND: 'Session not found.',
        IDENTITY_NOT_FOUND: 'Linked account not found.',
        IDENTITY_LAST_LOGIN_METHOD: 'You cannot remove your last sign-in method. Add another one first.',
        IDENTITY_ALREADY_LINKED: 'This provider account is linked to another account. Sign in with an email code.',
        TAG_NOT_FOUND: 'Tag not found.',
        TAG_MERGE_INVALID: 'These tags cannot be merged.',
        CARD_LIMIT_EXCEEDED: 'You can create at most {limit} cards.',
        CARD_DEFAULT_REQUIRED: 'You cannot delete your default card. Make another card default first.',
        ORGANIZATION_NOT_FOUND: 'Organization not found.',
        ORGANIZATION_MEMBER_NOT_FOUND: 'Team member not found.',
        ORGANIZATION_MEMBER_EXISTS: 'This person is already a member or has been invited.',
        ORGANIZATION_OWNER_REQUIRED: 'Only the organization owner can perform this action.',
        ORGANIZATION_OWNER_CANNOT_LEAVE: 'The organization owner cannot leave. Transfer ownership to another active member first; after the transfer you become a regular member and can leave.',
        ORGANIZATION_MEMBER_SELF_REMOVE: 'You cannot remove yourself from the team. To leave the organization, use "Leave the organization".',
        ORGANIZATION_COVER_NOT_SET: 'This organization has no cover image.',
        ORGANIZATION_COVER_CHANGED: 'The cover changed in the meantime. Refresh the page and check again.',
        ORGANIZATION_INVITATION_ALREADY_ACCEPTED: 'This invitation was just accepted and the person is now on the team. The list has been refreshed. To remove them, use "Remove" on their row.',
        ORGANIZATION_TRANSFER_TARGET_UNAVAILABLE: 'Ownership cannot be transferred to this member, because their account is no longer in use. Transfer to an active member, or invite one first.',
        ORGANIZATION_MEMBER_PERSONAL_ACCOUNT: 'This address is linked to the organization through a personal account. A personal account cannot hold an organization role or ownership. Invite the person at the work address of their organization account.',
        ORGANIZATION_SELF_SERVE_DISABLED: 'Organizations are now opened by our team after a short call. Contact us to organize your event and we will open your organization as verified and send you an invitation.',
        ORGANIZATION_OWNER_INVITATION_CONFLICT: 'The owner invitation of this organization cannot be changed this way. It has already been accepted or another owner invitation is pending.',
        ORGANIZATION_NAME_CONFLICT: 'This name is too close to the name of a verified organization. Your organization stays registered; to publish, either make the name distinctive or send an organization verification request. The request asks for your work email address, your website and an authorization document.',
        ORGANIZATION_VERIFICATION_ALREADY_OPEN: 'This organization already has an application awaiting a decision.',
        ORGANIZATION_VERIFICATION_REQUEST_NOT_FOUND: 'Verification application not found.',
        ORGANIZATION_VERIFICATION_ALREADY_DECIDED: 'This application has already been decided.',
        ORGANIZATION_VERIFICATION_DOCUMENT_UNAVAILABLE: 'The document is no longer available (its retention period may have expired).',
        SPEAKER_NOT_FOUND: 'Speaker not found.',
        SPEAKER_HAS_SESSIONS: 'This speaker is linked to agenda sessions. Confirm the session links to delete.',
        SPEAKER_LINK_NOT_PENDING: 'There is no pending link request.',
        SPEAKER_ALREADY_LINKED: 'This speaker is already linked to a user.',
        AGENDA_SESSION_NOT_FOUND: 'Session not found.',
        AGENDA_TIME_CONFLICT: 'Another session overlaps in the same room. Confirm to save anyway.',
        AGENDA_SESSION_OUTSIDE_EVENT: 'The session must fall within the event dates.',
        ATTENDEE_LIST_REQUIRES_REGISTRATION: 'The attendee list is open only to people registered for the event.',
        EXPORT_NOT_FOUND: 'Data export request not found.',
        EXPORT_NOT_READY: 'The export is not ready yet. Please try again shortly.',
        EXPORT_LINK_EXPIRED: 'The download link has expired. Create a new export request.',
        EXPORT_LINK_USED: 'This file was already downloaded. Create a new export request.',
        DELETION_ALREADY_REQUESTED: 'You already have a pending account deletion request.',
        DELETION_NOT_REQUESTED: 'You have no pending account deletion request.',
        DELETION_BLOCKED_SOLE_OWNER: 'You are the only owner of an organization. Transfer ownership to someone on the team before deleting your account.',
        USER_NOT_FOUND: 'User not found.',
        SUSPEND_NOT_ALLOWED: 'This account cannot be suspended from the panel.',
        USER_ALREADY_SUSPENDED: 'This account is already suspended.',
        USER_NOT_SUSPENDED: 'This account is not suspended.',
        MODERATION_ITEM_NOT_FOUND: 'Queue item not found.',
        MODERATION_ALREADY_DECIDED: 'This item has already been decided.',
        MODERATION_CLAIM_DECIDED_IN_CLAIM_FLOW: 'Claim requests are decided in the claim decision flow, not from this queue.',
        MEETING_REQUIRES_ATTENDANCE: 'Register for the event to send a meeting request.',
        MEETING_REQUEST_NOT_FOUND: 'Meeting request not found.',
        MEETING_REQUEST_ALREADY_OPEN: 'You already have a request awaiting this person response.',
        MEETING_REQUEST_DECLINED_BEFORE: 'This person already answered your request for this event; you cannot send a new one.',
        MEETING_OPEN_REQUEST_LIMIT_REACHED: 'You have reached your open request limit for this event. Send a new one once a pending request is resolved.',
        MEETING_INVALID_TRANSITION: 'This request is no longer eligible for that action.',
        MEETING_SLOT_CONFLICT: 'That time is no longer available. A new request is needed for another time.',
        MEETING_SLOT_INVALID: 'The proposed time is outside the event window.',
        MEETING_SLOT_NOT_FOUND: 'The selected time does not belong to this request.',
        MEETING_BLOCK_INVALID: 'This person cannot be blocked.',
        MEETING_BLOCKED_BY_YOU: 'You blocked this person. Remove the block in your settings to send a request.',
        MEETING_AREA_NOT_FOUND: 'Meeting area not found.',
        MEETING_AREA_NAME_TAKEN: 'An area with this name already exists for this event.',
        MEETING_AREA_UNAVAILABLE: 'The selected time is outside this area availability window.',
        MEETING_AREA_FULL: 'This area is full at that time. Pick another time or area.',
        MEETING_AREA_CAPACITY_CONFLICT: 'Accepted meetings do not fit this capacity. Raise the capacity or pick another area.',
        MEETING_AREA_WINDOW_CONFLICT: 'Some accepted meetings fall outside this availability window.',
        PLAN_LIMIT_EXCEEDED: 'This event passed the attendee limit of your organization’s plan; this action needs a higher plan.',
        ANNOUNCEMENT_NOT_FOUND: 'Announcement not found.',
        ANNOUNCEMENT_AUDIENCE_UNAVAILABLE: 'The recipient group you chose is not available for this event. Pick a group that can be selected.',
        ANNOUNCEMENT_KIND_DISABLED: 'Promotional announcements are turned off for now. You can send an informational announcement about this event.',
        ANNOUNCEMENT_NOT_EDITABLE: 'This announcement is no longer a draft and cannot be changed. You can write a new one.',
        ANNOUNCEMENT_EVENT_UNAVAILABLE: 'The event is not published, so an announcement cannot be sent.',
        ANNOUNCEMENT_NO_RECIPIENTS: 'Nobody is in the groups you chose right now. Pick another group or try again later.',
        ANNOUNCEMENT_DAILY_LIMIT: 'Your organization sent 10 announcements in the last 24 hours. You can send the next one later.',
        ANNOUNCEMENT_AUDIENCE_LIMIT: 'This group received 2 announcements in the last 24 hours. You can send a new one later.',
        ANNOUNCEMENT_LINK_SHORTENER: 'Shortened links are not allowed. Write the full address of the link.',
        ANNOUNCEMENT_EMAIL_PAUSED: 'Email announcements of your organization are paused right now. You can choose the push channel or contact us.',
        ANNOUNCEMENT_SEND_DISABLED: 'Sending announcements is not open yet. Your draft is saved and you can send it once sending opens.',
        ANNOUNCEMENT_SCHEDULE_TOO_SOON: 'An announcement can be scheduled at least 10 minutes ahead. Pick a later time.',
        ANNOUNCEMENT_SCHEDULE_TOO_LATE: 'An announcement can be scheduled at most 30 days after the event ends. Pick an earlier time.',
        ANNOUNCEMENT_NOT_CANCELLABLE: 'This announcement cannot be cancelled. Only scheduled announcements or ones being sent can be cancelled.',
        ANNOUNCEMENT_UNSUBSCRIBE_INVALID: 'This link is no longer valid. To turn off announcements, sign in and go to Settings › Notifications.',
        ANNOUNCEMENT_UNSUBSCRIBE_UNDO_UNAVAILABLE: 'This link can no longer turn announcements back on. To do that, sign in and go to Settings › Notifications.',
        EMAIL_FEEDBACK_REJECTED: 'The request could not be verified.',
        POST_EVENT_NOT_ELIGIBLE: 'The certificate of attendance and the survey are available to checked-in attendees after the event ends.',
        EVENT_SURVEY_NOT_FOUND: 'This event has no survey.',
        EVENT_SURVEY_EXISTS: 'This event already has a survey.',
        EVENT_SURVEY_CLOSED: 'The survey is closed. Answers can no longer be submitted.',
        EVENT_SURVEY_ALREADY_SUBMITTED: 'You already filled in this survey. Submitted answers can’t be changed.',
        EVENT_SURVEY_ANSWER_INVALID: 'One of the answers doesn’t match this survey. Refresh the page and try again.',
        EVENT_SURVEY_STATE_CONFLICT: 'The survey’s status doesn’t allow this. Refresh the page.',
        EVENT_SURVEY_PUBLISH_WINDOW_CLOSED: 'A survey can be published until 7 days after the event ends.',
        EVENT_SURVEY_QUESTION_LIMIT: 'A survey can have up to 10 questions.',
        EVENT_SURVEY_QUESTION_INVALID: 'The question list has changed. Refresh the page and try again.',
        EVENT_CERTIFICATE_DISABLED: 'This event doesn’t issue certificates of attendance.',
    },
    recommendation: {
        kicker: 'Why this',
        sharedTagsOne: 'Matches your "{sample}" interest.',
        sharedTagsMany: 'You share {count} interests, including "{sample}".',
        sameCity: 'You have attended events in {city} before.',
        startingSoon: 'Starts in {days} days; registration is still open.',
        startingToday: 'Starts today.',
        startingTomorrow: 'Starts tomorrow.',
        speaker: 'Speaking at this event.',
        none: 'Personal recommendations sharpen as your attendance history grows.',
        coAttendance: '{count} people from {event}, where you are registered too, are also registered here.',
        coAttendance_one: '{count} person from {event}, where you are registered too, is also registered here.',
        coAttendanceAttended: '{count} people who were at {event} with you are registered here too.',
        coAttendanceAttended_one: '{count} person who was at {event} with you is registered here too.',
        relatedTags: '"{sample}" is close to your "{relatedTo}" interest.',
        peerProfileBoth: '{count} attendees share your {sector} field and {seniority} level.',
        peerProfileBoth_one: '{count} attendee shares your {sector} field and {seniority} level.',
        peerProfileSector: '{count} attendees work in {sector}, like you.',
        peerProfileSector_one: '{count} attendee works in {sector}, like you.',
        peerProfileSeniority: '{count} attendees are at {seniority} level, like you.',
        peerProfileSeniority_one: '{count} attendee is at {seniority} level, like you.',
        popularAmongPeers: "One of this week's fastest filling events - {count} registered already.",
        sharedHistory: 'You were at {count} events together before.',
        sharedHistory_one: 'You were at {count} event together before.',
        sharedHistoryNamed: 'You were both at {event}.',
        roleSpeaker: 'Speaking at this event - your interests overlap.',
        roleStaff: 'Part of the team running this event.',
        // --- Web: unregistered people do not see the attendee count (user decision 2026-10-30) ---
        countless: {
            coAttendance: 'Attendees of {event}, where you are registered too, are also registered here.',
            coAttendanceAttended: 'People who were at {event} with you are registered here too.',
            peerProfileBoth: 'Popular with people like you in {sector} at {seniority} level.',
            peerProfileSector: 'Popular with people like you in {sector}.',
            peerProfileSeniority: 'Popular with people like you at {seniority} level.',
            popularAmongPeers: "One of this week's fastest filling events.",
        },
        // --- Block 9-2: recommendation surface copy (screens 4 / 3 / 7) ----------
        kickerEvent: 'Why this event',
        kickerPerson: 'Why this person',
        sectionTitle: 'Events recommended for you',
        sectionLead: 'Based on the events you attended and what similar attendees chose.',
        // Users with tags but no attendance history: the claim stays within interests/profile.
        sectionLeadTags: 'Based on your interests and profile - attending events sharpens these with your history too.',
        coldStartTitle: 'Standing out right now',
        coldStartLead: 'You have no attendance history yet. These were picked by topic proximity and how fast they are filling; the list adapts as you attend.',
        peopleTitle: 'People suggested for you',
        peopleLead: 'Attendees matched through shared history and overlapping interests.',
        peopleLeadTags: 'Attendees matched through your interests and profile.',
        peopleColdStartLead: 'You have no shared history yet. These attendees are close to your profile; the list sharpens as you meet people.',
        peopleEmptyTitle: 'No one to suggest yet',
        peopleEmptyBody: 'There are no attendees to recommend to you at this event yet. Completing your interests makes the recommendations more accurate.',
        openEvent: 'Details',
        openPerson: 'View',
        onlyRecommended: 'Only recommended for me',
        resultCount: '{count} recommendations',
        resultCount_one: '{count} recommendation',
        emptyTitle: 'No personal recommendation yet',
        emptyBody: 'Once you register for an event and pick your interests, recommendations appear here with their reason.',
        emptyStepInterests: 'Pick your interests',
        emptyStepInterestsBody: 'Your tags feed topic matching; the first reason comes from there.',
        emptyStepAttend: 'Register for an event',
        emptyStepAttendBody: 'Attendance history is the source of the "who came with you" reason.',
        previewLabel: 'A recommendation looks like this',
        previewTitle: 'Data Engineering Meetup',
        previewMeta: 'Istanbul - In person',
        loadError: 'Recommendations could not be loaded.',
    },
    mobile: {
        /* Product password — sentences only mobile reads (Block C). See TR note. */
        auth: {
            resetStep: 'Reset',
            confirmLost: 'Your sign-up details are no longer on this device. Sign up again with the same email and we will send you a new code.',
            resetLost: 'Your reset details are no longer on this device. Request a new code.',
            missingFields: '{count} fields are missing. Focus moved to the first one.',
            missingFields_one: '{count} field is missing. Focus moved to it.',
            offlineSubmit: 'No connection. The request was not sent; try again once you are online.',
            timeoutSubmit: 'The request timed out. Try again.',
            timeoutCodeSent: 'The request did not respond. The code may have arrived, check your inbox.',
            rateLimitedOver: 'The wait is over. You can try again now.',
        },
        welcome: {
            skip: 'Skip',
            next: 'Continue',
            start: 'Get started',
            dotA11y: 'Card {position} of {total}',
            beforeTitle: 'Plan the event ahead',
            beforeBody: 'Review the attendees, speakers and agenda before the event. Schedule business meetings before it begins.',
            beforeArtA11y: 'Attendee list and meeting request',
            duringTitle: 'The whole event in one app',
            duringBody: 'The agenda, the speakers and your check-in QR code are all here. You share your business card with a QR code. Nothing is shared until both sides approve.',
            duringArtA11y: 'Agenda and meeting over a QR code',
            afterTitle: 'Stay in touch after the event',
            afterBody: 'The business cards you exchange are kept in your wallet. You note who you met at which event, so the connection is not lost.',
            afterArtA11y: 'Business card wallet',
        },
        home: {
            myEventsTitle: 'Events you joined',
            manageTitle: 'Events you manage',
            manageWarnCount: '{count} warnings',
            manageWarnCount_one: '{count} warning',
            manageMetaWarn: '{countLabel} · {warnLabel}',
            recommendedTitle: 'Recommended for you',
        },
        checkin: {
            title: 'Check-in',
            openCta: 'Run check-in',
            notOpenNote: 'Check-in opens on the event day.',
            gateDefault: 'Door 1',
            frameKicker: 'Line the code up in the frame',
            scanHint: 'Scan the badge or the QR code in the attendee app.',
            manualCta: 'Search by name',
            manualTitle: 'Search by name',
            manualSearchLabel: 'First or last name',
            manualSearchPlaceholder: 'Type at least two letters',
            manualEmpty: 'No attendee found with that name. Check the spelling or ask for the ticket number.',
            manualPrivacyNote: 'This door list stays on this screen and carries no contact details.',
            backToScan: 'Back to scanning',
            okKicker: 'Entry confirmed',
            dupKicker: 'This badge was already scanned',
            dupBody: 'The same badge was scanned twice. It may be shared; ask the person to confirm their identity.',
            forceCta: 'Let them in anyway',
            rejectCta: 'Turn away and continue',
            nextCta: 'Next person',
            forcedNote: 'Let in by staff decision. Recorded; the first check-in time didn’t change.',
            rowTicket: 'Ticket',
            rowTicketNone: 'Not set',
            rowGate: 'Door',
            rowTime: 'Time',
            alreadyBadge: 'Already inside',
            replayedNote: 'This scan had already been sent; no new entry was created.',
            queuedKicker: 'Queued',
            queuedBody: 'You are offline. The entry is stored on this device and will sync automatically.',
            queuedUnknownPerson: 'Scanned badge',
            queueNote: '{count} entries are queued. They sync automatically once you are back online.',
            queueNote_one: '{count} entry is queued. It is sent automatically once you are back online.',
            queueSendCta: 'Send the queue now',
            queueSyncing: 'Sending…',
            permissionKicker: 'Camera permission is off',
            permissionBody: 'Scanning QR codes needs camera permission. You can still check people in by searching their name.',
            permissionCta: 'Allow camera',
            permissionSettingsCta: 'Open system settings',
            scannerUnavailable: 'This build has no camera reader module; check people in by name search.',
            cameraUnavailable: 'No usable back camera on this device; check people in by name search.',
            cameraError: 'The camera could not start. Restart the app or check people in by name search.',
            foreignCode: 'That code is not one of our badges. Scan the badge or the QR code in the attendee app.',
            torchOnCta: 'Torch',
            torchOffCta: 'Torch off',
            cameraPrivacyNote: 'The camera feed is decoded on this device; it is never stored or uploaded.',
            privacyNote: 'The door shows name, title and ticket type only; email and phone are never displayed.',
        },
        manage: {
            title: 'Manage',
            lead: 'Every management section of your event lives here. Flagged ones still open on the web panel only.',
            /* Organizer announcements (Story 12, T4c - designs M1-M7). Mobile-only copy. */
            announcements: {
                emptyBody: 'Announce event-day changes to attendees from here. Your organization only sees how many people receive it.',
                loadErrorTitle: 'Announcements could not be loaded.',
                composeNewTitle: 'New announcement',
                previewStep: 'Preview',
                composeEditTitle: 'Edit announcement',
                subjectPlaceholder: 'E.g. Session delayed',
                bodyPlaceholder: 'What do you want to tell attendees?',
                bodyHint: 'Keep it short and clear. The notification shows the first two lines.',
                countLoading: 'Counting people…',
                countPick: 'Choose an audience',
                channelRequired: 'Choose at least one channel',
                audienceRequired: 'Choose an audience',
                inAppIncluded: 'Every announcement also appears in the in-app notification center.',
                kindKicker: 'Content type',
                saveNote: 'Your draft is saved when you open the preview. You can edit it until you send.',
                emailFrom: 'From',
                emailSubject: 'Subject',
                pushLockScreen: 'How it looks on the lock screen',
                pushHint: 'The device shortens the text after two lines.',
                notSelectedTitle: 'This channel is not selected',
                notSelectedAddable: 'To send on this channel too, go back to editing and add it.',
                sendClosed: 'Sending announcements is not open yet. You can prepare your draft now.',
                previewError: 'The preview could not be created.',
                sendSheetTitle: 'Send now?',
                factAudience: 'Audience',
                factChannels: 'Channels',
                factKind: 'Content type',
                sendEmpty: 'Nobody is in the audience you chose right now. Choose another audience or send later.',
                sendError: 'Sending could not start. Your announcement is still a draft, you can try again.',
                sending: 'Sending…',
                sendingLive: 'Starting to send',
                datePlaceholder: 'Choose a date',
                timePlaceholder: 'Choose a time',
                zoneLine: 'Time zone: {zone}',
                scheduleBounds: 'Earliest 10 minutes from now, latest {date} (30 days after the event ends).',
                scheduleTooSoon: 'The earliest you can choose is {time}. Scheduling must be at least 10 minutes from now.',
                scheduleTooLate: 'The latest you can choose is {date}. Scheduling can be at most 30 days after the event ends.',
                scheduling: 'Scheduling…',
                statusTitle: 'Announcement status',
                whenScheduled: 'Scheduled: {date}',
                whenSending: 'Started: {date}',
                whenSent: 'Sent: {date}',
                whenCancelled: 'Cancelled: {date}',
                whenFailed: 'Attempted: {date}',
                stripTarget: 'Target',
                stripQueued: 'Queued',
                stripSent: 'Sent',
                stripFailed: 'Failed',
                liveScheduled: 'Sending starts at {date}.',
                liveSending: 'Sending in progress. Numbers update on their own.',
                liveSent: 'Sending finished.',
                liveCancelled: 'The announcement was cancelled.',
                liveFailed: 'Sending could not finish.',
                breakdownNote: 'The breakdown counts messages. One person can receive it on several channels.',
                breakdownPending: 'The channel breakdown appears once sending starts.',
                breakdownFew: 'The channel breakdown is not shown for a small number of messages.',
                cancelledBeforeSend: 'Cancelled before sending started. No message went out.',
                failedNote: 'Sending could not finish. This announcement cannot be sent again, write a new one if needed.',
                statusForbidden: 'You do not have access to this announcement’s status.',
                statusNotFound: 'Announcement not found.',
                cancelBodyScheduled: 'Sending has not started. No message goes out and the announcement stays cancelled.',
                cancelBodyQueued: '{count} queued messages will not be sent.',
                cancelBodyQueued_one: '{count} queued message will not be sent.',
                cancelBodySent: '{count} messages already sent cannot be recalled.',
                cancelBodySent_one: '{count} message already sent cannot be recalled.',
                cancelTooLateTitle: 'This announcement can no longer be cancelled',
                cancelTooLateBody: 'Sending finished. Messages that went out cannot be recalled.',
                cancelling: 'Cancelling…',
            },
            /* Hub (T1 - "Shell and section map" reference). No invented numbers. */
            sectionsHeading: 'Sections',
            sectionCount: '{count} sections',
            sectionCount_one: '{count} section',
            sectionOnWebBadge: 'On web',
            sectionOnWebHint: 'This section currently opens on the web panel only.',
            noAccessTitle: 'You do not have access to this panel',
            noAccessBody: 'The organizing company has not granted you access, or the event was removed. Ask the company owner for access; panel details stay hidden until then.',
            kpiLoadError: 'Counts could not be loaded.',
            attendeeRows: '{count} registration rows',
            attendeeRows_one: '{count} registration row',
            checkedInAt: 'Checked in: {time}',
            notCheckedIn: 'Not checked in',
            applicationsOnlyApprovalNote: 'The application queue only exists on approval events; registration is direct here.',
            decideError: 'The decision could not be saved. Check your connection and try again.',
            /* Invitations (T2 - "Invitations" reference). Shared copy lives under `manage.invite*`. */
            invite: {
                newCta: 'Send invitations',
                newA11y: 'Send a new invitation',
                emptyTitle: 'You have not sent any invitations yet',
                emptyBodyInviteOnly: 'This event is open to invitees only; nobody can register on their own. Begin by sending the first invitation.',
                emptyBodyApproval: 'Anyone can apply to this event; the people you invite join directly without queuing for approval. Begin by sending the first invitation.',
                filterEmptyTitle: 'No invitations in this filter',
                filterEmptyBody: 'Tap another chip to look at the other invitations.',
                sentTotal: '{count} invitations sent',
                sentTotal_one: '{count} invitation sent',
                countsPartial: 'Chip counts appear once every invitation is loaded.',
                dateSent: 'sent {date}',
                dateExpires: 'valid until {date}',
                dateAccepted: 'accepted {date}',
                dateRejected: 'declined {date}',
                dateExpired: 'expired {date}',
                dateRevoked: 'cancelled {date}',
                csvOnWeb: 'Bulk invitations from a file happen on the web panel; on mobile you paste the addresses yourself. A single send handles at most {limit} addresses.',
                invalidBlocks: 'Fix the invalid addresses before you can send.',
                revokeTitle: 'Cancel the invitation',
                revokeBody: 'The link stops working and this cannot be undone. The invited person is not told about the cancellation. You can invite the same address again and one place opens up in the quota.',
                offlineNote: 'No connection. You can read the list, but invitations cannot be sent; the invitation email is sent by the server.',
                sendError: 'The invitations could not be sent. Check your connection and try again.',
                listError: 'Invitations could not be loaded.',
                ticketRemaining: '{count} left',
                ticketRemaining_one: '{count} left',
            },
            /* Registrations (turn T3, reference screen 02). Shared wording lives under `manage.*`. */
            reg: {
                searchLabel: 'Search name or company',
                filteredRows: '{count} registration rows in this filter',
                filteredRows_one: '{count} registration row in this filter',
                emptyTitle: 'No registrations yet',
                emptyBody: 'The event is live; the first registration will be listed here. Share the event page link to get started.',
                emptyCta: 'Open the event page',
                filterEmptyTitle: 'No registrations in this filter',
                filterEmptyBody: 'Clear the filter to see every registration again.',
                filterClear: 'Clear the filter',
                selectMode: 'Select',
                selectModeExit: 'Close selection',
                bulkOpen: 'Bulk action',
                detailA11y: 'Details of {name}',
                detailClose: 'Close the sheet',
                sheetRegisteredAt: 'Registered at',
                sheetCheckIn: 'Check-in',
                ticketNone: 'No ticket type',
                ticketPickLabel: 'New ticket type',
                ticketPickEmpty: 'This event has no open ticket type, so the type cannot be changed.',
                changeTicket: 'Change ticket type',
                rowError: 'The action could not be completed. Check your connection and try again.',
                offlineNote: 'You are offline. You can read the list, but registrations cannot be changed; the server applies the change.',
            },
            app: {
                chipCount: '{label} · {count}',
                chipCount_one: '{label} · {count}',
                selectMode: 'Select',
                selectModeExit: 'Close selection',
                selectedCount: '{count} applications selected',
                selectedCount_one: '{count} application selected',
                selectRow: 'Select the application of {name}',
                detailA11y: 'Details of the application of {name}',
                detailClose: 'Close the sheet',
                noteHeading: 'Application note, in full',
                noteNone: 'No application note was written.',
                sheetSubmittedAt: 'Applied',
                queueRows: '{count} applications in the queue',
                queueRows_one: '{count} application in the queue',
                filteredRows: '{count} applications in this filter',
                filteredRows_one: '{count} application in this filter',
                emptyTitle: 'No applications yet',
                emptyBody: 'Your event page is live and visitors can see the apply button. This queue fills up once the first applications arrive.',
                emptyCta: 'Open the event page',
                filterEmptyTitle: 'No applications in this filter',
                filterEmptyBody: 'Clear the filter to see every application again.',
                filterClear: 'Clear the filter',
                listError: 'Applications could not be loaded.',
                offlineNote: 'You are offline. You can read the queue, but decisions cannot be made; the server applies the decision.',
                bulkApproveTitle: 'Approve {count} applications?',
                bulkApproveTitle_one: 'Approve {count} application?',
                bulkRejectTitle: 'Reject {count} applications?',
                bulkRejectTitle_one: 'Reject {count} application?',
                bulkApproveConfirm: 'Approve',
                bulkRejectConfirm: 'Reject',
            },
            prog: {
                tabAgenda: 'Agenda',
                tabSpeakers: 'Speakers · {count}',
                tabSpeakers_one: 'Speakers · {count}',
                newSessionA11y: 'Add a new session',
                newSpeakerA11y: 'Add a new speaker',
                daySessions: '{count} sessions',
                daySessions_one: '{count} session',
                agendaEmptyTitle: 'The programme is not set up yet',
                agendaEmptyBody: 'The event runs for {count} days and no session has been added yet. The agenda block on your event page is empty; it fills up as soon as you add the first session.',
                agendaEmptyBody_one: 'The event runs for {count} day and no session has been added yet. The agenda block on your event page is empty; it fills up as soon as you add the first session.',
                agendaEmptyCta: 'Add the first session',
                speakersEmptyTitle: 'No speakers yet',
                speakersEmptyBody: 'Adding a speaker needs no approval; what you type shows on the event page. Linking the person to their own account sends a separate approval request.',
                speakersEmptyCta: 'Add the first speaker',
                conflictTitle: 'Two sessions overlap in the same room',
                conflictCount: '{count} overlaps',
                conflictCount_one: '{count} overlap',
                linkNone: 'Not linked',
                linkedProfileNote: 'Once the link is approved, the person’s own profile (name, title, bio, photo) replaces what you entered on the event page.',
                photoOnWeb: 'Speaker photos are uploaded from the web panel for now; the app shows a monogram.',
                endedNotice: 'This event has ended. You can still correct the programme; the record stays on the event page and attendees are not notified.',
                listError: 'The programme could not be loaded.',
                mutationError: 'The action could not be completed. Check your connection and try again.',
                offlineNote: 'You are offline. You can read the programme, but sessions and speakers cannot be saved; the server applies the change.',
                sessionNewTitle: 'Add session',
                sessionEditTitle: 'Edit session',
                speakerNewTitle: 'Add speaker',
                speakerEditTitle: 'Edit speaker',
                sessionSpeakersEmpty: 'Add a speaker first; you can attach them to the session here.',
                speakerSessionsNone: 'Not attached to any session yet.',
                sessionSpeakersNone: 'No speaker is attached to this session.',
                sessionActionsA11y: 'Actions for the session {title}',
                speakerActionsA11y: 'Actions for {name}',
                sheetClose: 'Close the sheet',
                moveUpA11y: 'Move up: {name}',
                moveDownA11y: 'Move down: {name}',
                linkDialogTitle: 'Link to their account',
                linkDialogSend: 'Send the request',
            },
            /*
             * TICKETS (turn T6). Shared wording lives under `manage.*`; only the
             * mobile-specific sentences are here.
             */
            tick: {
                newA11y: 'Add a new ticket type',
                newTitle: 'Add ticket type',
                editTitle: 'Edit ticket type',
                rowActionsA11y: 'Actions for the {name} ticket type',
                sheetClose: 'Close the sheet',
                emptyTitle: 'No ticket type defined',
                emptyBody: 'Registration cannot open without a ticket type. Add at least one; it can stay free.',
                floorNote: '{count} registrations use this type, so the quota cannot go below that.',
                floorNote_one: '{count} registration uses this type, so the quota cannot go below that.',
                floorNoneNote: 'No registrations use this type yet, so the quota is free to change.',
                priceNote: 'Ticket pricing and payment do not exist in the product yet; every type registers for free.',
                defaultNote: 'The default type is the one attached when an attendee registers without picking a type.',
                deleteBlockedNote: 'This type has {count} registrations and cannot be deleted. Set the sales status to "Closed" to stop new registrations.',
                deleteBlockedNote_one: 'This type has {count} registration and cannot be deleted. Set the sales status to "Closed" to stop new registrations.',
                deleteReservedError: 'This ticket type has registrations and cannot be deleted. Set the sales status to "Closed" to stop new registrations.',
                quotaFloorError: 'The quota cannot go below the {count} existing registrations. The lowest value is {count} until a registration is cancelled.',
                quotaFloorError_one: 'The quota cannot go below the {count} existing registration. The lowest value is {count} until a registration is cancelled.',
                quotaInvalid: 'The quota must be a whole number.',
                quotaHint: 'This type has {count} registrations. Typing "0" effectively closes it.',
                quotaHint_one: 'This type has {count} registration. Typing "0" effectively closes it.',
                quotaUnlimitedHint: 'Leave it empty for an unlimited quota. "0" does not mean unlimited, it effectively closes the type.',
                quotaDecrease: 'Decrease the quota by one',
                quotaIncrease: 'Increase the quota by one',
                statusNote: 'Anyone can pick a type that is on sale, a hidden type is attached only through invitations, and a closed type takes no new registrations.',
                offlineNote: 'You are offline. Ticket types stay readable, but quota changes cannot be saved; the server applies them.',
            },
            /*
             * GÖRÜŞME ALANLARI (Tur T9) — mobilin KENDİ cümleleri; kavram cümleleri
             * `meetingAreas.*` altında ve web paneliyle ORTAK.
             */
            area: {
                newA11y: 'Add a new meeting area',
                rowActionsA11y: 'Actions for {name}',
                sheetClose: 'Close the sheet',
                offlineNote: 'You are offline. Meeting areas stay readable, but adding, editing and deleting are closed; all three are decided on the server.',
                noFilterNote: 'This list is not filtered; every area defined for this event is here.',
                linkedNote: '{count} accepted meetings are attached to this area. The confirmation step shows what happens before you delete it.',
                linkedNote_one: '{count} accepted meeting is attached to this area. The confirmation step shows what happens before you delete it.',
                linkedNoneNote: 'No accepted meeting is attached to this area; deleting it affects nobody.',
                capacityDecrease: 'Decrease the capacity by one',
                capacityIncrease: 'Increase the capacity by one',
                capacityRangeHint: 'The capacity can be between {min} and {max}.',
                hoursFromLabel: 'Start time',
                hoursToLabel: 'End time',
                hoursClear: 'Clear the hours',
                hoursAllHint: 'Leave the hours empty to keep the area open for the whole event.',
                daysHint: 'Leave every day unselected to keep the area open on all event days.',
                daysSingleNote: 'This event lasts a single day, so there is no day to pick.',
                impactUnknown: 'The affected meetings cannot be read right now. An area is not deleted before that number is known.',
                impactRetry: 'Read the impact again',
                deleteBlockedOffline: 'You are offline; deletion is processed on the server.',
            },
            stats: {
                offlineNote: 'You are offline. The numbers on screen are from the last load; new registrations show up once you reconnect.',
                chartA11y: 'Registrations by hour, bar chart',
                hourlyPeak: 'Busiest hour {hour} · {count} registrations',
                hourlyPeak_one: 'Busiest hour {hour} · {count} registration',
                ticketEmpty: 'No ticket type is defined for this event, so there is no split to show.',
                emptyCtaShare: 'Open the event page',
                rangeNote: 'The numbers cover the whole event; a date range filter does not exist in the product yet.',
            },
            setup: {
                stepBarA11y: 'Setup steps',
                savingStep: 'Saving this step…',
                offlineNote: 'You are offline. Setup stays readable, but saving and publishing are closed; both are decided on the server.',
                recoveryTitle: 'An unfinished setup is waiting',
                recoveryBody: 'We found a draft saved on {date} at step {step}. You can continue where you left off or start over.',
                recoveryContinue: 'Continue',
                recoveryRestart: 'Start over',
                recoveryRestored: 'The unfinished setup was restored. If you leave without saving, it will wait here again.',
                publishBlocked: 'Missing before publishing: {fields}',
                publishWarning: 'Once you publish, the event page opens to search engines and the registration link starts working. Changing the access mode later also changes how the page is visible.',
                keepDraft: 'Keep as draft',
                alreadyPublished: 'The event is live; publishing runs only once.',
                ticketsCta: 'Go to the tickets section',
            },
            brand: {
                offlineNote: 'You are offline. You can read your brand settings, but uploading and saving are closed; both are decided on the server.',
                logoOwn: 'This event has its own logo.',
                logoInherited: 'Using the logo of {organization}.',
                logoNone: 'No logo yet; the initials of the organization are used.',
                logoLimits: '{formats} · at least {minWidth}×{minHeight} px · up to {megabytes} MB',
                logoUpload: 'Upload a logo',
                logoReplace: 'Replace the logo',
                coverKicker: 'Cover image',
                coverNote: 'Without a cover we derive one from your brand tones; we never show an empty grey box.',
                coverLimits: '{formats} · 16:9 · up to {megabytes} MB',
                coverUpload: 'Upload a cover',
                coverReplace: 'Replace the cover',
                coverClear: 'Remove the cover',
                colorNote: 'The brand colour is used only inside the content area of the event page and on the badge. The app frame stays the same.',
                orgBrandOnWeb: 'You set the logo and colour of your organization in the web panel. This section manages the brand of this event only.',
                savedPreviewNote: 'The image you uploaded stays here until you save.',
                pickTitle: 'Upload an image',
                pickCamera: 'Take a photo',
                pickLibrary: 'Choose from the gallery',
                pickSourceKicker: 'Choose a source',
                pickClose: 'Close the sheet',
                cropTitle: 'Crop',
                cropChoose: 'Crop',
                cropCancel: 'Cancel',
                preparing: 'Preparing the image',
                preparingBody: 'The picker is open. Once you pick and crop, the upload continues here.',
                uploading: 'Uploading',
                uploadMeta: '{size} · {percent}%',
                uploadMetaUnknown: '{size}',
                uploadCancel: 'Stop the upload',
                errorTitle: 'The image could not be uploaded',
                errorType: 'We do not accept this file type. Pick one of {formats}.',
                errorSize: 'The file exceeds the {megabytes} MB limit. Pick a smaller image or crop it to reduce the size.',
                errorDimensions: 'The image must be at least {minWidth}×{minHeight} px. Pick a larger one.',
                errorTransport: 'The upload stopped halfway. Check your connection and try again.',
                errorProcessing: 'The image was uploaded but could not be prepared. Try again shortly; if it keeps failing, pick another image.',
                errorUnavailable: 'The image picker cannot open in this build. Try again after updating the app; for now you can upload from the web panel.',
                errorUnreadable: "This photo couldn't be opened. Try another photo.",
                errorPickAnother: 'Another image',
                permissionCameraTitle: 'Camera permission was not granted',
                permissionCameraBody: 'Taking a photo needs permission. You can also choose from the gallery; that permission is asked separately. The image is cropped without leaving the device.',
                permissionLibraryTitle: 'Gallery permission was not granted',
                permissionLibraryBody: 'Choosing from the gallery needs permission. You can also take a photo; that permission is asked separately. The image is cropped without leaving the device.',
                permissionSettingsCta: 'Open settings',
                permissionAskCta: 'Ask for permission',
            },
        },
        comingSoon: {
            kicker: 'Coming soon',
            qrTitle: 'Meeting via QR is coming',
            qrBody: 'Card exchange works with two-sided approval and opens in the next phase. This tab stays in place so your habit is not broken.',
            action: 'See who is coming',
        },
        fileSave: {
            savedToast: '{name} saved to Downloads.',
        },
        push: {
            channelName: 'Event notifications',
            channelPeopleName: 'Meeting requests and cards',
            permissionTitle: 'System notification permission',
            permissionGranted: 'On',
            permissionDenied: 'Off',
            permissionUnknown: 'Not asked yet',
            permissionBody: 'Date, venue changes and cancellations for events you registered for arrive instantly.',
            permissionBodyOrganization: 'News that concerns your organization account arrives on this device instantly. Attendee notifications do not come to this account.',
            deniedBody: 'Permission is off at the system level. Your email notifications stay on, and you can also see changes on the My agenda screen.',
            deniedBodyOrganization: 'Permission is off at the system level. Your email notifications stay on.',
            assurance: 'Notifications are only about your own participations; no other user data travels on this channel.',
            assuranceOrganization: 'Notifications only concern this organization account. No other user data travels on this channel.',
            allowCta: 'Allow notifications',
            openSettingsCta: 'Open system settings',
            registeredMeta: 'Device registration active.',
            registeredDevMeta: 'Device registration active (development token - no real FCM key).',
            notRegisteredMeta: 'No device registration.',
            registrationFailed: 'Permission is on but the device could not be registered, so notifications will not reach this device yet. Try the registration again. The Notifications screen in Settings also retries it when opened.',
            registrationRetryCta: 'Retry registration',
            devProbeCta: 'Show test notification (development only)',
            devProbeFollowCta: 'Show follow notification (development only)',
            devProbeAnnouncementCta: 'Show announcement notification (development only)',
            devProbePostEventCta: 'Show after-event notification (development only)',
            stepTitle: 'Don’t miss a chance to meet',
            stepLead: 'We’ll let you know when:',
            stepReasonMeeting: 'You get a meeting request or a reply',
            stepReasonCard: 'Your card exchange is accepted',
            stepReasonEvent: 'An event you registered for is coming up',
            stepReasonFollow: 'An organizer you follow publishes a new event',
            stepNoAds: 'We don’t send ads or promotions.',
            stepLater: 'Not now',
            stepFootnote: 'You can change this anytime in Settings › Notifications.',
            stepSampleName: 'Ayşe Y.',
            stepSampleTime: 'now',
            stepSampleTitle: 'Ayşe Y. wants to meet you',
            stepSampleMeta: 'Open Banking Summit · 24 October, 10:30',
            enabledToast: 'Notifications are on. We’ll let you know when a meeting request comes in.',
            deniedCardTitle: 'Notifications are off',
            deniedCardBody: 'You’ll see meeting requests when you open the app. You can turn them on anytime in Settings.',
            deniedCardCta: 'Open Settings',
            contextualTitle: 'Want to know when they reply?',
            contextualBody: 'We’ll tell you as soon as your meeting request is answered.',
            contextualRegistrationTitle: 'Want a heads-up before your event?',
            contextualRegistrationBody: "We'll let you know when an event you're registered for is coming up, when an organizer sends an announcement, and when you receive a meeting request.",
            contextualCta: 'Turn on notifications',
            cardClose: 'Close',
        },
        agenda: {
            title: 'My agenda',
            subtitle: 'Programme of the events you joined',
            emptyTitle: 'Your agenda is empty for now',
            emptyBody: 'Once you register for an event, sessions are listed here day by day. If the programme is not published yet, the event still shows up.',
            noSessions: 'The programme is not published yet.',
            dayLabel: 'Day {day}',
        },
        person: {
            title: 'Attendee profile',
            interestsTitle: 'Interests',
            aboutTitle: 'About',
            cardNote: 'Card details are shared only after two-sided approval. Viewing a profile is not sharing.',
            contactHidden: 'Contact details are never shown.',
            speakerBadge: 'Speaker',
            organizerBadge: 'Organizer',
            contextTitle: 'Context',
            contextEventRole: 'You are seeing this person in the {event} attendee list.',
            contextSpeaker: 'Listed as a speaker at this event.',
            contextSector: 'Sector: {sector}',
            contextSeniority: 'Seniority: {seniority}',
            similarTitle: 'Similar people',
            similarLead: 'People at the same event whose interests overlap with yours.',
            similarAllCta: 'See all',
        },
        /* Speaker detail (27.10.2026): mobile twin of web screen 29, most copy comes from `speakerPage`. */
        speaker: {
            title: 'Speaker',
            openA11y: 'Open the speaker page for {name}',
            shareCard: 'Share contact',
            shareFailed: 'The card could not be shared. Try again.',
            notFoundBody: 'The speaker may have been removed, or the event is no longer published.',
            loading: 'Loading',
            offline: 'You’re offline. Showing what you last saw.',
        },
        event: {
            tabGeneral: 'Overview',
            tabSpeakers: 'Speakers',
            tabAgenda: 'Agenda',
            tabAttendees: 'Attendees',
            attendeesLocked: 'The attendee list is available to registered attendees.',
            inviteOnlyNotice: 'This event is invite only. You need an invitation from the organizer.',
            openInvite: 'I have an invite code',
            onlyRecommended: 'Only recommended to me',
            onlyRecommendedHint: 'Shows attendees you share interests with; those without a reason are hidden.',
            navAttendees: 'Attendees',
            navAttendeesOpen: 'Search and filter',
            navAttendeesLocked: 'Opens after you register',
            navCellA11y: '{title}, {meta}',
            navAgenda: 'Agenda',
            navAgendaMeta: '{count} sessions',
            navAgendaMeta_one: '{count} session',
            navAgendaEmpty: 'Programme not published',
            speakersKicker: 'Speakers',
            agendaKicker: 'Programme',
            openProfile: 'Profile',
        },
        invite: {
            title: 'Open invitation',
            tokenLabel: 'Code from the invitation link',
            tokenHint: 'Paste the code at the end of the link in your email. Invitations are not transferable.',
            lookupCta: 'Fetch invitation',
        },
        settings: {
            title: 'Settings',
            appearanceTitle: 'Appearance',
            appearanceHint: 'Starts in light theme. You can change it any time, and your choice is remembered.',
            languageTitle: 'Language',
            accountTitle: 'Account',
            signOut: 'Sign out',
            signedInAs: 'Signed in as {email}',
            dataTitle: 'Your data',
            exportOnWeb: 'Exporting happens in the browser. You verify your identity once more there, and the link arrives by email when it is ready.',
            exportOpenWebCta: 'Open in browser',
            exportOpenWebHint: 'The link opens at {domain}.',
            kvkkRequestTitle: 'Data subject request',
            kvkkRequestBody: 'You make your request through the form on the web. The button opens it in your browser, where you may be asked to sign in.',
            kvkkRequestCta: 'Make a data subject request',
            deletionConfirmHint: 'Turning this on lets you request a verification code by email. No account is deleted without that code.',
            deletionGraceDays: 'You can undo the request within {days} days; nothing is permanently deleted before then.',
            profileTitle: 'Profile and business card',
            profileHint: 'Your name, card, seniority and sector, and interest tags.',
            speakerLinksHint: 'Events where you are listed as a speaker, and pending approvals.',
            teamInvitationHint: 'If you received a team invitation to an organization, paste the code here.',
            organizationsHint: 'See your organizations and run your verification application from here.',
        },
        common: {
            backA11y: 'Go back',
            tabsA11y: 'Screen sections',
            pullToRefresh: 'Pull to refresh',
            loadMore: 'Show more',
            resultsLoading: 'Loading list',
            previewPersonName: 'Ada Yilmaz',
            previewPersonRole: 'Product Manager · Example Tech',
            previewEventTitle: 'Sample Event',
            previewEventMeta: 'Istanbul · In person',
            previewOrganizationName: 'Example Organization',
        },
        cardReady: {
            kicker: 'Setup complete',
            poster: 'Your card is ready.',
            assurance: 'You can now meet people with your QR code and send meeting requests before events. Your profile is only visible to attendees of the events you register for.',
            cardKicker: 'Your card',
            qrHint: 'Your QR code is short-lived and is regenerated in the QR tab at every introduction; it is not stored here.',
            showCardCta: 'Show my card',
            nextTitle: 'Next step',
            nextBody: 'Register for an event; the attendee list and recommendations open then.',
            nextCta: 'Find an event',
            visibilityCta: 'Review privacy settings',
        },
        /**
         * ORGANIZATIONS OPEN VIA SALES · MOBILE (T6 · M1-M8). Copy shared with the
         * web lives in `salesInquiry`, `orgEntry`, `credits`, `publishDialog` and
         * `ceiling`. Only mobile-specific sentences live here. No price, currency
         * or purchase copy (V10, `no-price.guard`).
         */
        salesInquiry: {
            offlineNotice: 'You are offline. Your details are kept on this device.',
            offlineNote: 'You can send it once you are back online.',
            rateLimitedNote: 'The button opens again in a few minutes.',
            sentBack: 'Back to my organizations',
            noticeOpen: 'Read the privacy notice',
        },
        orgEntry: {
            listOfflineNotice: 'You are offline. Showing the last list you saw.',
            listOfflineNote: 'The list refreshes when you are back online.',
            listError: 'We could not load your organizations.',
            contactLink: 'Contact us about a new event',
            boxTitle: 'Want to run your event on {brandName}?',
            boxBody: 'We will set up your organization for you.',
            askRole: 'Ask your organization admin for permission to create events.',
            askRoleHint: 'If you are on an organization team, ask your admin for permission.',
            createOfflineNotice: 'You are offline. Creating an event needs a connection.',
            organizerPlaceholder: 'Choose an organization',
            organizerSingle: 'This is the only organization you can manage.',
            organizerLocked: 'The draft was saved for {organizationName}. The organizer cannot be changed later.',
            organizerNone: 'You have no organization to create events with. Contact us and we will set one up.',
            organizerLoadFailed: 'Could not load',
            organizerLoadError: 'We could not load your organizations.',
            organizerOfflineNotice: 'You are offline. The organization list refreshes when you are back online.',
        },
        credits: {
            offlineNotice: 'You are offline. Credit details are shown as last seen.',
            eventsTitle: 'Events',
            teamTitle: 'Team',
            eventRegistrations: '{count} registrations',
            eventRegistrations_one: '{count} registration',
            kickerExhausted: 'Out of credits',
            unitPublish: 'publishing',
        },
        publish: {
            infoCredit: 'Publishing uses 1 credit. You have {count} credits.',
            infoCredit_one: 'Publishing uses 1 credit. You have {count} credit.',
            infoTrial: 'Publishing uses your welcome credit. Attendee limit {ceiling}.',
            infoContract: 'Published under your Enterprise agreement. No credit is used.',
            infoExhausted: 'Your organization is out of credits. The event stays a draft.',
            offlineNotice: 'You are offline. Publishing needs a connection.',
        },
        ownerInvite: {
            title: 'You are invited as the owner of {organizationName}',
            body: 'The {brandName} team set up your organization. Once you accept, you can create events, invite your team and see your credit status.',
            notMine: 'This invitation is not for me',
            acceptedTitle: 'You are now the owner of {organizationName}',
            acceptedBody: 'You can create events, invite your team and see your credit status.',
            acceptError: 'We could not accept the invitation. Try again.',
            expired: 'This invitation has expired. Contact us for a new one.',
            expiredTitle: 'Expired',
            expiredCta: 'Contact us',
            offlineNotice: 'You are offline. Accepting the invitation needs a connection.',
        },
        ceiling: {
            contractNote: 'Enterprise agreement · no attendee limit',
            offlineNotice: 'You are offline. Registration count is shown as last seen.',
            lockBreakdownAction: 'Sector/seniority',
        },
        /**
         * ORGANIZATION ACCOUNT MOBILE SURFACES (K6 · H12, H14 · TM1, TM2, TM4, TM5, TM6).
         */
        orgAccount: {
            tabOrganization: 'Organization',
            tabEvents: 'Events',
            tabAccount: 'Account',
            upcoming: 'Upcoming',
            allEvents: 'All events (including past)',
            todayAt: 'Today · {time}',
            eventsEmptyTitle: 'No events to manage yet',
            eventsEmptyBody: 'Events show up here when you open one or are added as an editor.',
            offlineSeen: 'You’re offline. Showing what you last saw.',
            offlineSaved: 'You’re offline. Changes are saved when you’re back online.',
            switchOrganization: 'Switch organization',
            teamMembers: '{count} members',
            teamMembers_one: '{count} member',
            teamPending: '{count} pending invites',
            teamPending_one: '{count} pending invite',
            teamManage: 'Manage team ›',
            teamManageLabel: 'Manage team',
            orphanBody: 'This organization account is for organization work only. You can use it again when an organization invites you.',
            toPersonal: 'Switch to your personal account',
            gateTitle: 'This screen is for personal accounts',
            gateWanted: 'Screen you wanted: {page}',
            gateWhy: 'Business cards, wallet, meeting requests and your personal agenda work with your personal account. You’re in the {organizationName} organization account.',
            gateWhyNoOrganization: 'Business cards, wallet, meeting requests and your personal agenda work with your personal account. You’re in an organization account.',
            gateBack: 'Back to Organization tab',
            otherCta: 'Add an organization account with this address',
            otherNote: 'Add account opens. Your personal account stays on this device.',
            otherNoteOrganization: 'Add account opens. This account stays on this device.',
            pageHome: 'Home',
            pageDiscover: 'Event discovery',
            pageCard: 'Business card',
            pageWallet: 'Wallet',
            pageMeetingRequest: 'Meeting request',
            pageMeetingRequests: 'Meeting requests',
            pageAgenda: 'Agenda',
            pageAttendees: 'Attendee list',
            pagePerson: 'Person profile',
            pageBadge: 'Badge',
            pageProfile: 'Business card profile',
            pageSettings: 'Personal settings',
            pageVisibility: 'Privacy settings',
            pageBlocked: 'Blocked people',
            pageSpeakerLinks: 'Speaker links',
            pageEncounterNote: 'Meeting note',
            pageEventSurvey: 'Event survey',
            blockJoin: 'You can’t attend with an organization account.',
            blockJoinBody: 'Switch to your personal account to attend.',
            blockApply: 'You can’t apply with an organization account.',
            blockApplyBody: 'Switch to your personal account to apply.',
            blockOwnBody: 'This is a {organizationName} event. Switch to your personal account to attend.',
            blockInviteBody: 'This invite went to an organization address. Ask for an invite to your personal address.',
            accountTitle: 'Account',
            sectionAccounts: 'Accounts',
            switchAccount: 'Switch account',
            transferLink: 'Transfer ownership ›',
            dataNote: 'The organization’s attendee data isn’t in this file.',
            signOutThis: 'Sign out of this account',
            accountLoadError: 'Couldn’t load account info.',
            identityNote: 'The logo is the organization’s brand. Change it in Organization › Brand.',
            inviteEntry: 'Have an organization invite?',
            inviteEntryTitle: 'Organization invite',
            managementTitle: 'Organization work uses a separate account',
            managementBody: 'Event management and the gate use an organization account. If you don’t have an organization, we’ll open one for you.',
        },
        /**
         * ORGANIZATION BRAND SCREEN (mobile organization settings · Story M1 · reference MK1).
         * No organization name, description or website field (K2). No price, credits or payment.
         */
        orgBrand: {
            title: 'Brand',
            desc: 'Your logo and color appear on event pages and badges.',
            logoKicker: 'Logo',
            limits: '{formats} · up to {megabytes} MB · at least {minWidth}×{minHeight}',
            noLogo: 'No logo. The organization’s initials are used.',
            hasLogo: 'This logo appears on the account icon, the organization header, the team list and badges.',
            pick: 'Choose logo',
            change: 'Change',
            remove: 'Remove logo',
            uploading: 'Uploading logo · {percent}%',
            uploadingNoPercent: 'Uploading logo',
            colorKicker: 'Color',
            colorNone: 'No color',
            colorRed: 'Red',
            colorBlue: 'Blue',
            colorGreen: 'Green',
            colorPurple: 'Purple',
            colorBlack: 'Black',
            contrast: 'The color is darkened if needed for readability.',
            darkened: 'The color was darkened for contrast.',
            inherit: 'If an event doesn’t pick its own brand, it uses the organization’s brand.',
            badgeKicker: 'Badge preview',
            sampleEvent: 'Product Days 2026',
            samplePerson: 'Deniz Arslan',
            sampleMeta: 'Product Manager · Atlas',
            sampleTag: 'Sample attendee',
            save: 'Save',
            saving: 'Saving…',
            saved: 'Brand saved.',
            saveError: 'Couldn’t save the brand. Your choices are still here, you can try again.',
            saveForbidden: 'Only owners and admins can change the brand. Your role may have changed, the info was refreshed.',
            loadError: 'Couldn’t load brand info.',
            offline: 'You’re offline. You can view the brand but not change it.',
            offlineWhy: 'You need a connection to choose a logo, change the color and save.',
            coverPickerUnavailable: 'The image picker cannot open in this build. Try again after updating the app.',
            readOnly: 'Only owners and admins can change the brand. Your role is {role}.',
            settingsKicker: 'Organization settings',
            entryNoLogo: 'No logo',
        },
        /**
         * ORGANIZATION TEAM (mobile organization settings round B1 · MK2). Role
         * names, permission lines, "You" and "Invitation pending" badges and the
         * summary counts are SHARED with the web (`orgTeam.*`, `ROLE_*_KEYS`); only
         * the mobile drawing's own sentences live here.
         */
        orgTeam: {
            title: 'Team',
            intro: 'Your team manages events together. Roles decide what each person can do.',
            inviteCta: 'Invite member',
            readOnly: 'Only owners and admins can change the team.',
            offlineSeen: 'You’re offline. Showing the last list you saw.',
            offlineWhy: 'You need a connection to send invites, change roles and transfer ownership.',
            loadError: 'Couldn’t load the team list.',
            refreshError: 'Couldn’t refresh the list. Showing the last list you saw.',
            soloTitle: 'It’s just you on the team for now',
            soloBody: 'People you invite show up here once they accept.',
            ownerNote: 'The owner role can’t be changed, only transferred.',
            inviteSent: 'Invite sent.',
            memberOptions: 'Options for {name}',
            sheetRole: 'Role',
            sheetValidity: 'Valid until',
            sheetExpired: 'Expired',
            cancelOffline: 'You need a connection to cancel the invite.',
            cancelTitle: 'Cancel invite?',
            cancelBody: 'The invite link sent to {email} will no longer work. You can invite them again later.',
            actionError: 'Couldn’t complete this. Check your connection and try again.',
            inviteGone: 'This invite is no longer on the list. The list was refreshed.',
            inviteAccepted: 'This invite was just accepted and the person is now on the team. The list was refreshed. To remove them, tap their row.',
            inviteTitle: 'Invite member',
            emailLabel: 'Email',
            emailPlaceholder: 'name@{domain}',
            emailPlaceholderShort: 'name@example.com',
            emailHint: 'Enter one address.',
            emailInvalid: 'Enter a valid email address.',
            domainNote: 'Addresses outside the organization’s domain can be invited too.',
            roleSection: 'Role',
            contentOrganization: 'Organization',
            contentInviter: 'Invited by',
            contentRole: 'Role',
            contentValidity: 'Valid for',
            validityDays: '{count} days',
            validityDays_one: '{count} day',
            scopeNote: 'The invited person’s email address is only shown in this team list. It never appears in attendee lists.',
            already: 'This address is already on the team.',
            tooMany: 'Too many attempts. Wait a bit and try again.',
            sendError: 'Couldn’t send the invite. Check your connection and try again.',
            backToTeam: 'Back to team',
            // --- Round B2 · active member sheet, removal and ownership transfer (MK2) ---
            inviteOffline: 'You need a connection to send the invite.',
            actionsOffline: 'You need a connection to change the role or remove someone.',
            removeCta: 'Remove from team',
            roleError: 'Couldn’t change the role. The previous role still applies, try again.',
            roleChanging: 'Changing role',
            removeTitle: 'Remove from team?',
            removeBody: '{email} loses access right away.',
            memberGone: 'This member was changed elsewhere or left the team. The list was refreshed.',
            transferHeading: 'Transfer ownership',
            transferIntro: 'You can transfer ownership to an active team member. After the transfer you become an Admin.',
            transferPick: 'New owner',
            transferChoose: 'Choose',
            transferNone: 'There’s no other active member to transfer to. Invite someone first.',
            transferSheetTitle: 'Choose the new owner',
            transferSheetNote: 'Ownership can’t go to someone whose invite is pending.',
            transferSheetConfirm: 'Choose',
            transferTitle: 'Transfer ownership?',
            transferBody: '{email} becomes the owner and you become an Admin. To undo this, the new owner has to transfer ownership back to you.',
            transferring: 'Transferring…',
            transferDone: 'Ownership transferred.',
            transferOffline: 'You need a connection to transfer ownership.',
            transferGone: 'Ownership can no longer go to the member you chose. The list was refreshed.',
            notOwner: 'You’re no longer the owner of this organization. The list was refreshed.',
        },
        /**
         * ACCOUNT SWITCHER (K7 · Story H13 · TM3). Up to three accounts on a device;
         * notifications only reach the active account (KH11).
         */
        accounts: {
            title: 'Accounts',
            active: 'Active',
            personal: 'Personal',
            noticeOrganization: 'Organization account',
            bellOn: 'Notifications come to this account',
            bellOff: 'No notifications here. They start when you switch.',
            bellDevice: 'This device isn’t receiving notifications. Turn them on in notification settings.',
            signOut: 'Sign out',
            signOutA11y: 'Sign out of {name}',
            switchA11y: 'Switch to {name}',
            add: 'Add account',
            full: 'Up to 3 accounts. Sign out of one to add another.',
            expired: 'Sign in again',
            offlineAdd: 'You need a connection to add an account. You can still switch between saved accounts.',
            switchingPersonal: 'Switching to personal account…',
            switchingOrganization: 'Switching to organization account…',
            done: 'You’re now in {account}. Notifications will come here.',
            failed: 'Couldn’t switch accounts. Try again.',
            addTitle: 'Add account',
            addLastSlot: 'This is the last account you can add on this device.',
            reauthSubtitle: 'Sign in again for {email}. Your other accounts aren’t affected.',
            close: 'Close',
            hasPersonal: 'Your personal account is on this device ({email}). You switch directly.',
            noPersonal: 'No personal account on this device. Add account opens.',
        },
    },
    /* Certificate of attendance and survey (Story 1): ready-made question texts. */
    eventSurvey: {
        presets: {
            overall_satisfaction: 'How was the event overall?',
            recommend: 'Would you recommend this event to someone you know?',
            liked_most: 'What did you like most?',
            improve: 'What could be done better?',
        },
        certificate: {
            title: 'Certificate of Attendance',
            kicker: 'CERTIFICATE OF ATTENDANCE',
            intro: 'This is to certify that',
            eventSentence: 'attended {eventTitle}.',
            dateLabel: 'DATE',
            placeLabel: 'VENUE',
            issuer: 'Issuing organization',
            codeLabel: 'Verification code',
            issuedVia: 'This certificate was issued via {brandName}.',
            online: 'Online',
        },
        participant: {
            sectionTitle: 'After the event',
            certTitle: 'Certificate of attendance',
            certBody: 'A document showing you attended this event. PDF, A4.',
            certLanguage: 'Certificate language',
            languageTr: 'Türkçe',
            languageEn: 'English',
            download: 'Download certificate',
            downloading: 'Downloading…',
            downloadError: 'Couldn’t download the certificate.',
            retry: 'Try again',
            surveyTitle: 'Event survey',
            questionCount: '{count} questions',
            questionCount_one: '{count} question',
            closesIn: 'Closes in {count} days',
            closesIn_one: 'Closes in {count} day',
            anonymous: 'Your answers are anonymous. The organizer only sees combined results.',
            fill: 'Take the survey',
            sentTag: 'Survey submitted',
            sentBody: 'Thank you. Your answers were added to the combined results.',
            closedTag: 'Survey closed',
            closedBody: 'The survey closed on {date}. You can still download your certificate.',
            closedBodyNoCert: 'The survey closed on {date}.',
            sectionLoadError: 'Couldn’t load this section.',
            closesInSentence: 'The survey closes in {count} days.',
            closesInSentence_one: 'The survey closes in {count} day.',
            ratingLow: '1 · Very poor',
            ratingHigh: '5 · Very good',
            npsLow: '0 · Not at all',
            npsHigh: '10 · Definitely',
            scaleAria: '{value} out of {scaleMax}',
            multiHint: 'You can choose more than one.',
            textWarning: 'The organizer sees this text. Don’t include anything that identifies you.',
            textPlaceholder: 'Write here',
            textMax: 'Use 500 characters or fewer.',
            submit: 'Submit survey',
            submitting: 'Submitting…',
            submitError: 'Couldn’t submit the survey. Your answers are still here, so you can try again.',
            answerRequired: 'Answer at least one question to submit.',
            optionalNote: 'You can skip any question you don’t want to answer.',
            backToEvent: 'Back to event page',
            thanksTitle: 'Thank you',
            thanksBody: 'Your answers were added anonymously to the combined results. The organizer can’t see who answered what.',
            closedTitle: 'This survey is closed',
            closedScreenBody: 'The survey closed on {date} and no longer accepts answers.',
            deniedTitle: 'This survey isn’t open to you',
            deniedBody: 'The survey is only open to attendees who checked in at the event.',
            surveyLoadError: 'Couldn’t load the survey.',
            rowCertificate: 'Certificate',
            rowSurvey: 'Survey',
            rowCertificateAria: 'Download certificate for {title}',
            rowSurveyAria: 'Take the survey for {title}',
            share: 'Share',
            sharing: 'Preparing…',
            screenTitle: 'Survey',
        },
        results: {
            title: 'Survey results',
            openTag: 'Open · closes {date}',
            responses: 'Responses',
            responsesSub: 'Updates in steps of 5',
            rate: 'Response rate',
            rateSub: '{responses} of {checkedIn} checked in',
            closes: 'Closes',
            closesValue: '{count} days',
            closesValue_one: '{count} day',
            closedValue: 'Closed',
            note: 'Results update with every 5 new answers. Values from 1 to 4 read “Fewer than 5”, 0 is shown. Questions with fewer than 5 answers aren’t shown. Who answered what, and when, isn’t visible.',
            answerCount: '{count} answers',
            answerCount_one: '{count} answer',
            textCount: '{count} answers · shuffled, undated',
            textCount_one: '{count} answer · shuffled, undated',
            belowThresholdCount: 'Fewer than 5 answers',
            belowThreshold: 'Shown once there are at least 5 answers.',
            averageLabel: 'Average {value} / 5',
            npsLabel: 'Recommendation score {value}',
            averageHidden: 'Average not shown',
            npsHidden: 'Recommendation score not shown',
            promoters: 'Promoters (9-10)',
            passives: 'Passives (7-8)',
            detractors: 'Detractors (0-6)',
            npsFoot: 'The score is the share of promoters minus the share of detractors.',
            multiFoot: 'People could pick more than one, so shares add up to more than 100%.',
            seeAll: 'See all {count} answers',
            seeAll_one: 'See {count} answer',
            emptyNoSurveyTitle: 'No survey',
            emptyNoSurveyBody: 'No survey was set up for this event. You can prepare and publish one in the survey builder.',
            setUpSurvey: 'Set up a survey',
            emptyUnderTitle: 'Fewer than 5 answers so far',
            emptyUnderBody: 'To protect attendees’ privacy, results appear once there are 5 answers. The survey is open until {date}.',
            emptyUnderBodyClosed: 'To protect attendees’ privacy, results appear once there are 5 answers.',
            loadError: 'Couldn’t load results.',
        },
        manage: {
            subCertificate: 'Certificate',
            subBuilder: 'Survey builder',
            subResults: 'Survey results',
            subNavLabel: 'Certificate and survey sections',
            certTitle: 'Certificate of attendance',
            certOnBody: 'Attendees who checked in can download their certificate after the event.',
            certOffBody: 'Certificates are off. Attendees won’t see one. If you turn them on after the event, they appear right away.',
            signerName: 'Signatory name',
            signerTitle: 'Signatory title',
            optional: '(optional)',
            signerNamePlaceholder: 'e.g. Deniz Arslan',
            signerTitlePlaceholder: 'e.g. Secretary General',
            signerHelp: 'If both are empty, the organization name appears instead of a signature.',
            logoNoteTitle: 'The certificate uses your organization logo.',
            logoNoteBody: 'Without a logo, the organization initials are used. Change the logo in Organization settings › Brand.',
            signerMax: 'Use 80 characters or fewer.',
            charCount: '{used} / {max}',
            save: 'Save',
            saved: 'Settings saved.',
            saveError: 'Couldn’t save settings. Try again.',
            previewLabel: 'Live preview',
            previewSample: 'With a sample attendee',
            sampleName: 'Sample Attendee',
            certLoadError: 'Couldn’t load certificate settings.',
            certViewerNote: 'Only owners, admins and editors can change certificate settings.',
            retry: 'Try again',
            builderTitle: 'Event survey',
            status: {
                draft: 'Draft',
                published: 'Live',
                closed: 'Closed',
                disabled_by_staff: 'Turned off',
            },
            questionCount: '{used} of {max} questions',
            audienceNote: 'The survey is open only to attendees who checked in.',
            sensitiveWarning: 'Don’t ask for sensitive information such as health, religion, political views or union membership.',
            builderIntro: 'You start with four ready-made questions. Remove them, reorder them or add your own. Drag the handle or use arrow keys on it to reorder.',
            publishedIntro: 'Questions in order. Editing is closed.',
            kind: {
                rating_5: '1-5 rating',
                nps_10: '0-10 recommend',
                single_choice: 'Single choice',
                multi_choice: 'Multiple choice',
                short_text: 'Short text',
            },
            presetTag: 'Ready-made',
            edit: 'Edit',
            remove: 'Remove',
            editAria: 'Edit question {index}',
            removeAria: 'Remove question {index}',
            moveAria: 'Move question {index}',
            addQuestion: 'Add question',
            limitNote: 'You can add up to 10 questions.',
            newQuestion: 'New question',
            editQuestion: 'Edit question',
            questionText: 'Question',
            questionKind: 'Type',
            options: 'Options',
            optionLabel: 'Option {index}',
            addOption: 'Add option',
            removeOption: 'Remove option',
            questionTextMax: 'Use 200 characters or fewer.',
            optionsRange: 'A choice question needs 2 to 8 options.',
            optionsEmpty: 'Don’t leave an option empty.',
            questionTextRequired: 'Write the question.',
            addThisQuestion: 'Add this question',
            saveQuestion: 'Save',
            cancel: 'Cancel',
            publish: 'Publish',
            publishNote: 'Questions can’t be changed after publishing. The survey opens when the event ends and closes 14 days later.',
            publishedNote: 'The survey is live. Questions can’t be changed. It closes on {date}.',
            confirmTitle: 'Publish this survey?',
            confirmBody: '{count} questions will be published. You can’t change them afterwards. The survey opens to checked-in attendees when the event ends.',
            confirmBody_one: '{count} question will be published. You can’t change it afterwards. The survey opens to checked-in attendees when the event ends.',
            emptyTitle: 'No questions yet',
            emptyBody: 'Add the ready-made questions back or write your own.',
            restorePresets: 'Restore ready-made questions',
            builderLoadError: 'Couldn’t load the survey.',
            publishError: 'Couldn’t publish the survey. Try again.',
            mutationError: 'Couldn’t save the change. Try again.',
            builderViewerNote: 'Only owners, admins and editors can change the survey.',
            noSurveyViewer: 'No survey has been set up for this event yet.',
            disabledNote: 'The support team turned this survey off. Attendees can’t see it.',
        },
    },
    csvExport: {
        colName: 'Name',
        colJobTitle: 'Job title',
        colCompany: 'Company',
        colEmail: 'Email',
        colEvent: 'Event',
        colMetAt: 'Met at',
        colMeetingArea: 'Meeting area',
        colMeetingCount: 'Meetings',
        colNote: 'Note',
        colTags: 'Tags',
        colStatus: 'Status',
        colRegisteredAt: 'Registered at',
        colCheckedInAt: 'Checked in at',
        colId: 'ID',
        colHeadline: 'Headline',
        colLocale: 'Language',
        colTimezone: 'Time zone',
        colCreatedAt: 'Created at',
        colSlug: 'Code',
        colLabelTr: 'Name (Turkish)',
        colLabelEn: 'Name (English)',
        colCardLabel: 'Card name',
        colBio: 'About',
        colPhone: 'Phone',
        colWebsite: 'Website',
        colLinkedin: 'LinkedIn',
        colX: 'X',
        colGithub: 'GitHub',
        colIsDefault: 'Default card',
        colEventSlug: 'Event address',
        colEventStartsAt: 'Event start',
        colSource: 'Source',
        colTicketKind: 'Ticket type',
        colQuestion: 'Question',
        colAnswer: 'Answer',
        colCertificateCode: 'Certificate code',
        colIssuedAt: 'Issued at',
        attendanceSource: {
            discovery: 'Discovery',
            direct_link: 'Direct link',
            invitation: 'Invitation',
            search: 'Search',
            organizer_auto: 'Automatic as organizer',
        },
        values: {
            direction: { outgoing: 'Outgoing', incoming: 'Incoming' },
            consentChannel: { signup: 'During sign-up', api: 'In the app' },
            encounterConfirmed: 'Confirmed',
            encounterChannel: { link: 'Link', manual: 'Manual', nfc: 'NFC' },
            memberStatus: { active: 'Active member', removed: 'Removed' },
            followSource: { organization_page: 'Organization page', event_page: 'Event page' },
            followEndReason: {
                unfollowed: 'You unfollowed',
                organization_deleted: 'Organization removed',
            },
            announcementOptOutAction: { opted_out: 'You turned off', opted_in: 'You turned back on' },
            announcementOptOutSource: { message: 'Link in the email', settings: 'App' },
            announcementAllChannels: 'All channels',
            announcementInApp: 'In-app',
            recommendationKind: { event: 'Event suggestion', person: 'Person suggestion' },
            recommendationSource: { precomputed: 'Precomputed', cold_start: 'Instant' },
            recommendationOutcome: {
                delivered: 'Listed',
                viewed: 'Viewed',
                opened: 'Opened',
                converted: 'Led to registration',
            },
            recommendationSurface: {
                web_home: 'Web home',
                web_home_empty: 'Web home, empty state',
                web_discovery: 'Web discovery',
                web_event_directory: 'Web attendee list',
                mobile_home: 'Mobile home',
                mobile_discovery: 'Mobile discovery',
                mobile_event_directory: 'Mobile attendee list',
                mobile_person_profile: 'Mobile person profile',
                email_reminder: 'Reminder email',
                push_reminder: 'Reminder notification',
            },
            recommendationReason: {
                sharedTags: 'Shared interest',
                relatedTags: 'Related interest',
                coAttendance: 'Attended together',
                peerProfile: 'Similar profiles',
                sameCity: 'Same city',
                popularAmongPeers: 'Popular among peers',
                startingSoon: 'Starting soon',
                sharedHistory: 'Shared history',
                complementaryRole: 'Complementary role',
                none: 'No reason',
            },
            candidateArm: {
                content: 'Content similarity',
                collaborative: 'Similar users',
                trend: 'Trending',
                context: 'Context',
                unknown: 'Unknown',
            },
            sessionRevokeReason: {
                logout: 'Signed out',
                user_revoked: 'Ended manually',
                revoked_all: 'All sessions ended',
                reuse_detected: 'Ended for security',
                account_deleted: 'Account deleted',
            },
            devicePlatform: { ios: 'iOS', android: 'Android' },
        },
        colCancelledAt: 'Cancelled at',
        colSentAt: 'Sent at',
        colRespondedAt: 'Responded at',
        colSubmittedAt: 'Submitted at',
        colConsentType: 'Consent type',
        colDocumentVersion: 'Document version',
        colChannel: 'Channel',
        colGrantedAt: 'Granted at',
        colWithdrawnAt: 'Withdrawn at',
        colDirection: 'Direction',
        colDurationMinutes: 'Duration (minutes)',
        colMessage: 'Message',
        colCounterpart: 'Other person',
        colProposedSlots: 'Proposed times',
        colAcceptedStartsAt: 'Accepted time',
        colStartsAt: 'Start',
        colEndsAt: 'End',
        colBlockedAt: 'Blocked at',
        colCounterpartCompany: 'Other person’s company',
        colCounterpartEmail: 'Other person’s email',
        colFollowUpDueAt: 'Follow-up reminder',
        colOrganization: 'Organization',
        colRole: 'Role',
        colJoinedAt: 'Joined at',
        colLeftAt: 'Left at',
        colFollowedAt: 'Followed at',
        colUnfollowedAt: 'Follow ended at',
        colEndReason: 'End reason',
        colSubject: 'Subject',
        colAction: 'Action',
        colNotificationType: 'Notification type',
        colEnabled: 'On',
        colDate: 'Date',
        colSurface: 'Shown on',
        colKind: 'Type',
        colRank: 'Position',
        colReasonKind: 'Reason type',
        colCandidateArm: 'Candidate group',
        colOutcome: 'Outcome',
        colTargetEventId: 'Recommended event ID',
        colContextEventId: 'Context event ID',
        colEngineVersion: 'Recommendation version',
        colOccurrences: 'Occurrences',
        colEventType: 'Event type',
        colTopic: 'Topic',
        colCreditPackage: 'Credit package',
        colReferralCode: 'Referral code',
        colEstimatedAttendees: 'Estimated attendees',
        colEstimatedDate: 'Estimated date',
        colContactName: 'Contact person',
        colNoticeVersion: 'Privacy notice version',
        colClient: 'App',
        colUserAgent: 'Browser details',
        colLastUsedAt: 'Last used',
        colExpiresAt: 'Valid until',
        colRevokedAt: 'Closed at',
        colRevokeReason: 'Close reason',
        colPlatform: 'Platform',
        colLastSeenAt: 'Last seen',
        colAcceptedAt: 'Accepted at',
    },
    dataCopy: {
        summaryFile: 'README.txt',
        jsonFile: 'data-copy.json',
        summaryTitle: 'Your personal data copy',
        summaryIntro: 'These files are a copy of the personal data linked to your account (KVKK article 11 and GDPR).',
        summaryAccount: 'This copy belongs to the account of {name} ({email}).',
        summaryPreparedAt: 'Prepared at: {time}',
        summaryTimezone: 'All times are in the {timezone} time zone.',
        summaryCards: 'Business cards: {value}',
        summaryEvents: 'Event registrations: {value}',
        summaryConnections: 'Connections: {value}',
        summaryFilesTitle: 'Files',
        summaryFileLine: '{file}. {description} {count} rows.',
        summaryFileLine_one: '{file}. {description} {count} row.',
        summaryJsonLine: '{file}. All your data in machine-readable form. Use it to move your data to another service.',
        summaryCsvHint: 'CSV files open in Excel, Numbers and Google Sheets.',
        sections: {
            profile: { title: 'Profile', description: 'Your account details.' },
            interests: { title: 'Interests', description: 'The interest tags you picked.' },
            cards: { title: 'Business cards', description: 'What your business card says.' },
            attendances: {
                title: 'Event registrations',
                description: 'Events you registered for and your registration status.',
            },
            invitations: {
                title: 'Event invitations',
                description: 'Event invitations you received and your replies.',
            },
            applications: {
                title: 'Event applications',
                description: 'Your applications to approval-only events.',
            },
            consents: {
                title: 'Consent records',
                description: 'Consents you gave and withdrew.',
            },
            meetingRequests: {
                title: 'Meeting requests',
                description: 'Meeting requests you sent and received.',
            },
            agenda: { title: 'Agenda', description: 'Times of your accepted meetings.' },
            blockedUsers: { title: 'Blocked people', description: 'People you blocked.' },
            encounters: {
                title: 'Connections',
                description: 'People you met, your notes and tags.',
            },
            organizationMemberships: {
                title: 'Organization memberships',
                description: 'Organizations you belong to and your role.',
            },
            organizationFollows: {
                title: 'Organizations you follow',
                description: 'Organizations you follow or unfollowed, with date and place.',
            },
            announcementsReceived: {
                title: 'Announcements you received',
                description: 'Announcements event organizers sent you, and the channel.',
            },
            announcementOptOuts: {
                title: 'Announcement preference records',
                description: 'Records of turning organizer announcements off and back on.',
            },
            surveyResponses: {
                title: 'Your survey answers',
                description: 'Your answers to post-event surveys. The link between an answer and your account is removed 30 days after the survey closes.',
            },
            certificates: {
                title: 'Your certificates of attendance',
                description: 'The code and issue time of the certificates of attendance you received.',
            },
            notificationPreferences: {
                title: 'Notification preferences',
                description: 'Which notifications you get on which channel.',
            },
            recommendationEvents: {
                title: 'Recommendation records',
                description: 'Event recommendations shown to you and what you did.',
            },
            salesInquiries: {
                title: 'Sales inquiries',
                description: 'Inquiries you sent to organize an event.',
            },
            sessions: { title: 'Sessions', description: 'Sessions signed in to your account.' },
            devices: { title: 'Devices', description: 'Your devices registered for notifications.' },
            teamInvitations: {
                title: 'Team invitations',
                description: 'Organization team invitations you received.',
            },
        },
    },
    fields: {
        eventTitle: 'Event name',
        eventSummary: 'Summary',
        eventDescription: 'Description',
        eventFormat: 'Event format',
        eventStartsAt: 'Start',
        eventEndsAt: 'End',
        eventTimezone: 'Time zone',
        eventOnlineUrl: 'Online link',
        eventCapacity: 'Capacity',
        eventWebsiteUrl: 'Website',
        eventTagIds: 'Interest tags',
        venueName: 'Venue',
        venueAddressLine: 'Address',
        name: 'Name',
        contactEmail: 'Contact email',
        venueCity: 'City',
        venueCountry: 'Country',
        venueGeo: 'Location on the map',
        city: 'City',
        currentPassword: 'Current password',
        newPassword: 'New password',
        salesCompanyName: 'Company name',
        salesEventType: 'Event type',
        salesEstimatedAttendees: 'Estimated attendees',
        salesEstimatedDate: 'Estimated date',
        salesContactName: 'Contact person',
        salesEmail: 'Email',
        salesPhone: 'Phone',
        salesNote: 'Note',
        salesNoticeAcknowledged: 'Privacy notice acknowledgement',
        token: 'Code',
        displayName: 'Display name',
        role: 'Role',
        startDay: 'Start date',
        endDay: 'End date',
        reference: 'Reference',
        reason: 'Reason',
        purchaseDay: 'Purchase date',
        salesInquiry: 'Sales lead',
        ownerEmail: 'Work email',
        amount: 'Amount',
        linkedinUrl: 'LinkedIn link',
        xUrl: 'X link',
        githubUrl: 'GitHub link',
        verificationCode: 'Verification code',
        password: 'Password',
        announcementSubject: 'Title',
        announcementBody: 'Text',
        announcementChannels: 'Channels',
        surveyAnswer: 'Answer',
    },
    validation: {
        'announcement.tooManyLinks': 'The text has too many links. The limit is {limit}.',
        'any.required': '{field} is required.',
        'any.only': '{field} is not a valid option.',
        'any.custom': '{field} is not valid.',
        'string.empty': '{field} cannot be empty.',
        'string.email': 'Enter a valid email address.',
        'string.uri': 'Enter a valid link.',
        // `httpUrlSchema` şemayı (`uri({ scheme })`) sınırladığı için Joi bu adı üretir (Tur V3).
        'string.uriCustomScheme': 'Enter a valid link.',
        'string.socialHost': "{field} must point to that network's own site.",
        'string.min': '{field} must be at least {limit} characters.',
        'string.max': '{field} can be at most {limit} characters.',
        'string.guid': '{field} is invalid.',
        'string.pattern.base': '{field} is not in the expected format.',
        'number.base': '{field} must be a number.',
        'number.min': '{field} must be at least {limit}.',
        'number.max': '{field} can be at most {limit}.',
        'number.integer': '{field} must be a whole number.',
        'array.min': 'Select at least {limit}.',
        'array.max': 'Select at most {limit}.',
        'array.unique': 'You cannot add the same value twice.',
        'object.unknown': '{field} is not a recognized field; remove it and try again.',
        'date.base': 'Enter a valid date.',
        'string.isoDate': 'Enter a valid date.',
        'event.onlineUrlRequired': 'A link is required for online events.',
        'event.venueCityRequired': 'A city is required for in-person events.',
        'event.venueNameRequired': 'A venue name is required for in-person and hybrid events.',
        'event.venueAddressRequired': 'A street address is required for in-person and hybrid events.',
        'event.venueGeoRequired': 'Pick the location on the map.',
        'event.capacityBelowTicketQuota': 'Capacity cannot be lower than the total ticket quota ({limit}).',
        'event.capacityAboveCeiling': "Capacity cannot exceed this event's attendee limit ({limit}).",
        'ticket.quotaAboveCeiling': "A ticket quota cannot exceed this event's attendee limit ({limit}).",
        'announcement.linkShortener': 'Shortened links are not allowed. Write the full address of the link.',
        'event.endsAfterStarts': 'The end time must be after the start time.',
        'consent.required': 'This consent is required to continue.',
        'consent.documentVersion': 'The consent document was updated. Please accept the current version.',
        'consent.retired': 'This consent is no longer collected.',
        'card.limit': 'You can create at most {limit} cards.',
        'string.unsafeChars': '{field} contains invalid characters.',
        'string.notPrintable': '{field} must contain at least one visible character.',
        'string.timezone': 'Select a valid time zone.',
        'string.length': '{field} must be exactly {limit} characters.',
        'date.calendar': 'That date does not exist on the calendar.',
        'date.window': '{field} must be between today and {years} years from now.',
    },
    catalog: {
        // City catalog (Story 19.1) - codes live in `domain/city-catalog.ts`.
        city: {
            'TR-01': 'Adana',
            'TR-02': 'Adiyaman',
            'TR-03': 'Afyonkarahisar',
            'TR-04': 'Agri',
            'TR-05': 'Amasya',
            'TR-06': 'Ankara',
            'TR-07': 'Antalya',
            'TR-08': 'Artvin',
            'TR-09': 'Aydin',
            'TR-10': 'Balikesir',
            'TR-11': 'Bilecik',
            'TR-12': 'Bingol',
            'TR-13': 'Bitlis',
            'TR-14': 'Bolu',
            'TR-15': 'Burdur',
            'TR-16': 'Bursa',
            'TR-17': 'Canakkale',
            'TR-18': 'Cankiri',
            'TR-19': 'Corum',
            'TR-20': 'Denizli',
            'TR-21': 'Diyarbakir',
            'TR-22': 'Edirne',
            'TR-23': 'Elazig',
            'TR-24': 'Erzincan',
            'TR-25': 'Erzurum',
            'TR-26': 'Eskisehir',
            'TR-27': 'Gaziantep',
            'TR-28': 'Giresun',
            'TR-29': 'Gumushane',
            'TR-30': 'Hakkari',
            'TR-31': 'Hatay',
            'TR-32': 'Isparta',
            'TR-33': 'Mersin',
            'TR-34': 'Istanbul',
            'TR-35': 'Izmir',
            'TR-36': 'Kars',
            'TR-37': 'Kastamonu',
            'TR-38': 'Kayseri',
            'TR-39': 'Kirklareli',
            'TR-40': 'Kirsehir',
            'TR-41': 'Kocaeli',
            'TR-42': 'Konya',
            'TR-43': 'Kutahya',
            'TR-44': 'Malatya',
            'TR-45': 'Manisa',
            'TR-46': 'Kahramanmaras',
            'TR-47': 'Mardin',
            'TR-48': 'Mugla',
            'TR-49': 'Mus',
            'TR-50': 'Nevsehir',
            'TR-51': 'Nigde',
            'TR-52': 'Ordu',
            'TR-53': 'Rize',
            'TR-54': 'Sakarya',
            'TR-55': 'Samsun',
            'TR-56': 'Siirt',
            'TR-57': 'Sinop',
            'TR-58': 'Sivas',
            'TR-59': 'Tekirdag',
            'TR-60': 'Tokat',
            'TR-61': 'Trabzon',
            'TR-62': 'Tunceli',
            'TR-63': 'Sanliurfa',
            'TR-64': 'Usak',
            'TR-65': 'Van',
            'TR-66': 'Yozgat',
            'TR-67': 'Zonguldak',
            'TR-68': 'Aksaray',
            'TR-69': 'Bayburt',
            'TR-70': 'Karaman',
            'TR-71': 'Kirikkale',
            'TR-72': 'Batman',
            'TR-73': 'Sirnak',
            'TR-74': 'Bartin',
            'TR-75': 'Ardahan',
            'TR-76': 'Igdir',
            'TR-77': 'Yalova',
            'TR-78': 'Karabuk',
            'TR-79': 'Kilis',
            'TR-80': 'Osmaniye',
            'TR-81': 'Duzce',
            ABROAD: 'Abroad',
        },
        ticketKind: {
            free: 'Free',
            paid: 'Paid',
            invite: 'Invite only',
            student: 'Student',
            sponsor: 'Sponsor',
            speaker: 'Speaker',
            press: 'Press',
        },
    },
    /** PHASE 3 — QR business-card exchange (Flow C) and wallet (Flow D/E). See tr.ts. */
    faz3: {
        exchange: {
            title: 'Card exchange',
            checkedIn: 'Checked in',
            notCheckedIn: 'Not checked in',
            modeShow: 'Show my card',
            modeScan: 'Scan',
            modeLabel: 'Show card or scan',
            qrA11y: 'QR code of your business card',
            cardCodeLabel: 'CODE {code}',
            mutualNotice: 'Nothing is shared until the person who scans your card approves too. The exchange is always mutual.',
            walletCta: 'Wallet',
            scanHint: 'Frame the other person’s code. Turn on the flash if the light is low.',
            torchOn: 'Turn flash on',
            torchOff: 'Turn flash off',
            manualCta: 'Enter the code manually',
            cameraPrivacy: 'The camera image is never stored and never sent to a server.',
            cameraUnavailable: 'No back camera found on this device. You can enter the code manually.',
            cameraError: 'The camera could not start. You can enter the code manually.',
            foreignCode: 'This code is not a {brandName} business card.',
            approvalKicker: 'Card exchange · your approval is needed',
            approvalTitle: '{name} wants to share their card',
            stampTitle: 'Where you met',
            approvalMutual: 'When you approve, your card goes to them as well. No one-way sharing.',
            approveCta: 'Share my card and receive theirs',
            declineCta: 'Cancel',
            pendingKicker: 'Waiting',
            pendingTitle: 'You approved',
            pendingBody: 'The encounter is recorded once {name} approves. You can close this screen; it finishes in the background.',
            pendingSpinner: 'Waiting for approval; usually a few seconds',
            pendingMineTitle: 'Your approval',
            pendingMineMeta: '{time} · your card is ready',
            pendingPeerTitle: '{name}’s approval',
            pendingPeerMeta: 'Waiting',
            pendingBackCta: 'Back to the QR screen',
            successKicker: 'Mutual approval complete',
            successPoster: 'You met.',
            successBody: '{name} was added to your wallet. The event and date were saved automatically.',
            successBodyNoEvent: '{name} was added to your wallet. The date was saved automatically.',
            successWalletCta: 'Go to wallet',
            noteCta: 'Add a note',
            continueCta: 'Continue',
            successHint: 'The two words you write now are what will find this person six months later.',
            noteTitle: 'Encounter note',
            noteFieldLabel: 'What did you talk about?',
            notePlaceholder: 'Two sentences about what you discussed',
            noteTagsTitle: 'Tag',
            noteReminderTitle: 'Follow-up reminder',
            reminderNone: 'No reminder',
            reminderWeek: 'In 1 week',
            reminderMonth: 'In 1 month',
            noteSaveCta: 'Save and go to wallet',
            tagPilot: 'Pilot',
            tagFollowUp: 'Follow up',
            tagInvestor: 'Investor',
            tagSupplier: 'Supplier',
            deniedKicker: 'No camera permission',
            deniedTitle: 'Scanning needs camera permission',
            deniedBody: 'The code cannot be read because permission is off. You can turn permission on in settings, or type the code instead. The other person can also start the exchange by scanning your card.',
            deniedPrivacy: 'The camera image is never stored and never sent to a server.',
            deniedSettingsCta: 'Open settings',
            deniedShowCardCta: 'Continue by showing my card',
            permissionAskCta: 'Allow camera',
            manualTitle: 'Enter the code manually',
            manualBody: 'Type the six-character code on the other person’s card screen. It does the same thing, no camera needed.',
            manualFieldLabel: 'Card code',
            manualPlaceholder: 'e.g. 4K7-92B',
            manualFindCta: 'Find the person',
            manualNotFound: 'Code not found. Check the letters and the dash.',
            offlineQueued: 'You are offline. The exchange is held on your device and will sync when you reconnect',
            offlineApproval: 'You are offline, so your approval cannot be sent right now. A card exchange is completed on the server with both approvals; approve from this screen once you reconnect.',
            loadingA11y: 'Preparing your card',
            queueCount: '{count} actions are queued; they will be sent when you are back online.',
            queueCount_one: '{count} action is queued; it will be sent when you are back online.',
            queueExpired: '{count} queued offer expired before it could be sent. Scan the code again.',
            queueExpired_one: '{count} queued offer expired before it could be sent. Scan the code again.',
            pendingBandTitle: 'Waiting for you',
            pendingBandOpen: 'Open the exchange with {name}',
            pendingBandMineTitle: 'You approved; waiting for them',
            pendingBandMineMeta: 'Your approval was sent; you meet once the other side approves too.',
            pendingBandResume: 'Go back to the pending exchange with {name}',
            codeExpiredTitle: 'The code has expired',
            codeExpiredBody: 'Card codes are short-lived and rotate for safety. It could not be refreshed because you are offline; refresh it once you reconnect, otherwise the other person cannot scan it.',
            codeRefreshCta: 'Refresh the code',
            expiresIn: 'Offer expires in {time}',
            /* Countdown carries its unit; a bare mm:ss reads as a wall clock. */
            countdownMinutes: '{minutes} min {seconds} sec',
            countdownSeconds: '{seconds} sec',
            countdownLeft: '{time} left',
            withdrawCta: 'Withdraw my approval',
            lockoutGuidance: 'That code did not work several times in a row. Repeated wrong attempts pause new tries for a while. The easiest way out is to show your own card and let the other person scan your code; the exchange works the same way.',
        },
        wallet: {
            title: 'Wallet',
            /*
             * SAYI + İSİM PARÇALARI AYRI ANAHTARDIR (QA Blok 23 §2 — çoğul sınıfı).
             * Çevirmen cümle başına TEK `count` çekimler; "{people} people · {events}
             * events" iki BAĞIMSIZ sayı taşıdığı için 1 olduğunda ikisi de çoğul
             * kalıyordu ("1 people · 1 events"). Parçalar tek tek çekimlenir, gövde
             * yalnız birleştirir.
             */
            peopleCount: '{count} people',
            peopleCount_one: '1 person',
            eventCount: '{count} events',
            eventCount_one: '1 event',
            recordCount: '{count} records',
            recordCount_one: '1 record',
            countMeta: '{peopleLabel} · {eventsLabel}',
            countMetaOldest: '{peopleLabel} · {eventsLabel} · oldest record {since}',
            emptyMeta: 'No records yet',
            searchPlaceholder: 'Name, company, event or note',
            searchLabel: 'Search the wallet',
            filterCta: 'Filter',
            filterCtaWithCount: 'Filter ({count})',
            exportCta: 'Export my contacts',
            resultLabel: '{recordsLabel} · out of {peopleLabel}',
            /* "Zaten tanıştıkların" kulvarı (backlog §6 md. 1). */
            laneKicker: 'People you already know',
            laneBody: '{count} people from your wallet are registered for this event',
            laneBody_one: '1 person from your wallet is registered for this event',
            laneReasonKicker: 'Why this list',
            laneReason: 'You added these people to your wallet yourself and they are registered for this event. They sit apart from the suggestion list so familiar faces do not crowd out new ones.',
            laneMore: '+{count} more people',
            laneMore_one: '+1 more person',
            /* Lane vocabulary is separate from the wallet summary vocabulary. */
            laneEventCount: '{count} upcoming events',
            laneEventCount_one: '1 upcoming event',
            lanePeopleCount: '{count} familiar faces',
            lanePeopleCount_one: '1 familiar face',
            laneSummary: '{peopleLabel} · {eventsLabel}',
            laneExpand: 'Show',
            laneCollapse: 'Hide',
            laneToggleLabel: 'Show or hide the “people you already know” list',
            newlyAdded: 'Newly added',
            removeFilter: 'Remove the {label} filter',
            noResultTitle: 'No records with these filters',
            noResultBody: 'Widen the date range, or keep only the event filter.',
            clearFiltersCta: 'Clear filters',
            emptyTitle: 'We are here so business cards do not get lost.',
            emptyBody: 'When you save someone you met at an event via QR, they stay here together with who, where, when and what you talked about. You will find them six months later even without the name.',
            emptyPreviewLabel: 'A record looks like this',
            emptyPreviewName: 'Kerem Doğan',
            emptyPreviewRole: 'Founder · Tera Analytics',
            emptyPreviewContext: 'AI & Fintech Istanbul · 18 August · “risk scoring, pilot in October”',
            firstStepsTitle: 'Three ways to make your first record',
            firstStep1Title: 'Meet with QR',
            firstStep1Body: 'Show your card or scan theirs; the record is created once both sides approve.',
            firstStep2Title: 'Set up a meeting before the event',
            firstStep2Body: 'Send a request from the attendee list; accepted meetings land in your agenda.',
            firstStep3Title: 'Share your card link',
            firstStep3Body: 'Even someone without the app can get your card from the link.',
            emptyPrimaryCta: 'Meet with QR',
            emptySecondaryCta: 'Open an event I registered for',
            emptyDiscoverCta: 'Discover events',
            emptyMineCta: 'Go to my events',
            filterTitle: 'Wallet filter',
            filterClear: 'Clear',
            filterEvents: 'Event',
            filterSectors: 'Sector',
            filterPeriod: 'When you met',
            periodAll: 'All time',
            periodRecent: 'Last 3 months',
            periodMid: '6–18 months ago',
            periodOld: 'Older than 18 months',
            filterApplyCta: 'Show {count} records',
            filterApplyCta_one: 'Show {count} record',
            detailHeader: 'Wallet record',
            detailStampTitle: 'Where and when you met',
            detailNoteTitle: 'Your note',
            detailNotePlaceholder: 'What did you talk about with this person?',
            detailFollowTitle: 'Follow-up',
            detailReminderActive: 'Remind me on {date} · “{text}”',
            detailReminderRemove: 'Remove',
            detailReminderAdd: 'Add a follow-up reminder',
            detailHistoryTitle: 'Shared history',
            detailMessageCta: 'Send a message',
            detailMessageLocked: 'Messaging is not in this release; reach out by email after the exchange.',
            detailShareCta: 'Share the card',
            exportTitle: 'Export',
            exportIntro: 'Your data is yours. Download the whole wallet or only the records you filtered.',
            exportScopeTitle: 'Scope',
            exportScopeFiltered: 'The {count} filtered records',
            exportScopeFiltered_one: 'The {count} filtered record',
            exportScopeAll: 'The whole wallet · {count} records',
            exportScopeAll_one: 'The whole wallet · {count} record',
            exportScopeFilteredUncounted: 'The filtered records',
            exportScopeAllUncounted: 'The whole wallet',
            exportFormatTitle: 'Format',
            exportCsv: 'CSV',
            exportCsvDesc: 'Excel, CRM import',
            exportVcard: 'vCard',
            exportVcardDesc: 'Phone contacts',
            exportWithNotes: 'Include my notes',
            exportSummary: '{count} records · {format} · {notes}',
            exportSummary_one: '{count} record · {format} · {notes}',
            exportNotesIncluded: 'notes included',
            exportNotesExcluded: 'notes excluded',
            exportSummaryBody: 'Their card, the event you met at and the date are on every row.',
            exportDownloadCta: 'Download',
            exportShareTitle: 'Wallet export',
            exportEmpty: 'No records in the selected scope.',
            loadingA11y: 'Loading the wallet',
            detailMeetingCount: 'You met {count} times',
            detailMeetingCount_one: 'You met {count} time',
            detailRemoveCta: 'Remove from my wallet',
            removeDialogTitle: 'Remove this record from your wallet?',
            removeDialogBody: 'The record leaves only your wallet; the other person keeps their copy and their own note. This cannot be undone.',
            removeDialogConfirm: 'Remove',
            removedNotice: 'The record was removed from your wallet.',
            savedToast: 'Saved. Your wallet record is up to date.',
            historyExchangeNoEvent: 'You exchanged cards with them',
            historyMeetingNoEvent: 'Your meeting request was accepted',
            historyExchange: 'You exchanged cards at {event}',
            historySameEvent: 'You were both registered for {event}',
            historyMeeting: 'Your meeting at {event} was accepted',
            historyHiddenEvent: 'An event',
            historyEmpty: 'No other shared history was found with this person.',
            loadMoreCta: 'Show more',
            exportDone: '{count} records exported.',
            exportDone_one: '{count} record exported.',
        },
        cardLink: {
            pageTitle: 'Digital business card',
            wallTitle: 'Sign in to see this card.',
            wallBody: 'Card details are for members only. Create a free account or sign in; the link brings you straight back to the card.',
            wallSignInCta: 'Sign in or sign up',
            wallPromiseTitle: 'Business cards do not get lost.',
            wallPromiseBody: 'Everyone you meet stays in one wallet, together with your event history.',
            metTitle: 'Where you met',
            addToWalletCta: 'Add to my wallet',
            inWalletNotice: 'This person is already in your wallet.',
            vcardCta: 'Save to phone (vCard)',
            mutualNotice: 'Adding to the wallet needs mutual approval, so no record is created until the card owner approves.',
            visibilityNotice: 'This page is visible only because the card owner chose to share it. Visibility is always their own decision.',
            notFoundTitle: 'This link is no longer valid',
            notFoundBody: 'The link may have been switched off, or it may never have existed. Ask the card owner for a new link.',
            settingsTitle: 'My card link',
            settingsBody: 'Share your card with a single address. Turn it off whenever you want.',
            settingsOpenCta: 'Turn the link on',
            settingsRevokeCta: 'Turn the link off',
            settingsCopyCta: 'Copy the link',
            settingsCopied: 'Copied',
            settingsClosed: 'The link is off.',
            settingsViews: 'opened {count} times',
            settingsViews_one: 'opened {count} time',
            settingsAnonymousCounter: 'The counter is anonymous, so who opened the link is never recorded.',
            settingsAddressUnavailable: 'The address cannot be shown right now; the link is on and can be turned off.',
            shareRequiresSignIn: 'Whoever opens the link has to sign in.',
        },
        cardShareRedirect: {
            pageTitle: 'Get the app',
            title: 'Download {brandName} to share business cards',
            body: 'Once the app is installed, scan the QR code again from inside the app. Cards are shared only after both people approve.',
            appStoreCta: 'Download on the App Store',
            googlePlayCta: 'Get it on Google Play',
            installedHint: 'Already have the app?',
            openInAppCta: 'Open in the app',
            webSignUpLink: 'Create an account on the web',
        },
        exchangeWeb: {
            pageKicker: 'Same flow · browser camera',
            allowCta: 'Allow the camera',
            showCardCta: 'Show my card',
            scanKicker: 'Waiting',
            scanSideBody: 'Once the code is read, the mutual approval screen opens.',
            scanHint: 'Bring the code into the frame. Showing the card on a screen works with a laptop camera too.',
            stopScanCta: 'Revoke access',
            deniedTitle: 'The camera could not be opened',
            deniedBody: 'The browser did not allow camera access, or there is no camera on this device. Type the other person’s code below or show your card; they can also scan your code and the same exchange starts.',
            myCardTitle: 'Your card',
            cameraPrompt: 'Scan the other person’s card QR code',
            cameraPrivacy: 'The image never leaves your device. The code is decoded in the browser.',
            myCodeLabel: 'Your card code',
            waitingTitle: 'You approved',
            waitingBody: 'Once {name} approves, the encounter record is created. You can close the page; it completes on the server.',
            waitingCountdown: 'Offer expires in: {time}',
            successNoteTitle: 'Note (optional)',
            newExchangeCta: 'New exchange',
            pageTitle: 'Card exchange',
            manualInvalid: 'A code has six characters, for example 4K7-92B. Add the missing ones.',
            manualUnavailable: 'Code not found or expired. Ask the other person for the current code on their screen.',
            manualSelf: 'This is your own code. Enter the code on the other person’s screen.',
            manualRateLimited: 'Too many attempts. Wait a little and try again, or show your card.',
            manualNetwork: 'Could not reach the server. Check your connection and try again.',
        },
        walletWeb: {
            pageKicker: 'Same job · desktop',
            pageTitle: 'Wallet',
            filterTitle: 'Filter',
            pageSub: 'People you exchanged cards with. An exchange always needs approval from both sides.',
            scenarioCta: 'Recall scenario',
            scenarioHint: 'If you cannot recall the name, pick the event, the sector and roughly when you met.',
            searchPlaceholder: 'Search name, company, event or inside notes',
            colPerson: 'Person',
            colContext: 'Where you met',
            colNote: 'Note',
            colTags: 'Tags',
            noNote: 'No note',
            noTags: 'No tags',
            filterSummaryNone: 'No filters',
            filterSummaryActive: '{count} filters active',
            filterSummaryActive_one: '{count} filter active',
            detailContextTitle: 'Context',
            detailEmptyTitle: 'Pick a record',
            detailEmptyBody: 'Click a person in the middle list; their note and shared history open here.',
            noteSaveCta: 'Save note',
            noteSaved: 'Note saved.',
            tableLabel: 'Wallet records',
            openRow: 'Open the record of {name}',
            exportDialogTitle: 'Export your wallet',
            exportCloseCta: 'Cancel',
        },
        walletContact: {
            title: 'Contact',
            saveToContacts: 'Save to contacts',
            shareContact: 'Share contact',
            listLabel: 'Contact details',
            fieldEmail: 'Email',
            fieldPhone: 'Phone',
            fieldWebsite: 'Website',
            fieldLinkedin: 'LinkedIn',
            fieldX: 'X',
            fieldGithub: 'GitHub',
            actionEmail: 'Send email',
            actionCall: 'Call',
            actionSms: 'Message',
            actionOpen: 'Open',
            copy: 'Copy',
            copyEmail: 'Copy email',
            copyPhone: 'Copy phone',
            copyWebsite: 'Copy website',
            copyLinkedin: 'Copy LinkedIn link',
            copyX: 'Copy X link',
            copyGithub: 'Copy GitHub link',
            copiedEmail: 'Email address copied',
            copiedPhone: 'Phone number copied',
            copiedWebsite: 'Website copied',
            copiedLinkedin: 'LinkedIn link copied',
            copiedX: 'X link copied',
            copiedGithub: 'GitHub link copied',
            copyFailed: 'Could not copy. You can select the value and copy it yourself.',
            empty: 'This person has not added contact details to their card.',
            loadingA11y: 'Loading contact details',
            error: 'Contact details could not be loaded.',
            retry: 'Try again',
            rowEmail: 'Email {name}',
            rowPhone: 'Call or message {name}',
            rowWebsite: "Open {name}'s website",
            rowLinkedin: "Open {name}'s LinkedIn profile",
            rowX: "Open {name}'s X profile",
            rowGithub: "Open {name}'s GitHub profile",
            mineTitle: 'What the other person will see',
            mineEdit: 'Edit',
            mineEditA11y: 'Edit contact details',
            mineEmpty: 'Your card has no contact details. What you add is visible only to people you exchange cards with.',
            mineAdd: 'Add contact details',
            mineVisibility: 'Only people who both approved a card exchange with you see these details.',
        },
    },
    legal: {
        kicker: 'Legal documents',
        breadcrumbRoot: 'Home',
        breadcrumbSection: 'Legal',
        tocTitle: 'On this page',
        tocSheetTitle: 'Contents',
        sectionCount: '{count} sections',
        sectionCount_one: '{count} section',
        tocProgressLabel: 'Reading progress',
        version: 'Version {version}',
        effective: 'In force from: {date}',
        lastUpdated: 'Last updated: {date}',
        print: 'Print',
        siblingTitle: 'Other documents',
        publicBadge: 'Readable without signing in',
        footerHeading: 'Legal',
        docTitles: {
            kvkkAydinlatma: 'Personal Data Protection Notice',
            gizlilikPolitikasi: 'Privacy Policy',
            kullanimKosullari: 'Terms of Service',
            acikRiza: 'Explicit Consent Statement',
            cerezPolitikasi: 'Cookie Policy',
            satisBasvurusu: 'Sales Inquiry Privacy Notice',
            kurumHesabiAydinlatma: 'Organization Account Privacy Notice',
            kvkkBasvuru: 'Data Subject Request Form',
            hesapSilme: 'Account Deletion',
        },
        rightsCta: 'Use my rights',
        errorTitle: 'The document could not be opened',
        errorBody: 'We could not fetch the text right now. Reload the page; if it keeps failing, write to support.',
    },
    legalDeletion: {
        kicker: 'Your account is yours',
        title: 'Deleting your account and data',
        lede: 'You can delete your account whenever you want; you do not need our permission, an e-mail exchange with support or a reason. This page explains exactly what deletion removes and how long it takes.',
        waysTitle: 'How to delete',
        way1Badge: 'Route 1',
        way1Title: 'From inside the app',
        way1Body: 'The fastest route. Your identity is verified from your session and no extra documents are needed.',
        way1Step1: 'Settings › Your data and account',
        way1Step2: 'Delete my account',
        way1Step3: 'Confirm with the code sent to your e-mail',
        way1Cta: 'Go to your data and account',
        way2Badge: 'Route 2',
        way2Title: 'If you cannot reach the app',
        way2Body: 'If your phone is not with you or you have uninstalled the app, you can request deletion by e-mail.',
        way2MailboxHint: 'Send your request to',
        way2MailboxNote: 'It has to come from the e-mail address on your account. We send a verification link to that address to confirm your identity.',
        way2Cta: 'Data subject request form',
        timelineTitle: 'What happens after a deletion request',
        timelineStep1Day: 'Day 0',
        timelineStep1Title: 'Request received',
        timelineStep1Body: 'Your account is suspended. You cannot sign in, your profile leaves attendee lists and search, and nobody can send you a meeting request.',
        timelineStep2Day: 'Days 0–{days}',
        timelineStep2Title: 'Cancellation window',
        timelineStep2Body: 'If you change your mind you can cancel with one click from the link in your e-mail. This window protects your account against accidental or malicious deletion.',
        timelineStep2Chip: 'Can be cancelled',
        timelineStep3Day: 'Day {days}',
        timelineStep3Title: 'Permanent deletion',
        timelineStep3Body: 'Data is irreversibly deleted or anonymised. There is no going back after this step; you can sign up again with the same e-mail, but your old data will not return.',
        timelineStep3Chip: 'Cannot be undone',
        scopeTitle: 'What is deleted and what stays',
        deletedTitle: 'Deleted',
        deletedItem1: 'Your name, e-mail address and password',
        deletedItem2: 'Your business cards, including phone, title, company, industry, education and social addresses',
        deletedItem3: 'Your profile photo and the images you uploaded',
        deletedItem4: 'The notes you wrote about people',
        deletedItem5: 'Your meeting requests and messages',
        deletedItem6: 'Your sessions and device push tokens',
        deletedItem7: 'Your saved card wallet',
        anonymisedTitle: 'Anonymised',
        anonymisedBody: 'The link to your identity is severed and only a number remains, which cannot be traced back to you.',
        anonymisedItem1: 'Event attendance counts, so the organizer report stays intact',
        anonymisedItem2: 'Encounter statistics',
        anonymisedItem3: 'The actions recorded in audit logs',
        keptTitle: 'Kept',
        keptBody: 'Only the records the law requires us to keep, and only for as long as the law says.',
        keptItem1: 'Consent records, because of the statutory burden of proof',
        keptItem2: 'Invoicing and accounting records if any, for 5 years under Tax Procedure Law art. 253',
        keptItem3: 'Traffic and transaction security logs, for 2 years under Law No. 5651',
        keptNote: 'These records are used for no other purpose and are deleted automatically once the period ends.',
        exportTitle: 'Before you delete, download your data',
        exportBody: 'Your business cards, encounters and event history can be exported as CSV and vCard. Your data is never held hostage; that is one of the product’s red lines.',
        exportCta: 'Download my data',
        faqTitle: 'Frequently asked',
        faq1Q: 'If I uninstall the app, is my account deleted?',
        faq1A: 'No. Uninstalling does not delete data; you need to send a deletion request through one of the routes on this page.',
        faq2Q: 'Do the cards I exchanged stay with the other person?',
        faq2A: 'A snapshot of your card at the moment you shared it stays in their wallet, and that copy is now their data. Your card stops updating and your profile is unlinked.',
        faq3Q: 'What happens to my events if I am an organizer?',
        faq3A: 'If you are the only admin of the organization you are asked to hand it over before deletion, so attendees are never left with an ownerless event.',
        faq4Q: 'I want it deleted before the waiting period ends.',
        faq4A: 'Send a request under Article 11; once we confirm your identity we delete it with no waiting period.',
    },
    legalRequest: {
        kicker: 'Request to the data controller',
        title: 'Data Subject Request Form',
        lede: 'Use this form to exercise your rights under Article 11 of Law No. 6698. Because you are signed in, your identity counts as verified and you do not need to send extra documents.',
        step1Title: 'Applicant',
        step1Hint: 'These fields came from your account. If they are wrong you can fix them in your profile.',
        fieldName: 'Full name',
        fieldEmail: 'E-mail',
        fromSession: 'from session',
        step2Title: 'Your request',
        step2Hint: 'You can pick more than one right. Some requests are met instantly, without the form.',
        instantChip: 'instant',
        requestA: 'I want to learn whether my data is processed',
        requestB: 'I want a copy of the data that is processed',
        requestC: 'I want to learn the purpose for which my data is processed',
        requestD: 'I want to learn the third parties my data is transferred to',
        requestE: 'I want incomplete or inaccurate data corrected',
        requestF: 'I want my data erased',
        requestG: 'I object to the outcome of automated analysis',
        requestH: 'I claim compensation for my damage',
        articleLabel: 'art. 11/{letter}',
        instantExportHint: 'Instant from Settings › Your data and account',
        instantDeleteHint: 'Instant from Settings › Your data and account',
        step3Title: 'Details',
        step3Hint: 'Make your request concrete. Which data, which date, which event?',
        explanationPlaceholder: 'For example, my company name is wrong on my attendance record for the event on 12 August, and I ask for it to be corrected.',
        explanationCounter: '{count} / {max} characters',
        attachmentNote: 'If you want to attach a document you can add it to the e-mail draft.',
        step4Title: 'How do you want the answer?',
        step4Hint: 'The Communiqué says you choose the reply channel.',
        channelEmail: 'E-mail',
        channelPost: 'Post',
        channelPostHint: 'to the address you provide',
        submitNote: 'Your request is recorded and answered within 30 days at the latest. If it entails an additional cost, the fee in the Board’s tariff is notified in advance.',
        submitCta: 'Send the request',
        submitDraftNote: 'The send button copies the items you picked and your explanation into a ready e-mail draft. You send the draft from your own mail app; because the address matches your account, your identity is verified.',
        slaValue: '30',
        slaLabel: 'days to answer',
        instantTitle: 'What you can do without waiting',
        instant1: 'Download your data',
        instant2: 'Withdraw your consents',
        instant3: 'Delete your account',
        instant4: 'Correct your profile details',
        otherChannelsTitle: 'Other ways to apply',
        otherChannelMail: 'E-mail',
        otherChannelPetition: 'Wet-signed petition',
        otherChannelPetitionBody: 'To the company address, in person or through a notary.',
        relatedTitle: 'Related documents',
        mailSubject: 'Data subject request',
        mailIntro: 'I am submitting the following requests under Article 11 of Law No. 6698.',
        mailRequestsHeading: 'Requests',
        mailExplanationHeading: 'Details',
        mailChannelHeading: 'Reply channel',
        selectionRequired: 'Pick at least one request.',
    },
    consentUpdate: {
        title: 'Our documents have been updated',
        body: 'We updated our {documents}. You can read the changes here.',
        orgBody: 'We updated our {documents}. You can read the changes here.',
        documents: {
            terms_of_service: 'terms of service',
            privacy_policy: 'privacy policy',
            kvkk_clarification: 'KVKK clarification notice',
            organization_account_notice: 'organization account notice',
        },
        optionalAlso: {
            analytics: 'The text of your analytics consent changed too. It stays off unless you consent again.',
            marketing_email: 'The text of your campaign email consent changed too. It stays off unless you consent again.',
        },
        consentsLink: 'Go to my consents',
        acknowledgeCta: 'I have read it',
        listAnd: 'and',
        failed: 'We could not save that you read it. Check your connection and try again.',
        orgFailed: 'We could not save that you read it. Check your connection and try again.',
    },
    consentCenter: {
        title: 'My consents',
        lede: 'This is where you see which document you approved, in which version and when. You can withdraw optional consents at any time, and withdrawing does not close your account.',
        orgLede: 'See which document, which version and when you approved here. Organization accounts have no optional consents because no marketing or analytics processing happens on them.',
        exportCta: 'Download the record',
        requiredGroupTitle: 'Approvals the service requires',
        requiredGroupHint: 'No account can be opened without these, and withdrawing them means closing the account.',
        optionalGroupTitle: 'Optional consents',
        optionalGroupHint: 'Switching them off locks no feature. Explicit consent can always be withdrawn (Article 7).',
        grantedBadge: 'Approved',
        readBadge: 'Read',
        notGrantedBadge: 'Not given',
        readDoc: 'Read the text',
        versionChip: 'version {version}',
        withdrawnAt: 'Withdrawn on {time}',
        neverGranted: 'Not given yet',
        newVersionTitle: 'Version {version} of {document} has been published',
        newVersionBody: 'The version you approved is {granted}. You can read the new text and refresh your approval.',
        newVersionCta: 'Read the new text',
        renewHint: 'The text was updated, give your preference again.',
        historyTitle: 'Consent history',
        historyHint: 'An auditable record, every change timestamped',
        historyLabel: 'Consent history table',
        colDate: 'Date',
        colDocument: 'Document',
        colVersion: 'Version',
        colAction: 'Action',
        colSource: 'Source',
        actionGranted: 'Granted',
        actionWithdrawn: 'Withdrawn',
        sourceUnknown: 'Unknown',
        historyNote: 'Because of the statutory burden of proof this record is kept even after your account is deleted, and it is used for no other purpose. Your IP address is stored hashed, never in the clear.',
        emptyTitle: 'No consent records yet',
        emptyStep1: 'The approvals you give while signing up land here.',
        emptyStep2: 'You can switch optional consents on with the toggles below.',
        emptyStep3: 'Every change is recorded with a timestamp.',
        updateFailed: 'The consent could not be updated. Check your connection and try again.',
        retiredNote: 'This consent is no longer the basis for any processing. Appearing on an attendee list depends on your registration. Once withdrawn, it stays only in your consent history.',
        withdrawCta: 'Withdraw',
        colType: 'Type',
        colGrantedAt: 'Approved on',
        colActions: 'Actions',
        kindRequired: 'Required',
        kindOptional: 'Optional',
    },
    consentDescriptions: {
        terms_of_service: 'The rules for using the platform, your responsibilities and when an account can be suspended.',
        privacy_policy: 'How your data is processed, where it is stored and who it is shared with.',
        kvkk_clarification: 'The information notice required by Article 10. It is not a consent, it is the record that you read it.',
        marketing_email: 'Announcements about new events and product news. Transactional mail such as sign-in codes and request notifications is independent of this and does not stop.',
        analytics: 'We measure which screens are used and what became of the recommendations shown to you (seen, opened, turned into a registration), tied to your account. When off, no analytics event is sent, no recommendation record tied to your account is kept and no feature is disabled.',
        attendee_visibility: 'The former optional attendee list consent. It is no longer collected, because everyone who registers for an event appears on its attendee list. Your earlier records are kept for audit.',
        organization_account_notice: 'How the organization account processes your data on behalf of the organization. It is not a consent, it is the record that you read it.',
    },
    venueMap: {
        osmName: 'OpenStreetMap',
        attributionBefore: '©',
        attributionAfter: 'contributors',
        searchAttributionBefore: 'Search results ©',
        directions: 'Get directions',
        newTabNote: '(opens in a new tab)',
        tilesFailedTitle: 'The map could not be loaded right now.',
        tilesFailedBody: 'The address and directions still work.',
        onlineTitle: 'Joining',
        onlineLead: 'This event is online.',
        hybridLead: 'You can also join online.',
        hybridLabel: 'Online participation',
        fieldLabel: 'Location on the map',
        fieldHint: 'Attendees see this location on the map on the event page. The search runs only when you press the button.',
        findCta: 'Find on map',
        searching: 'Searching…',
        addressFirst: 'Enter the street address first',
        addressPlaceholder: 'e.g. Sütlüce Mah. Karaağaç Cad. No:1, Beyoğlu',
        resultsCount: '{count} results · pick one',
        resultsCount_one: '{count} result · pick it',
        noResult: 'We could not find this address. Try again with the neighbourhood and district.',
        busyTitle: 'Search is unavailable right now. Try again shortly.',
        busyBody: 'You can save the event as a draft and add the location later.',
        selected: 'Location selected',
        remove: 'Remove location',
        addressChanged: 'The address changed, find the location again. The saved location was removed.',
        publishBlockedNote: 'The event was not published. A draft can be saved without a location.',
        /* Mobile (map round C): offline tile state (E8), map tap and offline search. */
        offlineTitle: 'The map appears once you are back online.',
        offlineBody: 'Directions open in your maps app.',
        openInMapsA11y: 'Open {name} in your maps app',
        openInMapsA11yNoName: 'Open the location in your maps app',
        offlineSearch: 'No connection. You can search once you are back online.',
        addressPlaceholderShort: 'e.g. Karaağaç Cad. No:1',
        /* User decision 16.10.2026: note beside the pin when the search only matched the street. */
        approximate: 'Approximate location',
        /* Organizer form: hint under the field when the "Find on map" result is street level. */
        approximateHint: 'The exact address was not found, the location will be shown at street level.',
    },
};
//# sourceMappingURL=en.js.map